import React, { useState, useEffect, useMemo, useRef, useId } from "react";
import {
  getSettings,
  updateSettings,
  appendTrade,
  appendLesson,
  fetchJournalRows,
  SHEET_HEADERS,
  getNextTradeNumber,
  getUserSettings,
  updateUserSettings
} from "../services/FirestoreService";
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import { FaRegClock, FaExclamationTriangle, FaClipboardList } from "react-icons/fa";
import { FiInfo } from "react-icons/fi";
import { getAuth } from "firebase/auth";
import PreTradeChecklistModal from './PreTradeChecklistModal';
import { savePreTradeChecklist } from "../services/FirestoreService";
import { createPortal } from "react-dom";
import { doc, onSnapshot, collection, query, where } from "firebase/firestore";
import { db } from "../firebase";
import SimpleCalculator from './SimpleCalculator'; 
import { checkFirstTradeAnalysis } from "../services/FirstTradeAnalysisService"; // Logic
import FirstTradeAnalysisPopup from "../components/FirstTradeAnalysisPopup";     // UI


// --- Icons (copy these SVGs exactly as shown) ----
const IconGoal = () => (
  <span className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300 rounded-full p-2 mr-2 shadow-sm">
    <svg width="24" height="24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#16a34a" strokeWidth="2"/>
      <path d="M8 12l2 2l4-4" stroke="#16a34a" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  </span>
);

const IconJournal = () => (
  <span className="bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-300 rounded-full p-2 mr-2 shadow-sm">
    <svg width="24" height="24" fill="none">
      <rect x="4" y="6" width="16" height="14" rx="2" stroke="#f59e42" strokeWidth="2"/>
      <path d="M8 2v4M16 2v4" stroke="#f59e42" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  </span>
);

// --- Constants for dropdowns ---
const MOOD_OPTIONS = ["1", "2", "3", "4", "5"];
const SESSION_OPTIONS = ["New York", "London", "Asian", "Sydney","Mixed"];
const RULES_OPTIONS = ["Yes", "No"];
const GRADE_OPTIONS = ["A", "B", "C", "D", "E", "F"];
const POSITION_OPTIONS = ["Buy", "Sell"];

// --- Sample product list, replace with your full array ---
const PRODUCT_OPTIONS = [
  ["YM - Dow - $5.00", "YM - US30 - $5.00"],
  ["MYM - Dow Micro - $0.50", "MYM - US30 Micro - $0.50"],
  ["ES - SXP 500 - $12.50", "ES - SXP 500 - $12.50"],
  ["MES - SXP 500 Micro - $1.25", "MES - SXP 500 Micro - $1.25"],
  ["NQ - NAS - $5.00", "NQ - NAS - $5.00"],
  ["MNQ - NAS Micro - $0.50", "MNQ - NAS Micro - $0.50"],
  ["GC - Gold - $10.00", "GC - Gold - $10.00"],
  ["MGC - Gold Micro - $1.00", "MGC - Gold Micro - $1.00"],
  ["NG - Natural Gas - $10.00", "NG - Natural Gas - $10.00"],
  ["MNG - Natural Gas Micro - $1.00", "MNG - Natural Gas Micro - $1.00"],
  ["CL - Oil - $10.00", "CL - Oil - $10.00"],
  ["MCL - Oil Micro - $1.00", "MCL - Oil Micro - $1.00"],
  ["RT - Russell - $5.00", "RT - Russell - $5.00"],
  ["M2K - Russell Micro - $0.50", "M2K - Russell Micro - $0.50"],
  ["SI - Silver - $25.00", "SI - Silver - $25.00"],
  ["SIL - Silver Micro - $5.00", "SIL - Silver Micro - $5.00"],
  ["HG - Copper - $25.00", "HG - Copper - $12.50"],
  ["MHG - Copper Micro - $5.00", "MHG - Copper Micro - $1.25"],
  ["6E - Euro FX Futures - $6.25", "6E - Euro FX Futures - $6.25"],
  ["M6E - Euro FX Micro - $1.25", "M6E - Euro FX Micro - $1.25"],
  ["6B - British Pound - $6.25", "6B - British Pound - $6.25"],
  ["M6B - Micro British Pound - $0.60", "M6B - Micro British Pound - $0.60"],
  ["6C - Canadian Dollar - $5.00", "6C - Canadian Dollar - $5.00"],
  ["6J - Japanese Yen - $6.25", "6J - Japanese Yen - $6.25"],
  ["6S - Swiss Franc - $6.25", "6S - Swiss Franc - $6.25"],
  ["HE - Lean Hog - $10.00", "HE - Lean Hog - $10.00"],
  ["GF - Feeder Cattle - $12.50", "GF - Feeder Cattle - $12.50"],
  ["LE - Live Cattle - $10.00", "LE - Live Cattle - $10.00"],
  ["BTC - Bitcoin - $25.00", "BTC - Bitcoin - $25.00"],
  ["MBT - Micro Bitcoin - $0.50", "MBT - Micro Bitcoin - $0.50"],
  ["ZS - Soybean - $12.50", "ZS - Soybean - $12.50"],
  ["XK - Mini Soybean - $1.25", "XK - Mini Soybean - $1.25"],
  ["ZW - Wheat - $12.50", "ZW - Wheat - $12.50"],
  ["MZW - Micro Wheat - $2.50", "MZW - Micro Wheat - $2.50"],
  ["ZC - Corn - $12.50", "ZC - Corn - $12.50"],
  ["MZC - Corn Mircro - $2.50", "MZC - Corn Mircro - $2.50"],
  ["ZF - 5 Year T-Note - $7.81", "ZF - 5 Year T-Note - $7.81"],
  ["TN - Ultra 10 Year Note - $15.63", "TN - Ultra 10 Year Note - $15.63"],
  ["MTN - Ultra 10 Year Note Micro - $1.56", "MTN - Ultra 10 Year Note Micro - $1.56"],
  ["ZN - 10 Year T-Note - $15.62", "ZN - 10 Year T-Note - $15.62"],
  ["ZT - 2 Year Note - $7.81", "ZT - 2 Year Note - $7.81"],
];

// --- Default Settings ---
const defaultSettings = {
    Product: "",
    // user-provided during create flow (may be 0 if not set yet)
    "Initial Balance": 0,
    "Running Balance": 0,
    "Account Goal": 0,
    "Maximum Loss Limit": 0,

    // derived/saved during create flow (load from Firestore if present)
    "Profit Target": 0,
    "Remaining Profit Amount": 0,
    "Prop Initial Balance": 0,
    "Prop Running Balance": 0,
    "Prop Account Goal": 0,

    // standard defaults
    "Stop Loss": 50,
    "Take Profit": 100,
    "Daily Goal": 500,
    "Daily Loss Limit": 200,
    "Risk Mode": "%",         // "Percent" | "Dollar"
    "Risk Percentage": 3.00,        // used when Risk Mode = Percent
    "Risk Amount": 0,               // used when Risk Mode = Dollar
    "Maximum Contracts": 10,
    "Drawdown Limit % Toggle": true,
    "Calculator Only": false,

  // popup timestamps (leave as existing keys if you use them elsewhere)
  DailyGoalPopupShownDate: "",
  DrawdownPopupShownDate: "",
  LossLimitWarningShownDate: "",
  MoodVolatilityPopupShownDate: "",
  OverRiskWarningShownDate: "",
  ConsecutiveWinsPopupShownDate: "",
  AccountGoalReachedShownDate: "",
  LessonsLearnedStreakWarningShownDate: "",
  RiskOfRuinWarningShownDate: "",
  TwoLossWarningShownDate: "",
  ThreeLossWarningShownDate: "",
  OvertradingPopupShownDate: "", 
  TwoLossAfterGoalWarningShownDate: "",
  Win10NoRulesPopupShownDate: "",
  Win10WithRulesPopupShownDate: "",
  PretradeChecklistShownDate: "",
  "DrawdownLockActive": false,
  "DrawdownLockStartedDate": "",
  "DrawdownLockPeak": 0,
};


const defaultJournal = {
  "Product": null,
  "Actual Balance": "",
  "Position": "",
  "Date": "",
  "Session": "",
  "Entry Time": "",
  "Exit Time": "",
  "Entry Price": "",
  "Exit Price": "",
  "Entry Strategy": "",
  "Exit Strategy": "",
  "Did I follow my Rules?": "",
  "Size": 0,
  "Ticks": "",
  "Potential": "",
  "Suggested Max # of Contracts": 0, // Futures only
  "Win": false,
  "Loss": false,
  "Mood_Mindset": "",
  "Grade": "",
  "Notes": "",
  "Emotional Notes": "",
  "Links_Screenshots": "",
  "Lesson": "",
  "LogLesson": false,
  "Timeframe": "",
  "Needs Review": false,
  "Target Reached": false
};

// --- Timeframe options for the Journal combobox ---
const TIMEFRAME_OPTIONS = ["30s","1m","2m","3m","5m","10m","15m","30m","1h","2h","4h","D","W","M"];

const DEBUG_POPUPS = true;
const dgroup = (label, fn) => {
  if (!DEBUG_POPUPS) return fn?.();
  console.group(label);
  try { return fn?.(); } finally { console.groupEnd(); }
};


// Treat these keys as "clearable numeric" (keep raw string while typing)
const RISK_KEYS = new Set(["Risk Percentage", "Risk Dollar", "Risk Amount", "Risk Dollar Amount"]);

// Helpers for blur-commit
const isDec = (s) => s === "" || /^\d*\.?\d*$/.test(s);
const commitNumberOrEmpty  = (s) => {
  const t = String(s).trim();
  if (t === "") return "";
  const n = Number(t);
  return Number.isFinite(n) ? n : "";
};
// For $ risk: must be > 0, but no upper limit
const commitPositiveOrEmpty = (s) => {
  const t = String(s).trim();
  if (t === "") return "";
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : "";
};


// --- Helper functions (keep at top) ---
function getPricePerTick(productStr) {
  if (!productStr) return 0;
  const match = productStr.match(/\$\s?([0-9,.]+)/);
  return match ? parseFloat(match[1].replace(/,/g, "")) : 0;
}

function getSessionFromTime(time24) {
  if (!time24) return "";
  
  const [hour] = time24.split(":").map(Number);

  // New York: 8:00 AM - 5:00 PM (17:00)
  if (hour >= 8 && hour < 17) return "New York";
  
  // London: 3:00 AM - 8:00 AM
  if (hour >= 3 && hour < 8) return "London";
  
  // Asian (Tokyo): 7:00 PM (19:00) - 3:00 AM 
  // (Using || because it crosses midnight)
  if (hour >= 19 || hour < 3) return "Asian";
  
  // Sydney: 5:00 PM (17:00) - 7:00 PM (19:00)
  // (Captures the gap after NY closes and before Tokyo opens)
  if (hour >= 17 && hour < 19) return "Sydney";
  
  return "";
}

// --- Tick-size helpers (same mapping as FuturesBacktest) ---
function getProductSymbol(productStr = "") {
  if (!productStr) return "";
  // Use the symbol before the first " - " or "·" etc.
  const firstPart = String(productStr).split(" - ")[0];
  return firstPart.split("·")[0].trim().toUpperCase();
}

const TICK_SIZES = {
  YM: 1,
  MYM: 1,
  ES: 0.25,
  MES: 0.25,
  NQ: 0.25,
  MNQ: 0.25,
  RTY: 0.1,
  M2K: 0.1,
  RT: 0.1,
  GC: 0.1,
  MGC: 0.1,
  CL: 0.01,
  MCL: 0.01,
  NG: 0.001,
  MNG: 0.001,
  SI: 0.005,
  SIL: 0.005,
  ZS: 0.25,
  ZC: 0.25,
  ZW: 0.25,
  MZS: 0.25,
  MZC: 0.25,
  MZW: 0.25,
  BTC: 5,
  MBT: 5,
};

function getTickSizeForProduct(productStr) {
  const sym = getProductSymbol(productStr);
  return TICK_SIZES[sym] ?? null;
}

// Returns absolute ticks between entry and exit (always ≥ 0) or null if invalid.
function computeTicksFromPricesAbs(productStr, entryPriceRaw, exitPriceRaw) {
  const tickSize = getTickSizeForProduct(productStr);

  // ✅ Require BOTH entry and exit price to be explicitly provided (non-empty)
  const entryStr = entryPriceRaw != null ? String(entryPriceRaw).trim() : "";
  const exitStr  = exitPriceRaw  != null ? String(exitPriceRaw).trim()  : "";
  if (!entryStr || !exitStr) return null;

  const entry = Number(entryStr);
  const exit  = Number(exitStr);

  if (!tickSize || tickSize <= 0) return null;
  if (!Number.isFinite(entry) || !Number.isFinite(exit)) return null;

  const diff = Math.abs(exit - entry);
  if (!diff) return 0;

  const rawTicks = diff / tickSize;
  if (!Number.isFinite(rawTicks)) return null;

  return Math.round(rawTicks);
}






function formatCurrency(val, currency = "USD") {
  if (val === "" || val === null || typeof val === "undefined") return "";
  const n = Number(val);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("en-US", { style: "currency", currency, minimumFractionDigits: 2 });
}


function absMLL(val) {
  const n = Number(val || 0);
  return Math.abs(n);
}

function useBlockWheelOnNumber(inputRef) {
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const stopWheel = (e) => e.preventDefault();
    el.addEventListener("wheel", stopWheel, { passive: false });
    return () => el.removeEventListener("wheel", stopWheel);
  }, [inputRef]);
}

function recomputePropFields(s) {
  const rb = Number(s["Running Balance"] || 0);
  const ib = Number(s["Initial Balance"] || 0);
  const ag = Number(s["Account Goal"] || 0);
  const m  = absMLL(s["Maximum Loss Limit"]);
  return {
    "Prop Initial Balance": ib + m,
    "Prop Running Balance": rb + m,
    "Prop Account Goal":   ag + m,
  };
}

function SummaryRow({ label, value, red }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <span className={red ? "text-red-600 font-bold" : "text-gray-900 dark:text-gray-100"}>
        {value}
      </span>
    </div>
  );
}

function to24HourTime(str) {
  if (!str) return "";
  if (/^\d{2}:\d{2}$/.test(str)) return str; // already 24h
  const m = str.match(/^(\d{1,2}):(\d{2})\s*([APap][Mm])$/);
  if (m) {
    let [, h, mins, ap] = m;               // <-- note the leading comma to skip the full match
    let hour = parseInt(h, 10);
    if (ap.toUpperCase() === "PM" && hour !== 12) hour += 12;
    if (ap.toUpperCase() === "AM" && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, "0")}:${mins}`;
  }
  return "";
}

function toAmPm(str) {
  // Convert "13:15" to "01:15 PM"
  if (!str) return "";
  if (!/^\d{2}:\d{2}$/.test(str)) return str;
  let [h, m] = str.split(":");
  h = parseInt(h, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  let hour12 = h % 12;
  if (hour12 === 0) hour12 = 12;
  return `${hour12.toString().padStart(2, "0")}:${m} ${ampm}`;
}

// Converts "01:23 PM" or "13:23" to { h, m, s }
function parseTimeToParts(str) {
  if (!str) return null;
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(str)) {
    const [h, m, s] = str.split(":").map(Number);
    return { h, m, s: s || 0 };
  }
  const match = str.match(/^(\d{1,2}):(\d{2})\s*([APap][Mm])$/);
  if (match) {
    let [, h, m, ap] = match;              // <-- fixed
    h = parseInt(h, 10);
    m = parseInt(m, 10);
    if (ap.toUpperCase() === "PM" && h !== 12) h += 12;
    if (ap.toUpperCase() === "AM" && h === 12) h = 0;
    return { h, m, s: 0 };
  }
  return null;
}

function parseCurrency(str) {
  const n = Number(String(str ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

// Duration in HH:MM:SS (handles overnight crossing)
function getDurationHHMMSS(start, end) {
  const et = parseTimeToParts(start);
  const xt = parseTimeToParts(end);
  if (!et || !xt) return "";
  let startSec = et.h * 3600 + et.m * 60 + (et.s || 0);
  let endSec = xt.h * 3600 + xt.m * 60 + (xt.s || 0);
  let diff = endSec - startSec;
  if (diff < 0) diff += 24 * 3600; // Crosses midnight
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

// Accepts "01:23 PM" or "13:23" or "13:23:00" as input
function timeStringToSeconds(str) {
  if (!str) return 0;
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(str)) {
    const [h, m, s] = str.split(":").map(Number);
    return (h * 3600) + (m * 60) + (s || 0);
  }
  const match = str.match(/^(\d{1,2}):(\d{2})\s*([APap][Mm])$/);
  if (match) {
    let [, h, m, ap] = match;              // <-- fixed
    h = parseInt(h, 10);
    m = parseInt(m, 10);
    if (ap.toUpperCase() === "PM" && h !== 12) h += 12;
    if (ap.toUpperCase() === "AM" && h === 12) h = 0;
    return (h * 3600) + (m * 60);
  }
  return 0;
}

// Converts seconds to "HH:MM:SS"
function secondsToHHMMSS(totalSeconds) {
  totalSeconds = Math.max(0, totalSeconds);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [
    h.toString().padStart(2, "0"),
    m.toString().padStart(2, "0"),
    s.toString().padStart(2, "0"),
  ].join(":");
}

function decimalToHHMMSS(decimal) {
  if (!decimal || isNaN(decimal)) return "00:00:00";
  const totalSeconds = Math.round(Number(decimal) * 3600);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [
    h.toString().padStart(2, "0"),
    m.toString().padStart(2, "0"),
    s.toString().padStart(2, "0")
  ].join(":");
}

function parseCurrencyToNumber(val) {
  if (val === null || val === undefined) return NaN;
  if (typeof val === "number") return val;
  let s = String(val).trim();
  if (!s) return NaN;

  // Optional: basic EU-style support if there's a comma but no dot: "100,50" => "100.50"
  if (s.includes(",") && !s.includes(".")) {
    s = s.replace(/\./g, "").replace(",", ".");
  }

  // Remove currency symbols, spaces, group separators, etc.
  s = s.replace(/[^0-9.-]/g, "");
  return s ? Number(s) : NaN;
}



// Use the same constant in each file
const TWO_LOSS_MESSAGE = [
  "You've had 2 losses in a row.",
  "",
  "Evaluate: Take a break and review your recent trades. What can you learn from them before placing your next order?",
  "",
  "Accept: Remember that losses are a part of trading. Learning to be okay with them is crucial for success.",
  "",
  "Refocus: No strategy is 100%, but following your rules consistently is the only way to improve your chances.",
  "",
  "You've got this. Apply what you learned in the next one!"
].join("\n");



function renderWithTiltTooltip(text) {
  const parts = text.split(/(\btilt\b)/gi);
  return parts.map((part, idx) => {
    if (part.toLowerCase() === "tilt") {
      return (
        <Tooltip
          key={idx}
          variant="yellow"
          content={
            <div className="space-y-1">
              <div className="text-sm uppercase tracking-wide font-bold opacity-100">
                On Tilt
              </div>
              <p className="mt-5  text-[14px] leading-relaxed">
                A heightened emotional state (like frustration or euphoria) that leads to impulsive, undisciplined trading, ignoring your plan.
              </p>
              <p className="mt-5  text-[14px] leading-relaxed">
                Your brain has entered "fight or flight" mode. Your logical decision-making is offline, and you are no longer in control.
              </p>

              <p className="mt-6 text-[14px] font-bold leading-relaxed">
                What to do:  
              </p>
              <ul className="mt-2 list-disc pl-4 text-[14px] opacity-100">
                <li>STOP TRADING IMMEDIATELY. This is the most important step.</li>
                <li>Step away from the screen. Reset your mind with a walk, exercise (particularly weight lifting), or another non-trading activity.</li>
                <li>Do not resume trading until you are calm and focused.</li>
              </ul>

            </div>
          }
        >
          tilt
        </Tooltip>
      );
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
}


function Tooltip({
  children,
  content,
  variant = "neutral",
  backdrop = false,   // ← NEW: dim the page behind the tooltip
  closeOnEsc = true,  // ← NEW: press Esc to close when backdrop is on
  lockScroll = true,  // ← NEW: lock body scroll when backdrop is on
}) {
  const id = useId();
  const [open, setOpen] = useState(false);


    // Close on ESC when using a backdrop
    useEffect(() => {
      if (!(open && backdrop && closeOnEsc)) return;
      const onKeyDown = (e) => { if (e.key === "Escape") setOpen(false); };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, backdrop, closeOnEsc]);
  
    // Optionally lock body scroll while backdrop is visible
    useEffect(() => {
      if (!(open && backdrop && lockScroll)) return;
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }, [open, backdrop, lockScroll]);


  const palette = {
    neutral: {
      panel: "bg-gray-900/95 text-white ring-white/10",
      arrow: "bg-gray-900/95",
      focus: "focus:ring-yellow-500", // trigger outline color
    },
    yellow: {
      panel: "bg-yellow-600/95 text-white ring-yellow-300/30",
      arrow: "bg-yellow-600/95",
      focus: "focus:ring-yellow-500",
    },
    red: {
      panel: "bg-red-800/95 text-white ring-red-300/30",
      arrow: "bg-red-800/95",
      focus: "focus:ring-red-500",
    },
    blue: {
      panel: "bg-slate-900/95 text-white ring-blue-300/30",
      arrow: "bg-slate-900/95",
      focus: "focus:ring-blue-500",
    },
  }[variant] || {
    panel: "bg-gray-900/95 text-white ring-white/10",
    arrow: "bg-gray-900/95",
    focus: "focus:ring-yellow-500",
  };

    const hoverClasses = backdrop
      ? ""
      : "group-hover:opacity-100 group-hover:scale-100 group-focus-within:opacity-100 group-focus-within:scale-100";
  
    return (
      <span
        className="relative inline-flex items-center group"
        onMouseEnter={() => !backdrop && setOpen(true)}
        onMouseLeave={() => !backdrop && setOpen(false)}
      >

      {/* Trigger (focusable + tap/click for mobile) */}
      <span
        tabIndex={0}
        aria-describedby={id}
        aria-expanded={open ? "true" : "false"}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen((o) => !o)}
        onBlur={() => { if (!backdrop) setOpen(false); }}
        className={`underline decoration-dotted underline-offset-2 cursor-help focus:outline-none ${palette.focus} rounded-[3px]`}
      >
        {children}
      </span>

      {/* Tooltip panel */}
            <span
              id={id}
              role={backdrop ? "dialog" : "tooltip"}
              aria-modal={backdrop ? "true" : undefined}
              className={`
                select-text absolute ${backdrop ? "z-[70]" : "z-[60]"}

          bottom-full left-1/2 -translate-x-1/2 mb-2
          max-w-[min(90vw,28rem)] w-max break-words
          rounded-lg px-4 py-3 text-[13px] leading-relaxed font-normal
          shadow-2xl ring-1 backdrop-blur-sm
          transition opacity-0 scale-95 pointer-events-none
          ${palette.panel}
                    ${open ? "opacity-100 scale-100 pointer-events-auto" : ""}
                    ${hoverClasses}
        `}
      >
        {/* Allow string or JSX for content */}
        {typeof content === "string" ? <span className="block">{content}</span> : content}

        {/* Arrow */}
        <span
          aria-hidden="true"
          className={`absolute top-full left-1/2 -translate-x-1/2 h-3 w-3 rotate-45 ${palette.arrow} shadow`}
        />
      </span>

            {/* Backdrop scrim rendered at <body> so it covers the whole viewport */}
            {open && backdrop && createPortal(
              <div
                className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[1px]"
                onClick={() => setOpen(false)}
                aria-hidden="true"
              />,
              document.body
            )}
    </span>
  );
}

// --- Main Component ---
export default function CalculateForm({ userId, accountId }) {
  const [settings, setSettings] = useState({ ...defaultSettings });
  const assetClass = "Futures";
  const [journal, setJournal] = useState(() => ({
  ...defaultJournal,
  Date: getTodayLocalISO(),
  Timeframe: String(settings?.["Default Timeframe"] || "").trim(),
}));

  const [savingSettings, setSavingSettings] = useState(false);
  const [savingTrade, setSavingTrade] = useState(false);
  const [calculatedSummary, setCalculatedSummary] = useState(null);

  // Modal/Popup states
  const [missingFields, setMissingFields] = useState([]);
  const [showZeroContractsPopup, setShowZeroContractsPopup] = useState(false);
  const [lossForDay, setLossForDay] = useState(0);
  const [lossLimitAmount, setLossLimitAmount] = useState(0);
  //const [overRiskMessage, setOverRiskMessage] = useState("");
  //const [warningFlags, setWarningFlags] = useState({}); // This can probably be removed now
  const [projectionOpen, setProjectionOpen] = useState(false); // For Win Projection Table
  const [lossProjectionOpen, setLossProjectionOpen] = useState(false); // For Loss Projection Table
  const [showDaySeparators, setShowDaySeparators] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showStrategyModal, setShowStrategyModal] = useState(false);
  const [lastRiskPercent, setLastRiskPercent] = useState(0);
  const [lastPercentOfBalance, setLastPercentOfBalance] = useState(0);
  const [lastTotalRisk, setLastTotalRisk] = useState(0);
  const [lastActualRiskPercent, setLastActualRiskPercent] = useState(0);
  const drawdownLimitPct = Number(settings["Drawdown Limit %"] || 10);
  const entryTimeRef = useRef();
  const exitTimeRef = useRef();
  const [userEditingSize, setUserEditingSize] = useState(false);
  const [manualCalculate, setManualCalculate] = useState(false);
  const [autoCalculatedOnce, setAutoCalculatedOnce] = useState(false);
  const [drawdownAmount, setDrawdownAmount] = useState(0);
  const [drawdownPct, setDrawdownPct] = useState(0);
  const [loadedFromDB, setLoadedFromDB] = useState(false);
  const popupsAllowedRef = useRef(false);
  const [showLearnMorePopup, setShowLearnMorePopup] = useState(false);
  // Track popups we've shown today in this session
  const shownTodayRef = useRef({});
  const [showCalculator, setShowCalculator] = useState(false);


  // Popup Queue States
  const [popupQueue, setPopupQueue] = useState([]);
  const [currentPopup, setCurrentPopup] = useState(null); // { id: 'popupName', data: { message: '...', ... } }

  // Projections (for win/loss tables)
  const [winProjection, setWinProjection] = useState({ trades: [], tradeCount: 0 });
  const [lossProjection, setLossProjection] = useState({ trades: [], tradeCount: 0, suggestedContracts: 0 });
  const { width, height } = useWindowSize();
  const [showChecklist, setShowChecklist] = useState(false);
  const [userPrefs, setUserPrefs] = useState(() => {
    // Initialize synchronously from localStorage to prevent balance flashing
    const cached = localStorage.getItem("isPrivacyMode");
    return cached === "true" ? { "Privacy Mode": true } : {};
  });
  const isPrivacyMode = userPrefs?.["Privacy Mode"] === true;
  const [initialSettings, setInitialSettings] = useState(null);
    // Show the red "Inactive" tag after a calculation when the loss projection says 0 trades left
  const [hasCalculated, setHasCalculated] = useState(false);
  const stopLoss = Number(settings["Stop Loss"] || 0);
  const riskPercent = Number(settings["Risk Percentage"] || 0);
  const pricePerTick = getPricePerTick(settings["Product"]);
  const canComputeLossProj = stopLoss > 0 && pricePerTick > 0 && riskPercent > 0;
  const accountIsBlown = canComputeLossProj && (lossProjection?.tradeCount ?? 0) === 0;
  // Daily progress bars
const [dailyGoalProg, setDailyGoalProg] = useState({ done: 0, target: 0, pct: 0 });
const [dailyLossProg, setDailyLossProg] = useState({ lost: 0, limit: 0, pct: 0 });
// 1. Add these refs and state variables at the top of the component
const actualBalRef = useRef(null);
const [entryStrategies, setEntryStrategies] = useState([]);
const [exitStrategies, setExitStrategies] = useState([]);

const [subStatus, setSubStatus] = useState("");

  useEffect(() => {
    if (!userId) return;
    const subscribersRef = collection(db, "subscribers");
    const q = query(subscribersRef, where("userId", "==", userId));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        setSubStatus(snapshot.docs[0].data().status || "");
      } else {
        setSubStatus("");
      }
    });
    return () => unsubscribe();
  }, [userId]);

  const isTrialing = subStatus === "TRIALING";

// 2. Ensure this hook is present to prevent mouse-wheel scrolling on the balance input
useBlockWheelOnNumber(actualBalRef);

// 3. Add this useEffect to fetch your strategies from Firestore
useEffect(() => {
  if (!userId || !accountId) return;

  let cancelled = false;

  (async () => {
    try {
      const rows = await fetchJournalRows(userId, accountId);
      if (!Array.isArray(rows) || cancelled) return;

      const entries = [...new Set(rows.map(r => r["Entry Strategy"]).filter(Boolean))];
      const exits   = [...new Set(rows.map(r => r["Exit Strategy"]).filter(Boolean))];

      if (!cancelled) {
        setEntryStrategies(entries);
        setExitStrategies(exits);
      }
    } catch (err) {
      console.error("Failed to prefill strategy options:", err);
    }
  })();

  return () => { cancelled = true; };
}, [userId, accountId]);

// --- DEBUG (minimal, safe to remove later sandra) ---
const PDBG = (...args) => {
  try {
    console.log("%c[PropPopup]", "color:#b45309;font-weight:600", new Date().toLocaleTimeString(), ...args);
  } catch {}
};

// Expose a tiny inspector so you can check flags/queue from DevTools
useEffect(() => {
  if (typeof window !== "undefined") {
    window.__pop = {
      today: () => getTodayLocalISO?.() ?? new Date().toISOString().slice(0,10),
      settings: () => ({ 
        TwoLossAfterGoalWarningShownDate: settings?.TwoLossAfterGoalWarningShownDate,
        TwoLossWarningShownDate: settings?.TwoLossWarningShownDate,
        DailyGoal: settings?.["Daily Goal"]
      }),
      queue:  () => ({ queue: popupQueue, current: currentPopup?.id }),
    };
  }
}, [settings, popupQueue, currentPopup]);

const [userFavorites, setUserFavorites] = useState({ futures: [], forex: [] });

useEffect(() => {
  setJournal(prev => {
    const next = { ...prev };
    let changed = false;

    // Helper to apply default if field is empty
    const applyDefault = (field, settingKey) => {
      const defVal = String(settings[settingKey] || "").trim();
      if (defVal && !String(next[field] || "").trim()) {
        next[field] = defVal;
        changed = true;
      }
    };

    applyDefault("Entry Strategy", "Default Entry Strategy");
    applyDefault("Exit Strategy", "Default Exit Strategy");
    applyDefault("Timeframe", "Default Timeframe");
    applyDefault("Session", "Default Session"); // ✅ ADDED THIS LINE

    return changed ? next : prev;
  });
}, [
  settings["Default Entry Strategy"], 
  settings["Default Exit Strategy"], 
  settings["Default Timeframe"],
  settings["Default Session"] // ✅ ADDED THIS DEPENDENCY
]);

useEffect(() => {
  if (!userId) return;

  const ref = doc(db, "users", userId, "settings", "userSettings");
  const unsub = onSnapshot(ref, snap => {
    const favs = snap.exists() ? (snap.data().favorites || { futures: [], forex: [] }) 
                               : { futures: [], forex: [] };
    setUserFavorites(favs);
  });

  return unsub; // cleanup
}, [userId]);




const favoriteFuturesOptions = useMemo(() => {
  const favCodes = (userFavorites?.futures || []).map(s => String(s).toUpperCase());
  if (!favCodes.length) return [];
  return favCodes
    .map(code => PRODUCT_OPTIONS.find(([v]) => String(v).startsWith(code)))
    .filter(Boolean);
}, [userFavorites, PRODUCT_OPTIONS]);

const favoriteValues = useMemo(
  () => new Set(favoriteFuturesOptions.map(([v]) => v)),
  [favoriteFuturesOptions]
);

const nonFavoriteOptions = useMemo(
  () => PRODUCT_OPTIONS.filter(([v]) => !favoriteValues.has(v)),
  [favoriteValues]
);



  // Function to get today's date in ISO format (YYYY-MM-DD)
function getTodayLocalISO() {
  const today = new Date();
  const tzOffset = today.getTimezoneOffset() * 60000;
  return new Date(today - tzOffset).toISOString().slice(0, 10);
}

// Build the full message for the "2 losses in a row" popup
// Build the full message for the "2 losses in a row" popup
function buildTwoLossMessage(rulesFirst, rulesSecond, lessonSecondRaw) {
  const v = (x) => String(x ?? "").toLowerCase().trim();
  const yes = (x) => ["yes", "true", "1"].includes(v(x));
  const no  = (x) => ["no", "false", "0"].includes(v(x));

  const lessonSecond = (lessonSecondRaw || "").trim();
  const hasLesson = !!lessonSecond;
  const msgIntro = "You lost 2 trades in a row.\n\n";

  // No/No
  if (no(rulesFirst) && no(rulesSecond) && !hasLesson) {
    return (
      msgIntro +
      "No strategy wins 100% of the time, however following your proven strategy will improve your chances of successful trading.\n\n" +
      "Take a moment to check your mindset and don't forget to follow your rules.\n\n" +
      "Not following your rules while on a losing streak can easily make us go on tilt."
    );
  }
  if (no(rulesFirst) && no(rulesSecond) && hasLesson) {
    return (
      msgIntro +
      "No strategy wins 100% of the time, following your proven strategy will improve your chances of successful trading.\n\n" +
      "You learned something from the trade, which is important. Try to apply what you learned on the next trade."
    );
  }

  // Yes/Yes
  if (yes(rulesFirst) && yes(rulesSecond) && !hasLesson) {
    return (
      msgIntro +
      "No strategy wins 100% of the time, you followed your rules of your proven strategy, which is important.\n\n" +
      "Review the trade to see if there was anything you could have done differently. Sometimes it could be that you are trading during difficult market conditons.\n\n" +
      "Be careful to monitor your mood/mindset to make sure you don't go on tilt or revenge trade."
    );
  }
  if (yes(rulesFirst) && yes(rulesSecond) && hasLesson) {
    return (
      msgIntro +
      "No strategy wins 100% of the time, you followed your rules of your proven strategy, which is important.\n\n" +
      "You learned something from it. \n\n" +
      "Be careful to monitor your mood/mindset to make sure you don't go on tilt or revenge trade. \n\n" +
      "Try to apply what you learned in the next trade."
    );
  }

  // No/Yes
  if (no(rulesFirst) && yes(rulesSecond) && hasLesson) {
    return (
      msgIntro +
      "You lost 2 trades in a row, but followed your rules on the second one.\n\n" +
      "No strategy is 100% but you followed your rules, which is important, and you learned something. \n\n" +
      "Try to apply what you learned in the next one."
    );
  }
  if (no(rulesFirst) && yes(rulesSecond) && !hasLesson) {
    return (
      msgIntro +
      "You lost 2 trades in a row, but followed your rules on the second one.\n\n" +
      "No strategy is 100% but you followed your rules, which is important.\n\n" +
      "Evaluate the trade and see if there was anything that you could have done differently."
    );
  }

  // Yes/No
  if (yes(rulesFirst) && no(rulesSecond) && !hasLesson) {
    return (
      msgIntro +
      "You lost 2 trades in a row, but didn't follow your rules on the second one.\n\n" +
      "No strategy is 100%, but it is important to always follow your rules. \n\n" +
      "Take some time to evaluate your trades and see if there is anything that could be learned from it. \n\n" +
      "Try to apply what you learned in the next one."
    );
  }
  if (yes(rulesFirst) && no(rulesSecond) && hasLesson) {
    return (
      msgIntro +
      "You lost 2 trades in a row, but didn't follow your rules on the second one.\n\n" +
      "No strategy is 100%, but it is important to always follow your rules. You learned something from the trade, which is good.\n\n" +
      "Try to apply what you learned in the next one."
    );
  }

  // Fallback
  return msgIntro + "Review your last two trades and take some time to reset your mindset before the next one.";
}



// Map persisted keys (already present in your file)
const SHOWN_DATE_KEY = {
  dailyGoalPopup: "DailyGoalPopupShownDate",
  drawdownWarning: "DrawdownPopupShownDate",
  lossLimitWarning: "LossLimitWarningShownDate",
  moodVolatilityPopup: "MoodVolatilityPopupShownDate",
  overRiskWarning: "OverRiskWarningShownDate",
  consecutiveWinsPopup: "ConsecutiveWinsPopupShownDate",
  accountGoalReached: "AccountGoalReachedShownDate",
  lessonsLearnedStreakWarning: "LessonsLearnedStreakWarningShownDate",
  riskOfRuinWarning: "RiskOfRuinWarningShownDate",
  twoLossWarning: "TwoLossWarningShownDate",
  threeLossWarning: "ThreeLossWarningShownDate",
  overtradingPopup: "OvertradingPopupShownDate",
  twoLossAfterGoalWarning: "TwoLossAfterGoalWarningShownDate",
  win10NoRulesPopup: "Win10NoRulesPopupShownDate",
  win10WithRulesPopup: "Win10WithRulesPopupShownDate",
  maxLossLimitBlown: "MaxLossLimitBlownShownDate",
  tooManyLossesPopup: "TooManyLossesPopupShownDate"
};

const DAILY_SCOPED_IDS = new Set(Object.keys(SHOWN_DATE_KEY));
const queuePopup = (id, data = {}) => {
  const today = getTodayLocalISO();

  // In-session per-day guard
  if (DAILY_SCOPED_IDS.has(id) && shownTodayRef.current[id] === today) {
    PDBG?.("queuePopup SKIP (session guard):", { id, today });
    return;
  }

  // Persisted once-per-day guard
  const key = SHOWN_DATE_KEY[id];
  if (key && String(settings?.[key] || "").slice(0, 10) === today) {
    PDBG?.("queuePopup SKIP (persisted guard):", { id, key, val: settings?.[key], today });
    return;
  }

  // ✅ Mark as seen for today in this session *before* enqueue to avoid race
  if (DAILY_SCOPED_IDS.has(id)) {
    shownTodayRef.current[id] = today;
  }

  setPopupQueue(prev => {
    let next = prev;

    // Priority: after-goal beats generic
    if (id === "twoLossAfterGoalWarning") {
      next = next.filter(p => p.id !== "twoLossWarning");
    }
    if (id === "twoLossWarning") {
      const higherPending = next.some(p => p.id === "twoLossAfterGoalWarning");
      const higherShowing = currentPopup?.id === "twoLossAfterGoalWarning";
      if (higherPending || higherShowing) return next;
    }

    // De-dupe queue + don't re-add current
    if (next.some(p => p.id === id) || currentPopup?.id === id) return next;

    return [...next, { id, data }];
  });
};



  // Function to close the current popup and show the next
  const closeCurrentPopup = () => {
    setCurrentPopup(null);
  };

useEffect(() => {
  let alive = true;
  (async () => {
    try {
      if (!userId || !accountId) return;
      const raw = (await getSettings(userId, accountId)) || {};
      if (!alive) return;
      // Do NOT push defaults back to Firestore here.
      // Just load what’s saved.
      setSettings(prev => ({ ...prev, ...raw }));
      if (initialSettings === null) setInitialSettings(raw);
      setLoadedFromDB(true);
    } catch (err) {
      console.error("Error loading settings:", err);
    }
  })();
  return () => { alive = false; };
}, [userId, accountId]);



useEffect(() => {
    if (!userId) return;
    getUserSettings(userId).then(data => {
      // If the prefs doc doesn't exist (data = {}), set default prefs!
      if (!data || Object.keys(data).length === 0) {
        // Pick your defaults here
        const defaultPrefs = {
          "Pretrade Checklist": "Yes",   // Or "No" if you want off by default
          "PretradeChecklistShownDate": ""
          // ...add any other default user prefs you want here
        };
        setUserPrefs(defaultPrefs);
        // ✅ Cache default to local storage
        localStorage.setItem("isPrivacyMode", "false"); 
        updateUserSettings(userId, defaultPrefs); // Save to Firestore
      } else {
        setUserPrefs(data);
        // ✅ Cache fetched setting to local storage
        localStorage.setItem("isPrivacyMode", data["Privacy Mode"] === true ? "true" : "false"); 
      }
    });
  }, [userId]);



  // Effect to manage the popup queue
// Effect to manage the popup queue
useEffect(() => {
    if (currentPopup === null && popupQueue.length > 0) {
      const nextPopup = popupQueue[0];
      setPopupQueue(popupQueue.slice(1)); // Remove the first item
      setCurrentPopup(nextPopup); // Set as current

      // Note: The timer for the 'tradeSaved' popup will be handled in handleSaveTrade now.
    }
  }, [popupQueue, currentPopup]);



async function checkConsecutiveWins(
  trades,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  const today = getTodayLocalISO();

  // ✅ Only look at today's trades (matches your other daily checks)
  const todayTrades = (trades || []).filter(t => (t["Date"] || "").startsWith(today));
  if (todayTrades.length < 5) return;

  // Be tolerant if "Win" can be string/boolean
  const isWin = v => v === true || String(v).toLowerCase() === "true" || String(v).toLowerCase() === "yes";

  // Keep your original “last 3 are wins” intent
  const last3 = todayTrades.slice(-5);
  const popupId = "consecutiveWinsPopup";
  const shownDateKey = "ConsecutiveWinsPopupShownDate";

  if (last3.every(t => isWin(t["Win"])) && currentSettings[shownDateKey] !== today) {
    queuePopup(popupId);
    const newSettings = { ...currentSettings, [shownDateKey]: today };
    await updateSettingsFunc(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  } else if (!last3.every(t => isWin(t["Win"])) && currentSettings[shownDateKey] === today) {
    // Optional: resetting here allows a second popup later the same day
    const newSettings = { ...currentSettings, [shownDateKey]: "" };
    await updateSettingsFunc(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  }
}


async function checkOvertrading(trades, maxTradesPerDay, currentSettings, updateSettingsFunc, userId, accountId, setLocalSettings) {
  const today = getTodayLocalISO();
  const todayTrades = trades.filter(t => (t["Date"] || "").startsWith(today));
  const popupId = "overtradingPopup";
  const shownDateKey = "OvertradingPopupShownDate";

  // Check if we are over the limit AND if the flag for today isn't already set
  if (todayTrades.length > maxTradesPerDay && currentSettings[shownDateKey] !== today) {
    queuePopup(popupId, { maxTradesPerDay });
    
    const newSettings = { ...currentSettings, [shownDateKey]: today };
    
    // Force wait for the database write to complete
    await updateSettingsFunc(userId, accountId, newSettings); 
    setLocalSettings(newSettings);
  } else if (todayTrades.length <= maxTradesPerDay && currentSettings[shownDateKey] === today) {
    // Reset if the user deletes trades or the day changes
    const newSettings = { ...currentSettings, [shownDateKey]: "" };
    await updateSettingsFunc(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  }
}


function highlightHundredPercent(line) {
  if (!line) return null;

  // This splits the line by "100%" but keeps "100%" in the array.
  return line.split(/(100%)/g).map((part, i) =>
    part === "100%" ? (
      <span key={i} className="font-extrabold text-yellow-500 dark:text-yellow-400">
        100%
      </span>
    ) : (
      // Use a React.Fragment to avoid adding extra wrapper elements
      <React.Fragment key={i}>{renderWithTiltTooltip(part)}</React.Fragment>
    )
  );
}

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
// --- Account blown helpers (minimal & safe) ---
const toNum = (v) => Number(v ?? 0);
const getMLL = (s) => toNum(s["Maximum Loss Limit"] ?? s["Max Loss Limit"]); // supports either key
const getRB  = (s) => toNum(s["Running Balance"]);
const reachedBlown = (s) => getRB(s) <= getMLL(s); // NEW: blown when RB <= MLL
const shownToday = (v, today) => String(v || "").slice(0, 10) === today;


function formatThreeLossMessage(line) {
  if (!line) return null;

  const highlights = {
    "Take a break!": "font-bold text-red-500 dark:text-red-500",
    "Trading is 80% Trading Psychology.": "font-bold text-blue-500 dark:text-blue-400",
  };

  // Escape phrases so regex treats them literally
  const escaped = Object.keys(highlights).map(escapeRegExp).join("|");
  const regex = new RegExp(`(${escaped})`, "g");

  return line.split(regex).map((part, i) => {
    return highlights[part]
      ? <span key={i} className={highlights[part]}>{part}</span>
      // ⬇️ route anything else through the tilt tooltip wrapper
      : <React.Fragment key={i}>{renderWithTiltTooltip(part)}</React.Fragment>;
  });
}

async function checkTooManyLosses(
  trades,
  maxLossesPerDay,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  const today = getTodayLocalISO();

  // Be tolerant of boolean/strings in the "Loss" field
  const isLoss = (v) => String(v).toLowerCase() === "true" || String(v).toLowerCase() === "yes";

  const todayLosses = trades.filter(
    (t) => (t["Date"] || "").startsWith(today) && isLoss(t["Loss"])
  );

  const popupId = "tooManyLossesPopup";
  const shownDateKey = "TooManyLossesPopupShownDate";

  // Fire when the limit is REACHED (>=), not only when it's exceeded
  if (todayLosses.length >= maxLossesPerDay && currentSettings[shownDateKey] !== today) {
    queuePopup(popupId, { maxLossesPerDay });
    const newSettings = { ...currentSettings, [shownDateKey]: today };
    await updateSettingsFunc(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  }
  // Reset once you're back UNDER the limit
  else if (todayLosses.length < maxLossesPerDay && currentSettings[shownDateKey] === today) {
    const newSettings = { ...currentSettings, [shownDateKey]: "" };
    await updateSettingsFunc(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  }
}


async function checkMoodVolatility(
  trades,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  const todayISO = getTodayLocalISO();
  const shownDateKey = "MoodVolatilityPopupShownDate";

  // Defensive: if the flag was corrupted to an array, reset it
    if (Array.isArray(currentSettings?.[shownDateKey])) {
      const reset = { ...currentSettings, [shownDateKey]: "" };
      await updateSettingsFunc(userId, accountId, reset);
      setLocalSettings?.(reset);
    }

  if (!Array.isArray(trades)) return;

  // Only look at today's trades
  const todayTrades = trades.filter(t => (t["Date"] || "").startsWith(todayISO));
  if (todayTrades.length < 3) return;

  const last3 = todayTrades.slice(-3);
  const moods = last3.map(t => {
    const v = Number(t["Mood_Mindset"] ?? t["Mood"] ?? t["Mood / Mindset"]);
    return Number.isFinite(v) ? v : null;
  });

  // Trigger when all three are 4 or 5
  const allHigh = moods.length === 3 && moods.every(v => v !== null && (v === 4 || v === 5));
  if (!allHigh) return;

  // Fire once per day
    if (currentSettings?.[shownDateKey] !== todayISO) {
      queuePopup("moodVolatilityPopup");
      const next = { ...currentSettings, [shownDateKey]: todayISO };
      await updateSettingsFunc(userId, accountId, next);
      setLocalSettings?.(next);
    }
}

// --- helpers ---
function getPropPoolParts(s) {
  const ib  = Number(s["Initial Balance"] || 0);
  const mll = Number(s["Maximum Loss Limit"] || s["Max Loss Limit"] || 0);
  if (!(ib > 0 && mll > 0)) return null;
  // robust whether MLL is an amount or a threshold
  const blownAt   = Math.max(mll, ib - mll);  // absolute balance where the challenge fails
  const poolStart = Math.min(mll, ib - mll);  // dollars you can lose from start
  return { blownAt, poolStart };
}

// Build a fresh, empty journal object using current settings (so Date/Balance are sensible)
const makeDefaultJournal = (s) => ({
  "Date": getTodayLocalISO(),
  "Trade #": "",
  "Product": s?.["Product"] || "",
  "Timeframe": s?.["Default Timeframe"] || "",
  "Size": "",
  "Entry Time": "",
  "Exit Time": "",
  "Actual Balance": "",
  "Win": "",
  "Loss": "",
  "Did I follow my Rules?": "",
  "Lesson": "",
  "Notes": "",
  // Add any other headers you render in this form to ensure they reset:
  "Position": "",
  "Session": s?.["Default Session"] || "",
  "Entry Strategy": s?.["Default Entry Strategy"] || "",
  "Exit Strategy": s?.["Default Exit Strategy"] || "",
  "Links_Screenshots": "",
  "LogLesson": false,
});

// key to force-remount the form section (helps reset any uncontrolled fields)
const [formResetKey, setFormResetKey] = useState(0);

async function checkPropDrawdown(
  trades,
  drawdownLimitPct,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings,
  setDrawdownAmount,
  setDrawdownPct,
  isWin // <-- NEW
) {
  const today = getTodayLocalISO();
  const popupId  = "drawdownWarning";
  const shownKey = "DrawdownPopupShownDate"; // legacy (analytics only)

  // Respect the toggle
  if (!currentSettings?.["Drawdown Limit % Toggle"]) return;

  // Compute "prop pool": max(0, Balance - blownAt)
  const parts = getPropPoolParts(currentSettings);
  if (!parts) return;
  const { blownAt } = parts;

  const rows = [...(trades || [])].sort(
    (a, b) => (Number(a.tradeNum ?? a["Trade #"] ?? 0) - Number(b.tradeNum ?? b["Trade #"] ?? 0))
  );
  const pools = rows.map(t => {
    const rb = Number(t["Running Balance"]);
    const ab = Number(t["Actual Balance"]);
    const bal = Number.isFinite(rb) && rb >= 0 ? rb : (Number.isFinite(ab) && ab >= 0 ? ab : null);
    return bal !== null ? Math.max(0, bal - blownAt) : null;
  }).filter(v => v !== null);
  if (pools.length === 0) return;

  const peak    = Math.max(...pools);
  const current = pools[pools.length - 1];

  const ddAmt = peak - current;
  const ddPct = peak > 0 ? ((peak - current) / peak) * 100 : 0;

  // Keep the UI in sync
  setDrawdownAmount?.(ddAmt);
  setDrawdownPct?.(ddPct);

  const overLimit   = ddPct >= Number(drawdownLimitPct || 0);
  const lockActive  = !!currentSettings?.DrawdownLockActive;
  const shownLegacy = String(currentSettings?.[shownKey] || "");

  // --- Migrate old per-day gate → new lock (once) ---
  if (overLimit && !lockActive && shownLegacy) {
    const patch = {
      DrawdownLockActive: true,
      DrawdownLockPeak: peak,
      DrawdownLockStartedDate: shownLegacy,
    };
    await updateSettingsFunc(userId, accountId, { ...currentSettings, ...patch });
    setLocalSettings?.((prev) => ({ ...prev, ...patch }));
    return; // don't re-show immediately
  }

  if (overLimit) {
    if (!lockActive) {
      if (isWin) return; // <-- NEW: Do not show warning on a winning trade
      // EDGE TRIGGER: first time crossing the threshold → show and lock
      queuePopup(popupId, {
        drawdownLimitPct: Number(drawdownLimitPct),
        drawdownAmount: ddAmt,
        drawdownPct: ddPct,
      });
      const patch = {
        [shownKey]: today,            // keep for analytics
        DrawdownLockActive: true,
        DrawdownLockPeak: peak,
        DrawdownLockStartedDate: today,
      };
      await updateSettingsFunc(userId, accountId, { ...currentSettings, ...patch });
      setLocalSettings?.((prev) => ({ ...prev, ...patch }));
    }
    return; // while locked and still over limit → remain silent
  }

  // Recovered (below limit, or new high) → unlock & clear legacy flag
  if (lockActive || shownLegacy) {
    const todayISO = getTodayLocalISO();
    const keepTodayStamp = String(currentSettings?.[shownKey] || "") === todayISO;
    const patch = {
      // keep today's stamp so we won't re-show today
      ...(keepTodayStamp ? {} : { [shownKey]: "" }),
      DrawdownLockActive: false,
      DrawdownLockPeak: 0,
      DrawdownLockStartedDate: "",
    };
    await updateSettingsFunc(userId, accountId, { ...currentSettings, ...patch });
    setLocalSettings?.(prev => ({ ...prev, ...patch }));
  }
}


 // Round to 2dp and trim trailing zeros / dot, no lookbehind (Safari-safe)
 function fmtRatio(n) {
   if (!Number.isFinite(n)) return "";
   const rounded = Number(n.toFixed(2));   // 2dp rounding
   const s = String(rounded);
   return s.includes(".")
     ? s.replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1")
     : s;
 }


// PROP daily drawdown: compares today's SOD Prop base vs current Prop Running Balance
async function checkPropDrawdownDaily(
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings,
  setDrawdownAmount,
  setDrawdownPct,
  isWin // <-- NEW
) {
  const today = getTodayLocalISO();
  const popupId = "drawdownWarning";
  const shownKey = "DrawdownPopupShownDate";

  const limitPct = Number(currentSettings["Drawdown Limit %"] || 0);
  if (!(limitPct > 0)) return;

  // ✅ require SOD base for *today*
  const sodBase = Number(currentSettings.PropSODBase || 0);
  const sodDate = String(currentSettings.PropSODDate || "");
  if (!(sodBase > 0) || sodDate !== today) return;

  // Compare current PRB to the SOD base
  const prb = Number(currentSettings["Prop Running Balance"] || 0);
  const lossAbs = Math.max(0, sodBase - prb);
  const lossPct = sodBase > 0 ? (lossAbs / sodBase) * 100 : 0;

  const alreadyShownToday = currentSettings[shownKey] === today;
  const shouldTrigger = lossPct >= limitPct;

  if (shouldTrigger && !alreadyShownToday) {
    if (isWin) return; // <-- NEW: Do not show warning on a winning trade
    setDrawdownAmount?.(lossAbs);
    setDrawdownPct?.(lossPct);
    queuePopup(popupId, {
      drawdownLimitPct: limitPct,
      drawdownAmount: lossAbs,
      drawdownPct: lossPct
    });
    const patch = { [shownKey]: today };
    await updateSettingsFunc(userId, accountId, { ...currentSettings, ...patch });
    setLocalSettings?.((prev) => ({ ...prev, ...patch }));
  }

  // ❌ do NOT clear [shownKey] the same day
}



// Optional: set your preferred time zone here (or pass one in when calling).
// For US markets you might prefer: 'America/New_York'
// Leave as undefined to use the user's local time.
const DEFAULT_TIME_ZONE = undefined;

function getDailyGoalMessage(timeZone = DEFAULT_TIME_ZONE) {
  const now = new Date();
  const weekday = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    ...(timeZone ? { timeZone } : {})
  }).format(now);

  if (weekday === 'Sunday' || weekday === 'Monday') {
    return "You are off to a great start of the week. Keep the momentum, consider stopping for the day and enjoy the things that matter outside of trading!";
  }
  if (weekday === 'Tuesday' || weekday === 'Wednesday') {
    return "You have the rest of the week!! The market will always be there, but this moment in time will be gone forever. Don't forget to enjoy the things that matter outside of trading!";
  }
  if (weekday === 'Thursday') {
    return "Go and enjoy the rest of your day!";
  }
  if (weekday === 'Friday') {
    return "Today is Capital Preservation Friday! Consider stopping for the day and get ready for a great weekend!!";
  }
  // Saturday or anything unexpected — fallback
  return "Go and enjoy the rest of your day!";
}


// Robust "is today?" helper that matches local day like the rest of the file
function isToday(d, todayISO = getTodayLocalISO()) {
  if (!d) return false;
  if (typeof d === "string") {
    const s = d.trim();
    if (s.includes("-")) return s.slice(0, 10) === todayISO;  // "YYYY-MM-DD" or ISO
    if (s.includes("/")) {
      const [mm, dd, yyyy] = s.split("/");
      const iso = `${yyyy}-${String(mm).padStart(2,"0")}-${String(dd).padStart(2,"0")}`;
      return iso === todayISO;
    }
    const dt = new Date(s);
    return !isNaN(dt) && dt.toISOString().slice(0,10) === todayISO;
  }
  if (d?.seconds) {
    return new Date(d.seconds * 1000).toISOString().slice(0,10) === todayISO; // Firestore TS
  }
  try {
    return new Date(d).toISOString().slice(0,10) === todayISO;
  } catch {
    return false;
  }
}

function buildTwoLossMessage(firstRules, secondRules, secondLesson) {
  const norm = v => String(v ?? "").trim().toLowerCase();
  const r1 = norm(firstRules), r2 = norm(secondRules);

  const base =
    "You've taken 2 losses in a row.\n\n" +
    "This is a pivotal moment where many traders go on tilt and try to win it back.\n\n" +
    "Pause, breathe, and consider stopping for the day.";

  const rulesNote =
    (r1 === "no" || r2 === "no")
      ? "\n\nYou also marked that you did NOT follow your rules on at least one trade. Going off‑plan compounds risk and stress."
      : "\n\nYou also marked that you followed your rules. Great discipline—now protect your mindset.";

  const lessonNote = secondLesson
    ? `\n\nLatest lesson noted: “${secondLesson}”`
    : "";

  const close = "\n\nTrading is 80% psychology. Step away, reset, and come back fresh.";

  return base + rulesNote + lessonNote + close;
}



async function checkDailyProfitTarget(
  trades,
  dailyGoal,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  const todayISO = getTodayLocalISO();
  const popupId = "dailyGoalPopup";
  const shownDateKey = "DailyGoalPopupShownDate";
  const shownGoalKey = "DailyGoalPopupShownForGoal";

  const todayTrades = (trades || []).filter(t => isToday(t["Date"], todayISO));
  const totalProfit = todayTrades.reduce(
    (sum, t) => sum + (Number(t["Actual Reward"]) || 0),
    0
  );

  const alreadyShownToday = String(currentSettings[shownDateKey] || "") === todayISO;

  // ✅ Show once per day when the goal is reached
  if (!alreadyShownToday && dailyGoal > 0 && totalProfit >= dailyGoal) {
    queuePopup(popupId, {
      message: "Congratulations on hitting your daily goal!",
      dailyGoal,
    });

    const next = {
      ...currentSettings,
      [shownDateKey]: todayISO,
      [shownGoalKey]: dailyGoal, // keep for analytics if you like
    };

    await updateSettingsFunc(userId, accountId, next);
    setLocalSettings?.(next);
  }

  // IMPORTANT: we no longer clear today's stamp if PnL drops below the goal.
  // That guarantees the popup only fires once per day.
}




async function checkWin10NoRulesPopup(trade, settings, updateSettings, userId, accountId, setLocalSettings, queuePopup) {
  const today = (new Date()).toISOString().slice(0, 10);
  const key = "Win10NoRulesPopupShownDate";

  // Check for win, 10%+ growth, didn't follow rules, and not shown today
  if (
    trade["Win"] === true &&
    Number(trade["Actual Percent Growth"]?.replace('%','')) >= 10 &&
    (trade["Did I follow my Rules?"] || trade["Did I follow my Rules"]) === "No" &&
    settings[key] !== today
  ) {
    queuePopup("win10NoRulesPopup", {
      message: "Winning BIG is always exciting!\n\n However you want to be careful. Having a big win without following your rules can be dangerous for your mindset.\n\nContinuing to do this could rewire your brain to seek dopamine hits instead of the true accomplishment of following your process.\n\nThis is another way to easily lose control and go on tilt.\n\nTreat this as a neutral event, and be mindful not to get hooked by big wins that happen outside your plan.\n\nDiscipline > excitement!",
    });
    const newSettings = { ...settings, [key]: today };
    await updateSettings(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  } else if (
    // Reset if not matching and was previously set
    (trade["Win"] !== true || Number(trade["Actual Percent Growth"]?.replace('%','')) < 10 || (trade["Did I follow my Rules?"] || trade["Did I follow my Rules"]) !== "No") &&
    settings[key] === today
  ) {
    const newSettings = { ...settings, [key]: "" };
    await updateSettings(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  }
}

async function checkWin10WithRulesPopup(trade, settings, updateSettings, userId, accountId, setLocalSettings, queuePopup) {
  const today = (new Date()).toISOString().slice(0, 10);
  const key = "Win10WithRulesPopupShownDate";

  // Check for win, 10%+ growth, followed rules, and not shown today
  if (
    trade["Win"] === true &&
    Number(trade["Actual Percent Growth"]?.replace('%','')) >= 20 &&
    (trade["Did I follow my Rules?"] || trade["Did I follow my Rules"]) === "Yes" &&
    settings[key] !== today
  ) {
    queuePopup("win10WithRulesPopup", {
      message: "Congratulations! You followed your rules and had a BIG win! \n\nDon't get over confident, stay consistent and continue following your rules. \n\nContinue being Awesome!",
      confetti: true
    });
    const newSettings = { ...settings, [key]: today };
    await updateSettings(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  } else if (
    (trade["Win"] !== true || Number(trade["Actual Percent Growth"]?.replace('%','')) < 20 || (trade["Did I follow my Rules?"] || trade["Did I follow my Rules"]) !== "Yes") &&
    settings[key] === today
  ) {
    const newSettings = { ...settings, [key]: "" };
    await updateSettings(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  }
}


async function checkLessonsLearnedStreak(
  trades,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  const todayISO = getTodayLocalISO();

  // Count how many of today's rows have a lesson logged (text present OR LogLesson true)
  const lessonsToday = (trades || []).filter(t => {
    const d = t["Date"];
    let isToday = false;
    if (!d) return false;
    if (typeof d === "string") {
      isToday = d.slice(0, 10) === todayISO || d.startsWith(todayISO);
    } else if (d?.seconds) {
      isToday = new Date(d.seconds * 1000).toISOString().slice(0, 10) === todayISO;
    } else {
      try { isToday = new Date(d).toISOString().slice(0, 10) === todayISO; } catch { isToday = false; }
    }
    if (!isToday) return false;

     const loggedFlag = t["LogLesson"] === true || String(t["LogLesson"]).toLowerCase() === "true";
     return loggedFlag; // only count lessons that were explicitly logged
  }).length;

  if (lessonsToday < 3) {
    // Optional: if previously shown today but the count fell below 3 (e.g., user edited a trade), clear the flag
    if (currentSettings["LessonsLearnedStreakWarningShownDate"] === todayISO) {
      const next = { ...currentSettings, LessonsLearnedStreakWarningShownDate: "" };
      await updateSettingsFunc(userId, accountId, next);
      setLocalSettings(next);
    }
    return;
  }

  // Fire once per day when the threshold (>= 3) is reached
  if (currentSettings["LessonsLearnedStreakWarningShownDate"] !== todayISO) {
    queuePopup("lessonsLearnedStreakWarning", { lessonsToday });
    const next = { ...currentSettings, LessonsLearnedStreakWarningShownDate: todayISO };
    await updateSettingsFunc(userId, accountId, next);
    setLocalSettings(next);
  }
}


// --- PROP risk of ruin (PRB <= 25% of Prop Initial) ---
async function checkRiskOfRuin(
  lossProjection,  // kept for compatibility with your call sites; not used for logic
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings,
  isWin // <-- NEW
) {
  const todayISO   = getTodayLocalISO();
  const popupId    = "riskOfRuinWarning";
  const shownKey   = "RiskOfRuinWarningShownDate";

  const propInit = Number(currentSettings["Prop Initial Balance"]  || 0);
  const propRB   = Number(currentSettings["Prop Running Balance"]  || 0);
  if (!(propInit > 0)) return;

  const threshold = propInit * 0.25; // 25% of prop initial

  if (propRB <= threshold && currentSettings[shownKey] !== todayISO) {
    if (isWin) return; // <-- NEW: Do not show warning on a winning trade
    
    // Extras: running balance & “blown-at” balance for context, and tradeCount for the UI
    const initial      = Number(currentSettings["Initial Balance"] || 0);
    const maxLossInput = Number(currentSettings["Maximum Loss Limit"] || currentSettings["Max Loss Limit"] || 0);
    const blownAt      = (initial > 0 && maxLossInput > 0) ? Math.max(maxLossInput, initial - maxLossInput) : 0;
    const running      = Number(currentSettings["Running Balance"] || 0);
    const tradeCount   = Number.isFinite(lossProjection?.tradeCount) ? Number(lossProjection.tradeCount) : undefined;

    queuePopup(popupId, {
      mode: "propThreshold",          // make the render branch explicit
      propInitial: propInit,
      propRunning: propRB,
      threshold,
      runningBalance: running,        // <- Account Running Balance you asked to show
      blownAt,                        // <- where the account would be considered failed
      tradeCount                      // <- remaining consecutive losses at current risk
    });
    const next = { ...currentSettings, [shownKey]: todayISO };
    await updateSettingsFunc(userId, accountId, next);
    setLocalSettings(next);
  } else if (propRB > threshold && currentSettings[shownKey] === todayISO) {
    const next = { ...currentSettings, [shownKey]: "" };
    await updateSettingsFunc(userId, accountId, next);
    setLocalSettings(next);
  }
}



async function checkMaxLossLimitBlown(
   currentSettings,
   updateSettingsFunc,
   userId,
   accountId,
   setLocalSettings
 ) {
   try {
     const today = getTodayLocalISO();
     const shownKey = "MaxLossLimitBlownShownDate";

    const initial      = Number(currentSettings["Initial Balance"] || 0);
    const maxLossInput = Number(currentSettings["Maximum Loss Limit"] || currentSettings["Max Loss Limit"] || 0);
    const rb           = Number(currentSettings["Running Balance"] || 0);

    // FIX 1: Remove the initial > 0 requirement. Max Loss Limit is the only required field.
    if (maxLossInput === 0) return;

    // FIX 2: Correctly calculate the floor for Funded accounts. 
    // If initial is $0 and limit is $2,000, the blownAt floor is -$2,000.
    const blownAt = initial - Math.abs(maxLossInput);
    const blown = rb <= blownAt;

    if (blown && !currentSettings[shownKey]) {
      queuePopup("maxLossLimitBlown", { initial, limit: blownAt, rb });
      const next = { ...currentSettings, [shownKey]: today };
      await updateSettingsFunc(userId, accountId, next);
      setLocalSettings?.(next);
     }
   } catch (e) {
     console.error("checkMaxLossLimitBlown error:", e);
   }
 }




// Shows a warning only after the daily goal has been reached AND any later pair of consecutive losses occurs.
async function checkTwoLossAfterGoal(
  trades,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings,
  queuePopupFunc
) {
  try {
    const todayISO = (typeof getTodayLocalISO === "function")
      ? getTodayLocalISO()
      : new Date().toISOString().slice(0, 10);

    const shownKey  = "TwoLossAfterGoalWarningShownDate";
    const dailyGoal = Number(currentSettings["Daily Goal"] || 0);

    PDBG("[2LAG] start", { todayISO, dailyGoal, shown: currentSettings[shownKey] });

    if (!(dailyGoal > 0)) {
      PDBG("[2LAG] exit: dailyGoal <= 0");
      return;
    }

    const isTrueLoss = (lossField, pnl) => {
      // tolerate boolean true, "true"/"yes"/1, or negative PnL
      const s = String(lossField).toLowerCase();
      return (
        lossField === true ||
        s === "true" || s === "yes" || s === "1" ||
        (Number.isFinite(Number(pnl)) && Number(pnl) < 0)
      );
    };

    const todayTrades = (trades || []).filter(t => {
      const d = t["Date"];
      if (!d) return false;
      if (typeof d === "string") return d.slice(0,10) === todayISO || d.startsWith(todayISO);
      if (d?.seconds) return new Date(d.seconds * 1000).toISOString().slice(0,10) === todayISO;
      try { return new Date(d).toISOString().slice(0,10) === todayISO; } catch { return false; }
    });

    PDBG("[2LAG] todayTrades", todayTrades.length);

    // Find the first index when cumulative PnL reaches/exceeds the daily goal
    let running = 0;
    let hitIdx  = -1;
    for (let i = 0; i < todayTrades.length; i++) {
      const r = Number(todayTrades[i]["Actual Reward"] || 0);
      running += Number.isFinite(r) ? r : 0;
      if (running >= dailyGoal) { hitIdx = i; break; }
    }
    PDBG("[2LAG] goalReachedAt", { hitIdx, runningAtHit: running });

    if (hitIdx === -1) {
      PDBG("[2LAG] exit: goal not reached today");
      return;
    }

    // Look for ANY pair of consecutive losses *after* the goal
    const after = todayTrades.slice(hitIdx + 1);
    PDBG("[2LAG] trades after goal", after.length);

    let streak = 0;
    let triggered = false;
    for (let i = 0; i < after.length; i++) {
      const t = after[i];
      const pnl = Number(t["Actual Reward"] || 0);
      const loss = isTrueLoss(t["Loss"], pnl);
      streak = loss ? streak + 1 : 0;
      PDBG("[2LAG] after-trade", { idx: i, pnl, loss, streak });
      if (streak >= 2) { triggered = true; break; }
    }

    if (!triggered) {
      PDBG("[2LAG] exit: no consecutive losses after goal");
      return;
    }

    if (currentSettings[shownKey] === todayISO) {
      PDBG("[2LAG] exit: already shown today", { shownDate: currentSettings[shownKey] });
      return;
    }

    // Enqueue the popup
    const message =
      "You have taken 2 losses after reaching your daily goal.\n\n" +
      "You could easily go on tilt if you continue to lose trying to get it back.\n\n" +
      "Consider stopping for the day to protect your gains.";
    PDBG("[2LAG] QUEUE POPUP", { id: "twoLossAfterGoalWarning" });
    queuePopupFunc("twoLossAfterGoalWarning", { message });

    // Persist the once-per-day guard
    const patch = { [shownKey]: todayISO };
    await updateSettingsFunc(userId, accountId, patch);
    setLocalSettings(prev => ({ ...prev, ...patch }));
    PDBG("[2LAG] saved flag", patch);
  } catch (err) {
    console.error("checkTwoLossAfterGoal error:", err);
  }
}





  // ---- Projection Logic (Prop) ----
 function getCompoundingProjection({
   balance,
   targetBalance,          // ← STOP when we reach this value
   riskPercent,
   riskMode,
   riskAmount,
   stopLoss,
   takeProfit,
   maxContracts,
   minContracts,
   pricePerTick,
 }) {
  let trades = [];
  let currentBalance = Number(balance) || 0;
  const tgt = Number(targetBalance) || 0;
  let tradeCount = 0;

  // Stop once the effective balance alone reaches the prop target
  while (currentBalance < tgt && tradeCount < 10000) {
        const { dollars: riskDollar } = getRiskSpec(
          { "Risk Mode": riskMode, "Risk Percentage": riskPercent, "Risk Amount": riskAmount },
          currentBalance
        );
    const perContractRisk = stopLoss * pricePerTick;

    let contracts = perContractRisk > 0 ? Math.floor(riskDollar / perContractRisk) : 0;
    contracts = Math.max(contracts, minContracts);
    contracts = Math.min(contracts, maxContracts);
    if (contracts < 1) contracts = minContracts;

    const profitPerContract = takeProfit * pricePerTick;
    const tradeProfit       = contracts > 0 ? contracts * profitPerContract : 0;
    const nextBalance       = contracts > 0 ? currentBalance + tradeProfit : currentBalance;

    trades.push({
      trade: tradeCount + 1,
      balance: currentBalance,
      riskDollar,
      contracts,
      totalRisk: contracts > 0 ? contracts * perContractRisk : perContractRisk * minContracts,
      tradeProfit,
      nextBalance,
      underfunded: perContractRisk > riskDollar,
    });

    currentBalance = nextBalance;
    tradeCount++;
    if (currentBalance >= tgt) break;
  }

  return { trades, tradeCount: trades.length };
}


// ---- Loss projection (UPDATED with Effective Balances) ----
function getLossProjection({
  balance,
  ledgerStart,
  blownAt,
  riskPercent,
  riskMode,
  riskAmount,
  stopLoss,
  maxContracts,
  minContracts,
  pricePerTick
}) {
  let trades = [];
  let currentBalance = Number(balance) || 0;
  let ledgerBalance  = Number(ledgerStart) || 0;

  const perContractRisk0 = stopLoss * pricePerTick;
  const { dollars: riskDollar0 } = getRiskSpec(
      { "Risk Mode": riskMode, "Risk Percentage": riskPercent, "Risk Amount": riskAmount },
      currentBalance
    );
  let suggestedContracts = perContractRisk0 > 0 ? Math.floor(riskDollar0 / perContractRisk0) : 0;

  const blowThreshold = Number(blownAt) || 0; // e.g. -2000

  let tradeCount = 0;
  while (
    tradeCount < 10000 &&
    ledgerBalance > blowThreshold &&
    currentBalance > 0
  ) {
    const { dollars: riskDollar } = getRiskSpec(
        { "Risk Mode": riskMode, "Risk Percentage": riskPercent, "Risk Amount": riskAmount },
        currentBalance
      );
    const perContractRisk = stopLoss * pricePerTick;

    let contracts = perContractRisk > 0 ? Math.floor(riskDollar / perContractRisk) : 0;
    contracts = Math.max(contracts, minContracts);
    contracts = Math.min(contracts, maxContracts);
    if (contracts < 1) contracts = minContracts;

    const plannedLoss = contracts > 0 ? contracts * perContractRisk : 0;
    const nextLedger  = ledgerBalance - plannedLoss;
    
    // Calculate Effective Balances (Distance to Blow Threshold)
    // e.g. Ledger = 0, Threshold = -2000 => Effective = 0 - (-2000) = 2000
    const effBefore = ledgerBalance - blowThreshold;

    // 1. Handle Clamped Loss (Blows Account)
    if (nextLedger <= blowThreshold) {
      const clampedLoss = Math.max(0, ledgerBalance - blowThreshold);
      const effAfter = 0; // Blown

      trades.push({
        trade: tradeCount + 1,
        contracts,
        totalRisk: clampedLoss,
        loss: clampedLoss,
        balanceBefore: ledgerBalance,
        balanceAfter: blowThreshold,
        effectiveBefore: effBefore,
        effectiveAfter: effAfter
      });
      
      ledgerBalance  = blowThreshold;
      currentBalance = Math.max(0, currentBalance - clampedLoss);
      tradeCount++;
      break; 
    }
    
    // 2. Regular Step
    const effAfter = nextLedger - blowThreshold;

    trades.push({
      trade: tradeCount + 1,
      contracts,
      totalRisk: plannedLoss,
      loss: plannedLoss,
      balanceBefore: ledgerBalance,
      balanceAfter: nextLedger,
      effectiveBefore: effBefore,
      effectiveAfter: effAfter
    });

    ledgerBalance  = nextLedger;
    currentBalance = currentBalance - plannedLoss;
    tradeCount++;
  }

  return { trades, tradeCount: trades.length, suggestedContracts, blownAt: blowThreshold };
}


  // ---- Field Change Handlers ----
const numericFields = [
  "Initial Balance", "Maximum Contracts", "Stop Loss", "Daily Goal",
  "Minimum Contracts", "Take Profit", "Daily Loss Limit", "Account Goal",
  "Running Balance", "Risk Percentage", "Max Trades Per Day",
  "Max Loss Trades Per Day", "Drawdown Limit %", "Risk Amount",
  "Maximum Loss Limit"
];



const handleSettingChange = (key, value) => {
  setSettings(prev => {
    const next = { ...prev };

    if (RISK_KEYS.has(key)) {
      // Keep the raw string while the user types (allows clearing)
      next[key] = value;
    } else if (numericFields?.includes?.(key)) {
      // Normal numeric behavior for all other fields
      next[key] = value === "" ? "" : Number(value);
    } else {
      next[key] = value;
    }

    if (key === "Initial Balance") {
      const n = Number(value);
      next["Running Balance"] = Number.isFinite(n) ? n : 0;
    }
    return next;
  });
  // Clear summary while editing so intermediates like "" don't recalc
  setCalculatedSummary?.(null);
};


// --- Win projection that sizes off Prop RB and stops when Prop goal is reached ---
function getWinProjection({
  propBalance,      // Prop Running Balance (compounds each step)
  propTarget,       // Prop Account Goal (fixed target)
  ledgerStart,      // Running Balance (for display only)
  riskPercent,
   riskMode,
  riskAmount,
  stopLoss,
  takeProfit,
  maxContracts,
  minContracts,
  pricePerTick,
}) {
  const trades = [];
  let currentProp = Number(propBalance) || 0;  // compounding base for sizing
  let ledger      = Number(ledgerStart) || 0;  // what users see on the account
  const tgt       = Number(propTarget)   || 0;

  if (!(currentProp > 0 && tgt > 0 && pricePerTick > 0 && stopLoss > 0 && takeProfit > 0)) {
    return { trades: [], tradeCount: 0 };
  }

  let n = 0;
  while (currentProp < tgt && n < 10000) {
    const perContractRisk = stopLoss * pricePerTick;
        const { dollars: riskDollar } = getRiskSpec(
      { "Risk Mode": riskMode, "Risk Percentage": riskPercent, "Risk Amount": riskAmount },
        currentProp
      );

    let contracts = perContractRisk > 0 ? Math.floor(riskDollar / perContractRisk) : 0;
    contracts = Math.max(contracts, minContracts);
    contracts = Math.min(contracts, maxContracts);
    if (contracts < 1) contracts = minContracts;

    const profitPerContract = takeProfit * pricePerTick;
    const tradeProfit       = contracts > 0 ? contracts * profitPerContract : 0;

    trades.push({
      trade: n + 1,
      balanceBefore: ledger,                 // ledger before trade
      propBefore: currentProp,               // prop pool before trade
      riskDollar,                                // <- add this line
      twoPctOfProp: (currentProp * riskPercent) / 100, // (keep for backwards-compat)
      contracts,
      totalRisk: contracts * perContractRisk,
      tradeProfit,
      nextBalance: ledger + tradeProfit,     // ledger after trade
      nextProp: currentProp + tradeProfit,   // prop after trade
      underfunded: perContractRisk > riskDollar,
    });

    ledger      += tradeProfit;
    currentProp += tradeProfit;
    n++;
  }

  return { trades, tradeCount: trades.length };
}

// Normalize to "YYYY-MM-DD"
 // new: always produce a LOCAL YYYY-MM-DD key, including Firestore Timestamps
 function localISODay(d) {
   const dt = new Date(d);
   const tz = dt.getTimezoneOffset() * 60000; // minutes → ms
   return new Date(dt - tz).toISOString().slice(0, 10);
 }

 function normalizeDateKey(v) {
   if (!v) return "";
   if (typeof v === "string") {
     const s = v.trim();
     if (s.includes("/")) {                 // "MM/DD/YYYY" → local ISO
       const [mm, dd, yyyy] = s.split("/");
       return `${yyyy}-${mm.padStart(2,"0")}-${dd.padStart(2,"0")}`;
     }
     return s.slice(0, 10);                 // "YYYY-MM-DD..." already fine
   }
   if (v?.seconds) return localISODay(v.seconds * 1000); // Firestore TS → local day
   try { return localISODay(v); } catch { return ""; }
 }

// --- Two Loss warning (static text) ---
const TWO_LOSS_WARNING_TEXT = `Heads up: You've had 2 losses in a row.

Evaluate: Take a break and review your recent trades. What can you learn from them before placing your next order?

Accept: Remember that losses are a part of trading. Learning to be okay with them is crucial for success.

Refocus: No strategy is 100%, but following your rules consistently is the only way to improve your chances.

You've got this. Apply what you learned in the next one!`;


const handleChecklistConfirm = async (selections) => {
  try {
    if (selections && typeof selections === "object") {
      await savePreTradeChecklist(userId, accountId, selections); // saves user-level doc now
    }
    const today = getTodayLocalISO();
    await updateUserSettings(userId, { PretradeChecklistShownDate: today }); // ✅ user-level write
    setUserPrefs(prev => ({ ...prev, PretradeChecklistShownDate: today }));  // ✅ immediate local suppress
  } catch (e) {
    console.error("[PTC] confirm error", e);
    setUserPrefs(prev => ({ ...prev, PretradeChecklistShownDate: getTodayLocalISO() })); // best-effort suppress
  } finally {
    setShowChecklist(false);
  }
};

  const handleJournalChange = (key, value) => setJournal(prev => ({ ...prev, [key]: value }));

  function getRiskSpec(s, base) {
    const raw = String(s["Risk Mode"] ?? "%").trim().toLowerCase();
    const mode = (raw === "$" || raw === "dollar") ? "dollar" : "percent";
    const pct  = Number(s["Risk Percentage"] ?? 0);
    const amt  = Number(s["Risk Amount"] ?? s["Risk Dollar Amount"] ?? 0);
    const dollars = mode === "dollar"
      ? Math.max(0, amt)
      : Math.max(0, base) * (pct / 100);
    const percent = base > 0 ? (dollars / base) * 100 : 0;
    return { mode, pct, amt, dollars, percent };
  }



// ---- Summary Calculation ----
  // Full, self-contained version
const handleCalculate = async (manual = false, settingsOverride = null) => {

    if (manual) setManualCalculate(true);
    setSavingSettings(true);

    try {
      // Work from freshest inputs
      const s = settingsOverride ? { ...settingsOverride } : { ...settings };

      // ---- Base inputs ----
      const initialBalance = Number(s["Initial Balance"] || 0);
      const accountGoal    = Number(s["Account Goal"] || 0);
      const maxLoss        = Number(s["Maximum Loss Limit"] || 0);
      const riskPercent    = Number(s["Risk Percentage"] || 0);

      // Seed Running Balance if empty
      if (!Number(s["Running Balance"] || 0) && initialBalance > 0) {
        s["Running Balance"] = initialBalance;
      }

      // ---- Prop values (ALWAYS derived from RB/IB/AG + |MLL|) ----
      const lossAbs = Math.abs(maxLoss);
      s["Prop Initial Balance"] = initialBalance + lossAbs;
      s["Prop Account Goal"]    = accountGoal    + lossAbs;
      s["Prop Running Balance"] = Number(s["Running Balance"] || 0) + lossAbs;
      
      // Keep Remaining Profit Amount in sync with s (read–only display field)
      s["Remaining Profit Amount"] = Math.max(
        0,
        accountGoal - Number(s["Running Balance"] ?? 0)
      );

      // ✅ FIX: Strip ephemeral flags to prevent the background calculator from wiping the DB
      const currentDbSettings = await getSettings(userId, accountId);
      const safePayload = { ...s };
      
      for (const k of Object.keys(currentDbSettings || {})) {
        if (k.endsWith("ShownDate") && currentDbSettings[k]) {
          safePayload[k] = currentDbSettings[k];
        }
      }

      // Persist derived settings first, then mirror locally
      await updateSettings(userId, accountId, safePayload);
      setSettings(safePayload);

      // ---- Sizing inputs (use s, not state) ----
      const product       = s["Product"];
      const pricePerTick  = getPricePerTick(product);
      const tickSize      = getTickSizeForProduct(product) || 0.25;
      const entryPrice    = Number(s["Entry Price"] || 0);
      
      let stopLoss      = Number(s["Stop Loss"] || 0);
      let takeProfit    = Number(s["Take Profit"] || 0);

      let stopLossStr = "";
      let takeProfitStr = "";

      // --- FIX: Convert Price to Ticks and build strings ---
      if (s["Stop Loss Mode"] === "Price" && entryPrice > 0 && stopLoss > 0) {
          const calcTicks = Math.round(Math.abs(entryPrice - stopLoss) / tickSize);
          stopLossStr = `${formatCurrency(stopLoss)} (${calcTicks} ticks)`;
          stopLoss = calcTicks;
      } else if (stopLoss > 0) {
          if (entryPrice > 0) {
              const calcPrice = entryPrice - (stopLoss * tickSize);
              stopLossStr = `${formatCurrency(calcPrice)} (${stopLoss} ticks)`;
          } else {
              stopLossStr = `${stopLoss} ticks`;
          }
      }

      if (s["Take Profit Mode"] === "Price" && entryPrice > 0 && takeProfit > 0) {
          const calcTicks = Math.round(Math.abs(takeProfit - entryPrice) / tickSize);
          takeProfitStr = `${formatCurrency(takeProfit)} (${calcTicks} ticks)`;
          takeProfit = calcTicks;
      } else if (takeProfit > 0) {
          if (entryPrice > 0) {
              const calcPrice = entryPrice + (takeProfit * tickSize);
              takeProfitStr = `${formatCurrency(calcPrice)} (${takeProfit} ticks)`;
          } else {
              takeProfitStr = `${takeProfit} ticks`;
          }
      }

      const maxContracts  = Math.max(1, Number(s["Maximum Contracts"] || 1));
      const minContracts  = Math.max(1, Number(s["Minimum Contracts"] || 1));

      // Use Prop Running Balance for % of balance and all projections (per spec)
      const baseForRisk      = Number(s["Prop Running Balance"] || 0);
      const riskMode         = String(s["Risk Mode"] || "Percent");
      const riskAmount       = Number(s["Risk Amount"] || 0);
      const { dollars: riskDollars, percent: riskPctFromBase } = getRiskSpec(
       { "Risk Mode": riskMode, "Risk Percentage": riskPercent, "Risk Amount": riskAmount },
       baseForRisk
       );
      const percentOfBalance = riskDollars; // keep downstream expectations: always dollars

      // ---- Contract sizing ----
      const perContractRisk = stopLoss * pricePerTick;

      let contracts =
        perContractRisk > 0 ? Math.floor(percentOfBalance / perContractRisk) : 0;

      // Clamp between min/max and re-check underfunding
      contracts = Math.max(minContracts, Math.min(maxContracts, contracts));
      if (contracts * perContractRisk > percentOfBalance) {
        contracts = Math.floor(percentOfBalance / perContractRisk);
        contracts = Math.max(minContracts, contracts);
      }
      if (contracts * perContractRisk > percentOfBalance) {
        contracts = 0; // still too large
      }

      // ---- Summary numbers ----
            const riskRewardRatio =
              takeProfit && stopLoss
                ? `${fmtRatio(takeProfit / stopLoss)}:1`
                : "";
      const totalRisk       = (contracts || minContracts) * perContractRisk;
      const targetReward    = (contracts || minContracts) * pricePerTick * takeProfit;
      const maxStopLossSize = pricePerTick > 0
        ? Math.floor(percentOfBalance / pricePerTick)
        : 0;
          // New: ticks allowed when trading your Minimum Contracts
          const maxStopLossSizeForMin = pricePerTick > 0
            ? Math.floor(percentOfBalance / (pricePerTick * Math.max(1, minContracts)))
            : 0;

      setCalculatedSummary({
                     pricePerTick,
                     riskRewardRatio,
                     // new: ≈ % when in $ mode
                     percentOfBalance: riskDollars,
                     riskPercent,
                     riskPctFromBase,     // used for “≈ % of balance” display
                     stopLoss,
                     takeProfit,
                     stopLossStr,
                     takeProfitStr,
                     contracts,
                     minContracts,
                     totalRisk,
                     targetReward,
                     maxStopLossSize,
                     maxStopLossSizeForMin,
                   });


      // ---- Projections (WIN) ----
      const ledgerStart = Number(s["Running Balance"] || 0);
      const propStart   = Number(s["Prop Running Balance"] || 0);
      const propTarget  = Number(s["Prop Account Goal"] || 0);

      // Only require sane inputs + propTarget above propStart
      const canWinProject =
        pricePerTick > 0 && stopLoss > 0 && takeProfit > 0 && propTarget > propStart;



      const winProj = canWinProject
        ? getWinProjection({
            propBalance: propStart,  // sizing/compounding base
            propTarget,              // STOP when prop goal reached
            ledgerStart,             // shown in table (Balance / End Balance)
            riskPercent,
             riskMode,
            riskAmount,
            stopLoss,
            takeProfit,
            maxContracts,
            minContracts,
            pricePerTick,
          })
        : { trades: [], tradeCount: 0 };

      setWinProjection(winProj);




      // Persist “Number of Trades to Goal”
      await updateSettings(userId, accountId, {
        ...s,
        "Number of Trades to Goal": winProj.tradeCount,
      });
      setSettings(prev => ({
        ...prev,
        "Number of Trades to Goal": winProj.tradeCount,
      }));

      // Loss projection — pass a superset of args (compatible with both signatures)
// ---- Projections ----
const canLossProject = pricePerTick > 0 && stopLoss > 0 && baseForRisk > 0;

// End target for loss projection is the Maximum Loss Limit itself (can be negative or 0).
const blownAt = Number((s["Maximum Loss Limit"] ?? s["Max Loss Limit"]) ?? 0);

//const ledgerStart = Number(s["Running Balance"] || 0);

const lossProj = canLossProject
  ? getLossProjection({
      balance: baseForRisk,     // prop-effective pool for % risk sizing
      ledgerStart,              // actual starting ledger balance
      blownAt,                  // blown-at threshold as a BALANCE
      riskPercent,
      riskMode: (s["Risk Mode"] || "Percent"),
      riskAmount: Number(s["Risk Amount"] || 0),
      stopLoss,
      maxContracts,
      minContracts,
      pricePerTick
    })
  : { trades: [], tradeCount: 0, suggestedContracts: contracts };

   setLossProjection(lossProj);
   // Once projections are computed, we can show the "Inactive" pill if blown
    setHasCalculated(true);


            // --- Daily goal / loss-limit progress (amounts, not %) ---
            try {
              const allRows = await fetchJournalRows(userId, accountId);
              const todayISO = getTodayLocalISO();
      
              // Helper: treat "YYYY-MM-DD", "MM/DD/YYYY", or Firestore TS as "today"
              const isTodayRow = (d) => {
                if (!d) return false;
                if (typeof d === "string") {
                  if (d.includes("-")) return d.startsWith(todayISO);
                  if (d.includes("/")) {
                    const [mm, dd, yyyy] = d.split("/");
                    const iso = `${yyyy}-${String(mm).padStart(2,"0")}-${String(dd).padStart(2,"0")}`;
                    return iso === todayISO;
                  }
                  const dt = new Date(d);
                  return !isNaN(dt) && dt.toISOString().slice(0,10) === todayISO;
                }
                if (d?.seconds) return new Date(d.seconds * 1000).toISOString().slice(0,10) === todayISO;
              try { return new Date(d).toISOString().slice(0,10) === todayISO; } catch (err) { return false; }
              };
      
              // Today's rows
              const todayRows = allRows.filter(r => isTodayRow(r["Date"]));
      
              // — Progress bars —
              const todayPnL        = todayRows.reduce((sum, r) => sum + (Number(r["Actual Reward"]) || 0), 0);
              const dailyGoal       = Number(s["Daily Goal"] || 0);
              const dailyLossLimit  = Number(s["Daily Loss Limit"] || 0);
              const positivePnL     = Math.max(0, todayPnL);
              const lossAbs         = Math.max(0, -todayPnL);
              const clampPct        = (n) => (isFinite(n) ? Math.max(0, Math.min(100, n)) : 0);
              const goalPct         = dailyGoal > 0 ? clampPct((positivePnL / dailyGoal) * 100) : 0;
              const lossPct         = dailyLossLimit > 0 ? clampPct((lossAbs / dailyLossLimit) * 100) : 0;
              
              setDailyGoalProg({ done: positivePnL, target: dailyGoal, pct: goalPct });
              setDailyLossProg({ lost: lossAbs, limit: dailyLossLimit, pct: lossPct });
            } catch (e) {
              setDailyGoalProg({ done: 0, target: Number(s["Daily Goal"] || 0), pct: 0 });
              setDailyLossProg({ lost: 0, limit: Number(s["Daily Loss Limit"] || 0), pct: 0 });
            }


      // ---- Progress (Prop) ----
             const ib  = Number(s["Initial Balance"] || 0);
             const mll = Math.abs(Number(s["Maximum Loss Limit"] || 0));
             const ag  = Number(s["Account Goal"] || 0);
            
             const pInit = Number(s["Prop Initial Balance"]  ?? (ib + mll));
             const pGoal = Number(s["Prop Account Goal"]     ?? (ag + mll));
             const pRun  = Number(s["Prop Running Balance"]  ?? (Number(s["Running Balance"] || 0) + mll));
      const denom = pGoal - pInit;
      const progressPct =
        denom > 0 ? Math.min(100, Math.max(0, ((pRun - pInit) / denom) * 100)) : 0;
      setProgress(progressPct);


      const effectiveBalance =
        Number(
          settings["Prop Running Balance"] ??
          (Number(settings["Running Balance"] || 0) + Math.abs(Number(settings["Maximum Loss Limit"] || 0)))
        );

// Use at least 1 contract when showing "what would the risk be" numbers
const minSize = Math.max(1, Number(settings["Minimum Contracts"] || minContracts || 1));
const zeroRiskDollars = minSize * (Number(stopLoss) * Number(pricePerTick));
const zeroRiskPct = effectiveBalance > 0
  ? Number(((zeroRiskDollars / effectiveBalance) * 100).toFixed(2))
  : 0;

// Feed the popup
setLastTotalRisk(zeroRiskDollars);
setLastActualRiskPercent(zeroRiskPct);

      // Only show zero-contracts popup on manual runs
      if (manual) setShowZeroContractsPopup(contracts === 0);
    } catch (err) {
      console.error("Settings save failed:", err);
    } finally {
      if (manual) setManualCalculate(false);
      setSavingSettings(false);
    }
  };

  const toBoolWinLoss = (v) => {
    if (v === true) return true;
    const s = String(v || "").trim().toLowerCase();
    return s === "true" || s === "yes" || s === "win";
  };




  // ---- Settings Load Effect ----
useEffect(() => {
  if (!userId || !accountId) return;
  const load = async () => {
    try {
      const raw = (await getSettings(userId, accountId)) || {};
      setSettings({ ...defaultSettings, ...raw });
      if (initialSettings === null) {
        setInitialSettings({ ...defaultSettings, ...raw });
      }
    } catch (err) {
      console.error("Error loading settings:", err);
    }
  };
  load();
}, [userId, accountId]);


//auto calculate on page load
useEffect(() => {
  if (autoCalculatedOnce || savingSettings) return;
  if (!settings?.Product) return;

  setAutoCalculatedOnce(true);
  handleCalculate(false); // silent: no popups on load
}, [settings?.Product, autoCalculatedOnce, savingSettings]);

const computedPropInitial =
  Number(settings["Initial Balance"] || 0) + Math.abs(Number(settings["Maximum Loss Limit"] || 0));
const computedPropAccountGoal =
  Number(settings["Account Goal"] || 0) + Math.abs(Number(settings["Maximum Loss Limit"] || 0));

// display saved PRB if present, else seed with computed initial
const displayPropRunningBalance =
  typeof settings["Prop Running Balance"] !== "undefined"
    ? Number(settings["Prop Running Balance"])
    : computedPropInitial;


    useEffect(() => {
  if (!settings) return;

  const dailyGoal      = Number(settings["Daily Goal"] || 0);
  const dailyLossLimit = Number(settings["Daily Loss Limit"] || 0);

  setDailyGoalProg(prev => ({
    ...prev,
    // keep the current "done" value; just ensure the target is visible immediately
    target: dailyGoal,
    pct: dailyGoal > 0 ? Math.min(100, Math.max(0, (prev.done / dailyGoal) * 100)) : 0
  }));

  setDailyLossProg(prev => ({
    ...prev,
    // keep the current "lost" value; just ensure the limit is visible immediately
    limit: dailyLossLimit,
    pct: dailyLossLimit > 0 ? Math.min(100, Math.max(0, (prev.lost / dailyLossLimit) * 100)) : 0
  }));
// depend only on the two fields we care about
}, [settings && settings["Daily Goal"], settings && settings["Daily Loss Limit"]]);


useEffect(() => {
  // Only check after settings are loaded
  if (!settings || !userId || !accountId) return;

  if (!popupsAllowedRef.current) return;
  const running = Number(settings["Running Balance"] || 0);
  const goal = Number(settings["Account Goal"] || 0);
  const today = getTodayLocalISO();

  if (goal > 0 && running >= goal && settings.AccountGoalReachedShownDate !== today) {
    queuePopup('accountGoalReached');
    const newSettings = { ...settings, AccountGoalReachedShownDate: today };
    updateSettings(userId, accountId, newSettings);
    setSettings(newSettings);
  }
  // Optionally: Reset on goal change or user/account change
  // eslint-disable-next-line
}, [settings, userId, accountId]);


useEffect(() => {
  if (
    calculatedSummary &&
    calculatedSummary.contracts !== undefined &&
    !userEditingSize
  ) {
    setJournal(prev => ({
      ...prev,
      Size: calculatedSummary.contracts
    }));
  }
}, [calculatedSummary]);


const openedForTodayRef = useRef(false);

// --- Auto-sync Entry/Exit Prices to Journal ---
  useEffect(() => {
    const plannedEntry = settings["Entry Price"] !== undefined ? settings["Entry Price"] : "";
    const tpVal = settings["Take Profit"] || "";
    
    let syncEntryPrice = "";
    let syncExitPrice = "";

    // Only auto-populate the journal if the user is planning the trade by exact Price
    if (settings["Stop Loss Mode"] === "Price" || settings["Take Profit Mode"] === "Price") {
        syncEntryPrice = plannedEntry;
    }
    
    if (settings["Take Profit Mode"] === "Price") {
        syncExitPrice = tpVal;
    }

    setJournal(prev => {
        const next = { ...prev };
        let changed = false;
        
        // Sync the Entry Price (or clear it if switched to Ticks/Pips/Points)
        if (next["Entry Price"] !== syncEntryPrice) {
            next["Entry Price"] = syncEntryPrice;
            changed = true;
        }
        
        // Sync the Exit Price (or clear it if switched to Ticks/Pips/Points)
        if (next["Exit Price"] !== syncExitPrice) {
            next["Exit Price"] = syncExitPrice;
            changed = true;
        }
        
        return changed ? next : prev;
    });
  }, [
    settings["Entry Price"], 
    settings["Take Profit"], 
    settings["Take Profit Mode"], 
    settings["Stop Loss Mode"]
  ]);

useEffect(() => {
  if (!userId) return;

  const today   = getTodayLocalISO();
  const enabled = ["yes","true","on","1"].includes(String(userPrefs?.["Pretrade Checklist"] ?? "").toLowerCase());
  const shown   = normalizeDateKey(
    userPrefs?.["PretradeChecklistShownDate"] ?? userPrefs?.["pretradeChecklistLastShown"] ?? ""
  );

  // If it's a new day relative to the last stamp, reset the gate so we can show again.
  if (shown !== today) {
    openedForTodayRef.current = false;
  } else {
    // Already stamped for today → make sure it's closed and mark handled.
    setShowChecklist(false);
    openedForTodayRef.current = true;
    return;
  }

  if (enabled && !openedForTodayRef.current) {
    setShowChecklist(true);
    openedForTodayRef.current = true; // prevent re-open if userPrefs reflows
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [
  userId,
  userPrefs?.["Pretrade Checklist"],
  userPrefs?.["PretradeChecklistShownDate"],  // canonical (user-level)
  userPrefs?.["pretradeChecklistLastShown"]   // legacy (read-only fallback)
]);




useEffect(() => {
  // If the user hasn't manually entered a value, set to suggested contracts
  if (
    calculatedSummary &&
    calculatedSummary.contracts !== undefined &&
    (journal["Size"] === undefined || journal["Size"] === "")
  ) {
    setJournal((prev) => ({
      ...prev,
      "Size": calculatedSummary.contracts
    }));
  }
  // eslint-disable-next-line
}, [calculatedSummary]);

// ---- Daily Goal Popup Effect ----
useEffect(() => {
  // Only check after settings are loaded
  if (!settings || !userId || !accountId) return;

  if (!popupsAllowedRef.current) return;

async function checkDailyGoalPopupOnLoad() {
  try {
    const freshSettings = await getSettings(userId, accountId);
    await checkDailyProfitTarget([], Number(freshSettings["Daily Goal"] || 0), freshSettings, updateSettings, userId, accountId, setSettings);
  } catch (err) {
    console.error("Failed to check daily goal popup on load:", err);
  }
}

  checkDailyGoalPopupOnLoad();
}, [settings, userId, accountId]); // Added currentPopup to dependencies


useEffect(() => {
  const ib  = Number(settings["Initial Balance"] || 0);
  const mll = Number(settings["Maximum Loss Limit"] || 0);
  const ag  = Number(settings["Account Goal"] || 0);

  const pInit = Number(settings["Prop Initial Balance"]  ?? Math.max(0, ib - mll));
  const pGoal = Number(settings["Prop Account Goal"]     ?? Math.max(0, ag - mll));
  const pRun  = Number(settings["Prop Running Balance"]  ?? pInit);

  let pct = 0;
  const denom = pGoal - pInit;
  if (denom > 0) {
    pct = ((pRun - pInit) / denom) * 100;
  }
  setProgress(Math.max(0, Math.min(100, pct)));
}, [
  settings["Prop Initial Balance"],
  settings["Prop Running Balance"],
  settings["Prop Account Goal"],
  settings["Initial Balance"],
  settings["Maximum Loss Limit"],
  settings["Account Goal"],
]);


// Base balance for Win Projection = current Running Balance
const baseBalance = Number(settings["Running Balance"] ?? 0);
    const calcBtnRef = useRef(null);

    // --- Derived values for Loss Projection (live; updates as you edit) ---
    const rbStart   = Number(settings["Running Balance"] || 0);
    const mllTarget = Number(settings["Maximum Loss Limit"] || 0); // can be negative or 0
    const prbStart  = rbStart + Math.abs(mllTarget);
    const prbEnd    = mllTarget + Math.abs(mllTarget); // = 0 when mllTarget <= 0
    const lossProjLive = canComputeLossProj
      ? getLossProjection({
          balance: Math.max(0, prbStart),            // compounding base (% risk)
          ledgerStart: rbStart,                      // display base (account ledger)
          blownAt: mllTarget,                        // end at the limit value
          riskPercent,
          stopLoss,
          maxContracts: Math.max(1, Number(settings["Maximum Contracts"] || 1)),
          minContracts: Math.max(1, Number(settings["Minimum Contracts"] || 1)),
          pricePerTick
        })
      : { trades: [], tradeCount: 0, suggestedContracts: 0, blownAt: mllTarget };


// ---- Save Trade Handler (single, complete) ----
const handleSaveTrade = async () => {
  // --- basic validation
  const errors = [];
  if (!calculatedSummary) errors.push("- Click the Calculate button before saving.");
  if (!journal["Actual Balance"]) errors.push("- Actual Balance");
  if (!journal["Date"]) errors.push("- Date");
  if (!settings["Product"]) errors.push("- Product");
  if (!journal["Did I follow my Rules?"]) errors.push("- Did I follow my Rules?");
  if (!journal["Win"] && !journal["Loss"]) errors.push("- Win/Loss");
    // If user asked to log a lesson, require some text
    if (journal.LogLesson && !String(journal["Lesson"] || "").trim()) {
      errors.push("- Lesson (enter text or uncheck 'Log this as a Lesson')");
    }

  const effectiveContracts =
    (journal["Size"] !== undefined && journal["Size"] !== "")
      ? Number(journal["Size"])
      : Number(calculatedSummary?.contracts ?? 0);

  if (!Number.isFinite(effectiveContracts) || effectiveContracts <= 0) {
    errors.push("- # of Contracts");
  }
  if (errors.length > 0) {
    setMissingFields(errors);
    return;
  }

  // Show "Trade Saved" FIRST; user will dismiss it
  setSavingTrade(true);
  setCurrentPopup({ id: "tradeSaved" }); // do NOT auto-close later

  try {
    // ---------- PREP ----------
    const tradeNum = await getNextTradeNumber();
    popupsAllowedRef.current = true; // allow popups during this save flow
    
    // Local helpers (scoped; no linter leaks)
    const todayISO = getTodayLocalISO();
    const isTodayRow = (d) => {
      if (!d) return false;
      if (typeof d === "string") {
        if (d.includes("-")) return d.startsWith(todayISO); // "YYYY-MM-DD..."
        if (d.includes("/")) {
          const [mm, dd, yyyy] = d.split("/");
          const iso = `${yyyy}-${String(mm).padStart(2,"0")}-${String(dd).padStart(2,"0")}`;
          return iso === todayISO;
        }
        const dt = new Date(d);
        return !isNaN(dt) && dt.toISOString().slice(0,10) === todayISO;
      }
      if (d?.seconds) return new Date(d.seconds * 1000).toISOString().slice(0,10) === todayISO;
      try { return new Date(d).toISOString().slice(0,10) === todayISO; } catch { return false; }
    };
    const to24 = (t) => (typeof to24HourTime === "function" ? to24HourTime(t) : t || "");
    const toSecs = (t) => (typeof timeStringToSeconds === "function" ? timeStringToSeconds(t) : 0);
    const toBool = (v) => {
      const s = String(v ?? "").toLowerCase();
      return s === "true" || s === "yes" || s === "win" || s === "1";
    };

// Work on a mutable snapshot of current settings
    let fresh = { ...settings };

    const prevRunning      = Number(fresh["Running Balance"] || 0);
    const product          = String(fresh["Product"] || "");
    const pricePerTick     = Number((typeof getPricePerTick === "function" ? getPricePerTick(product) : 0) || 0);
    const tickSize         = getTickSizeForProduct(product) || 0.25;
    const entryPrice       = Number(fresh["Entry Price"] || 0);

    let stopLossTicks    = Number(fresh["Stop Loss"] || 0);
    let takeProfitTicks  = Number(fresh["Take Profit"] || 0);

    // --- NEW: Convert Price to Ticks before saving to Journal ---
    if (fresh["Stop Loss Mode"] === "Price" && entryPrice > 0 && stopLossTicks > 0) {
        stopLossTicks = Math.round(Math.abs(entryPrice - stopLossTicks) / tickSize);
    }
    if (fresh["Take Profit Mode"] === "Price" && entryPrice > 0 && takeProfitTicks > 0) {
        takeProfitTicks = Math.round(Math.abs(takeProfitTicks - entryPrice) / tickSize);
    }

    const minContracts     = Math.max(1, Number(fresh["Minimum Contracts"] || 1));
    const riskPercent      = Number(fresh["Risk Percentage"] || 0);
    const actualBalanceVal = Number(journal["Actual Balance"] || prevRunning || 0);

    // Robust "prop pool" baseline
    const initialBalance   = Number(fresh["Initial Balance"] || 0);
    const maxLossLimit     = Number(fresh["Maximum Loss Limit"] || fresh["Max Loss Limit"] || 0);
    const accountGoal      = Number(fresh["Account Goal"] || 0);
    const propInitial      = Math.max(0, initialBalance - maxLossLimit);
    const propGoal         = Math.max(0, accountGoal - maxLossLimit);
    const prevPropRB       = Number(fresh["Prop Running Balance"] || 0); 
    const safePrevPropRB   = (Number.isFinite(prevPropRB) && prevPropRB > 0) ? prevPropRB : propInitial;

    // This trade's PnL from Actual Balance delta (fallback 0)
    const actualBalanceNum     = Number(journal["Actual Balance"] || 0);
    const computedActualReward = (Number.isFinite(actualBalanceNum) && Number.isFinite(prevRunning))
      ? (actualBalanceNum - prevRunning)
      : 0;

    // ----- Derive ticks: prefer user-entered, otherwise from Entry/Exit Price -----
    const isDidNotTake =
      String(journal["Position"] || "").trim().toLowerCase() === "did not take";

    const ticksRaw = journal["Ticks"];
    const ticksFromForm =
      ticksRaw === "" || ticksRaw == null ? null : Number(ticksRaw);
    let effectiveTicks =
      Number.isFinite(ticksFromForm) ? ticksFromForm : null;

    if (effectiveTicks == null && !isDidNotTake) {
      const ticksFromPriceAbs = computeTicksFromPricesAbs(
        product,
        journal["Entry Price"],
        journal["Exit Price"]
      );

      if (ticksFromPriceAbs != null && Number.isFinite(ticksFromPriceAbs)) {
        const isLoss = !!journal["Loss"];
        const signed = isLoss ? -ticksFromPriceAbs : ticksFromPriceAbs;
        effectiveTicks = signed;
      }
    }


    // ---------- BUILD TRADE OBJECT ----------
    const tradeObj = {};
    SHEET_HEADERS.forEach((header) => {
      switch (header) {
        case "Trade #":
          tradeObj[header] = tradeNum;
          break;

        case "tradeNum":
          tradeObj["tradeNum"] = tradeNum;
          break;

        case "Product":
          tradeObj[header] = product;
          break;

        case "Running Balance":
          tradeObj[header] = actualBalanceVal;
          break;

        case "Price Per Tick":
          tradeObj[header] = pricePerTick;
          break;

                case "Reward to Risk Ratio": {
                  if (takeProfitTicks && stopLossTicks) {
                    const pretty = fmtRatio(takeProfitTicks / stopLossTicks);
                    tradeObj[header] = `${pretty}:1`;
                  } else {
                    tradeObj[header] = "";
                  }
                  break;
                }

        case "Percent of Balance": {
          const base = Number(fresh["Running Balance"] || 0) + absMLL(fresh["Maximum Loss Limit"]);
          tradeObj[header] = (base * riskPercent) / 100;
          break;
        }

        case "Stop Loss_Take Profit":
          tradeObj[header] = (stopLossTicks && takeProfitTicks) ? `${stopLossTicks}/${takeProfitTicks}` : "";
          break;

        case "Suggested Max # of Contracts":
          tradeObj[header] = calculatedSummary?.contracts ?? "";
          break;

        case "Max Stop Loss Size for 1 Contract(s) (Ticks)": {
          const percentOfBalance = Number(fresh["Running Balance"] || 0) * (riskPercent / 100);
          tradeObj[header] = pricePerTick > 0 ? Math.floor(percentOfBalance / pricePerTick) : 0;
          break;
        }

        case "Total Risk": {
          const contracts = Number(journal["Size"] || minContracts);
          tradeObj[header] = contracts * stopLossTicks * pricePerTick;
          break;
        }

        case "Size":
          tradeObj[header] = journal["Size"];
          break;

        case "Tcks":
        case "Ticks":
          tradeObj[header] =
            effectiveTicks == null ? "" : Number(effectiveTicks);
          break;

        case "Total Target Reward": {
          const contracts = Number(journal["Size"] || minContracts);
          tradeObj[header] = contracts * takeProfitTicks * pricePerTick;
          break;
        }

        case "Actual Reward":
          tradeObj[header] = computedActualReward;
          break;

        case "Duration (h:mm)": {
          const et = journal["Entry Time"] || "";
          const xt = journal["Exit Time"] || "";
          if (et && xt) {
            let start = toSecs(et);
            let end   = toSecs(xt);
            let diff  = end - start;
            if (diff < 0) diff += 24 * 3600;
            const h   = Math.floor(diff / 3600);
            const m   = Math.floor((diff % 3600) / 60);
            tradeObj[header] = `${String(h).padStart(1,"0")}:${String(m).padStart(2,"0")}`;
          } else {
            tradeObj[header] = "";
          }
          break;
        }

        case "Entry Time":
          tradeObj[header] = journal["Entry Time"] ? to24(journal["Entry Time"]) : "";
          break;

        case "Exit Time":
          tradeObj[header] = journal["Exit Time"] ? to24(journal["Exit Time"]) : "";
          break;

        case "Actual Percent Growth": {
          const pct = safePrevPropRB > 0 ? (computedActualReward / safePrevPropRB) * 100 : 0;
          tradeObj[header] = `${pct.toFixed(2)}%`;
          break;
        }

        case "Win":
          tradeObj[header] = toBool(journal["Win"]);
          break;

        case "Loss": {
          const inputLoss = journal["Loss"];
          // Keep mutually exclusive; infer if not explicitly set
          tradeObj[header] = inputLoss != null ? toBool(inputLoss) : !toBool(journal["Win"]);
          break;
        }

        case "Actual Risk to Reward": {
          const contracts    = Number(journal["Size"] || minContracts);
          const totalRisk    = contracts * stopLossTicks * pricePerTick;
          const actualReward = (tradeObj["Actual Reward"] !== undefined && tradeObj["Actual Reward"] !== "")
            ? Number(tradeObj["Actual Reward"])
            : (actualBalanceVal && prevRunning ? actualBalanceVal - prevRunning : 0);
          let rtr = "";
          if (totalRisk > 0 && isFinite(actualReward)) {
                        // Store as <rewardMultiple>:1 (e.g., 3:1). Negative multiples are kept for losses (e.g., -0.5:1).
                        const multiple = actualReward / totalRisk; // can be negative
                        // round to 2 dp, normalize -0.00 -> 0
                        const rounded = Number.isInteger(multiple) ? multiple : Number(multiple.toFixed(2));
                        const cleaned = String(rounded)
                          .replace(/^-0(\.0+)?$/, "0")
                          .replace(/\.?0+$/, "");
                        rtr = `${cleaned}:1`;
                       }
                       tradeObj[header] = rtr;
                       break;
                     }

        case "Lesson":
          tradeObj[header] = journal["Lesson"] || "";
          break;

        default:
          tradeObj[header] = journal[header] ?? "";
      }
    });

    tradeObj["tradeNum"]  = tradeNum;
    tradeObj["Timeframe"] = journal["Timeframe"] || tradeObj["Timeframe"] || "";
    tradeObj["Potential"] = journal["Potential"] || "";
    tradeObj["tickSize"] = tickSize;
    tradeObj["assetClass"] = "FUTURES";
    tradeObj["Needs Review"] = !!journal["Needs Review"];
    tradeObj["Emotional Notes"] = journal["Emotional Notes"] || "";
    tradeObj["Target Reached"] = !!journal["Target Reached"];

    // ---------- WRITE TRADE ----------
    await appendTrade(userId, accountId, tradeObj);
    await checkFirstTradeAnalysis(tradeObj, userId, queuePopup);

         // ----- OPTIONAL LESSON LOG -----
         if (journal.LogLesson && String(journal["Lesson"] || "").trim()) {
           const lessonDoc = {
             tradeNum,                                 // link back to the trade
             Date: normalizeDateKey(journal["Date"]) || getTodayLocalISO(),
             Lesson: String(journal["Lesson"]).trim(), // <-- the actual text
             Product: tradeObj["Product"],
             Timeframe: tradeObj["Timeframe"],
             "Did I follow my Rules?": tradeObj["Did I follow my Rules?"],
             Win: !!tradeObj["Win"],
             Loss: !!tradeObj["Loss"],
             "Entry Strategy": tradeObj["Entry Strategy"],
             "Exit Strategy": tradeObj["Exit Strategy"],
             Notes: tradeObj["Notes"],
             Links_Screenshots: tradeObj["Links_Screenshots"],
             Position: tradeObj["Position"],
             Session: tradeObj["Session"],
           };
           try {
             const textOnly = (journal.Lesson || "").trim();
             await appendLesson(textOnly);   // ✅ single argument only
           } catch (e) {
             console.error("appendLesson failed:", e);
           }
         }

         // === Copy Trade (if this account is a Futures leader) ===
         try {
           // use your already-computed delta for the trade (or recompute from balances)
           const delta = Number(tradeObj?.["Actual Reward"] ?? 0) ||
                         (Number(journal["Actual Balance"] || 0) - Number(settings["Running Balance"] || 0));
           const me = await getSettings(userId, accountId);
           if (me?.CopyTrade?.role === "leader" || me?.settings?.CopyTrade?.role === "leader") {
             const { copyTradeToFollowers } = await import("../services/CopyTradeService");
             await copyTradeToFollowers({
               userId,
               leaderAccountId: accountId,
               leaderTrade: tradeObj,     // or your `toSave` object in that file
               delta,
               accountType: "futures"
             });
           }
         } catch (e) {
           console.error("[CopyTrade] futures prop copy failed", e);
         }


    // Celebration / learning popups that only need this one trade
    await checkWin10NoRulesPopup(
      tradeObj, fresh, updateSettings, userId, accountId,
      (next) => { fresh = next; setSettings(next); }, queuePopup
    );
    await checkWin10WithRulesPopup(
      tradeObj, fresh, updateSettings, userId, accountId,
      (next) => { fresh = next; setSettings(next); }, queuePopup
    );

// ---------- UPDATE BALANCES ----------
    const rewardNum = Number(tradeObj["Actual Reward"] || 0);
    const lossAbs = Math.abs(Number(fresh["Maximum Loss Limit"] || fresh["Max Loss Limit"] || 0));
    
    // Explicitly define the previous Prop Running Balance for the SOD calculation
    const currentPrevPropRB = Number(prevRunning || 0) + lossAbs;

    const newPropInit = Number(fresh["Initial Balance"] || 0) + lossAbs;
    const newPropGoal = Number(fresh["Account Goal"] || 0) + lossAbs;
    const newPropRB   = Number(actualBalanceVal) + lossAbs;
    
    fresh = {
      ...fresh,
      "Running Balance": actualBalanceVal,
      "Prop Initial Balance": newPropInit,
      "Prop Account Goal":    newPropGoal,
      "Prop Running Balance": newPropRB,
      "Remaining Profit Amount": Math.max(0, accountGoal - actualBalanceVal),
    };
    
    // Seed SOD (once per day) for daily drawdown checks
    if (fresh.PropSODDate !== todayISO || !Number(fresh.PropSODBase || 0)) {
      fresh.PropSODDate = todayISO;
      // Use the variable we just defined to fix the undefined error
      fresh.PropSODBase = currentPrevPropRB > 0 ? currentPrevPropRB : newPropInit;
    }

    await updateSettings(userId, accountId, fresh);
    setSettings(fresh);

    // ---------- FETCH TRADES ONCE ----------
    const fetchedRows = await fetchJournalRows(userId, accountId);
    const allRows = [...fetchedRows].sort((a, b) => 
      (Number(a.tradeNum ?? a["Trade #"] ?? 0) - Number(b.tradeNum ?? b["Trade #"] ?? 0))
    );

    // ---------- DAILY LOSS LIMIT ----------

        const today =
          (typeof getTodayLocalISO === "function")
            ? getTodayLocalISO()
            : new Date().toISOString().slice(0, 10);
        let freshSettings = await getSettings(userId, accountId);
    {
      const todayRows = allRows.filter(r => isTodayRow(r["Date"]));
      const todayPnL  = todayRows.reduce((s, r) => s + (Number(r["Actual Reward"]) || 0), 0);
      const dailyLossLimit = Number(fresh["Daily Loss Limit"] || 0);
      const lossForDayAbs  = Math.max(0, -todayPnL);
      if (dailyLossLimit > 0 && lossForDayAbs >= dailyLossLimit && fresh.LossLimitWarningShownDate !== todayISO) {
        setLossForDay(lossForDayAbs);
        setLossLimitAmount(dailyLossLimit);
        queuePopup("lossLimitWarning", { lossForDay: lossForDayAbs, lossLimitAmount: dailyLossLimit });
        fresh.LossLimitWarningShownDate = todayISO;
      }
    }

    const updateFresh = (patchOrFn) => {
      if (typeof patchOrFn === "function") {
        fresh = patchOrFn(fresh);
      } else {
        fresh = { ...fresh, ...patchOrFn };
      }
    };

    await checkTwoLossAfterGoal(
      allRows,
      freshSettings,
      updateSettings,
      userId,
      accountId,
      updateFresh, // Fix: Updates the `fresh` payload so the flag isn't wiped on save
      queuePopup
    );

    // ---------- 3‑LOSS and 2‑LOSS (today only; suppressed if “after goal” fired) ----------
    {
      const todayRows = allRows.filter(r => isTodayRow(r["Date"]));
      const lastThree = todayRows.slice(-3);
      const lastTwo   = todayRows.slice(-2);
      const isLoss    = (v) => v === true || v === 1 || String(v).toLowerCase() === "true" || String(v).toLowerCase() === "yes";

      const lostAllThree = lastThree.length === 3 && lastThree.every(t => isLoss(t["Loss"]));
      const lostBoth     = lastTwo.length   === 2 && lastTwo.every(t => isLoss(t["Loss"]));



     // --- Three‑loss message (full text you asked for)
      if (lostAllThree && fresh.ThreeLossWarningShownDate !== todayISO) {
        const message = "You've lost 3 trades in a row.\n\nTake a break! \n\nTake some time to evaluate what is really going on. Make sure you are fresh and have a clear mind before taking another trade.\n\nThe Key is to avoid going on tilt and revenge trading.\n\nTrading is 80% Trading Psychology. This may be a good time to watch some videos.  The video below from Psychology of Profits is a good one to start with.   You are worthy of success and you got this!!";
        const videoLink = "https://www.youtube.com/watch?v=s-_oWdFkvkw";
        queuePopup("threeLossWarning", { message, videoLink });
        updateFresh({ ThreeLossWarningShownDate: todayISO });
      }

      // --- Two‑loss message (complete variants)
      if (
        lostBoth &&
        !lostAllThree &&
        fresh.TwoLossWarningShownDate !== todayISO &&
        fresh.TwoLossAfterGoalWarningShownDate !== todayISO
      ) {
        const message = TWO_LOSS_MESSAGE;
        queuePopup("twoLossWarning", { message });
        updateFresh({ TwoLossWarningShownDate: todayISO });
      }
    }

    // ---------- Over‑Risk warning (once per day)
    {
      const propRB         = Number(fresh["Prop Running Balance"] || 0);
      const fallbackRB     = Math.max(0, Number(fresh["Running Balance"] || 0) - Number(fresh["Maximum Loss Limit"] || 0));
      const baseRB         = propRB > 0 ? propRB : fallbackRB;
      
      const { dollars: plannedRiskUSD } = getRiskSpec(
        { "Risk Mode": fresh["Risk Mode"], "Risk Percentage": fresh["Risk Percentage"], "Risk Amount": fresh["Risk Amount"] },
        baseRB
      );

      if (computedActualReward < 0 && Math.abs(computedActualReward) > plannedRiskUSD && fresh.OverRiskWarningShownDate !== todayISO) {
        queuePopup("overRiskWarning", {
          riskAmount: plannedRiskUSD,
          amountLost: Math.abs(computedActualReward),
        });
        updateFresh({ OverRiskWarningShownDate: todayISO });
      }
    }

// ---------- Other checks ----------
    if (fresh["Max Trades Per Day Toggle"]) {
      await checkOvertrading(allRows, Number(fresh["Max Trades Per Day"] || 10), fresh, updateSettings, userId, accountId, updateFresh);
    }
    await checkConsecutiveWins(allRows, fresh, updateSettings, userId, accountId, updateFresh);
    if (fresh["Max Loss Trades Per Day Toggle"]) {
      await checkTooManyLosses(allRows, Number(fresh["Max Loss Trades Per Day"] || 5), fresh, updateSettings, userId, accountId, updateFresh);
    }
    await checkMoodVolatility(allRows, fresh, updateSettings, userId, accountId, updateFresh); // once-per-day guarding inside

    // Daily goal reached popup
    await checkDailyProfitTarget(allRows, Number(fresh["Daily Goal"] || 0), fresh, updateSettings, userId, accountId, updateFresh);

    // Lessons streak
    await checkLessonsLearnedStreak(allRows, fresh, updateSettings, userId, accountId, updateFresh);

    // Drawdown: daily and pool
    if (fresh["Drawdown Limit % Toggle"]) {
      await checkPropDrawdownDaily(fresh, updateSettings, userId, accountId, updateFresh, setDrawdownAmount, setDrawdownPct, tradeObj["Win"]); 
      await checkPropDrawdown(allRows, Number(fresh["Drawdown Limit %"] || 20), fresh, updateSettings, userId, accountId, updateFresh, setDrawdownAmount, setDrawdownPct, tradeObj["Win"]); 
    }

    // Max loss limit blown (absolute)
    await checkMaxLossLimitBlown(fresh, updateSettings, userId, accountId, updateFresh);

    // Risk of ruin (uses your lossProjection)
    await checkRiskOfRuin(lossProjection, fresh, updateSettings, userId, accountId, updateFresh, tradeObj["Win"]);

    
    // ---------- Persist any “shown today” flags ----------
    await updateSettings(userId, accountId, fresh);
    setSettings(fresh);

    // ---------- RESET FORM ----------
    const nextJournal = makeDefaultJournal(fresh); // ensure this leaves "Actual Balance": ""
    setJournal(nextJournal);
    setUserEditingSize(false);
    setMissingFields([]);
    setCalculatedSummary(null);

    // fully clear uncontrolled inputs
    setFormResetKey((k) => k + 1);

    // allow repaint; then silent recalc projections with fresh settings
    await new Promise(requestAnimationFrame);
    await handleCalculate(false, fresh);

  } catch (err) {
    console.error("Trade save failed:", err);
  } finally {
    popupsAllowedRef.current = false;
    setSavingTrade(false);
  }
};



// End Save Trade
const initBal = Number(settings["Initial Balance"] || 0);

// 🔁 Refresh daily goal/loss bars once on page load
useEffect(() => {
  (async () => {
    try {
      const allRows = await fetchJournalRows(userId, accountId);
      const todayISO = new Date().toISOString().slice(0, 10);

      const isTodayRow = (d) => {
        if (!d) return false;
        if (typeof d === "string") {
          if (d.includes("-")) return d.startsWith(todayISO);
          if (d.includes("/")) {
            const [mm, dd, yyyy] = d.split("/");
            const iso = `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
            return iso === todayISO;
          }
          const dt = new Date(d);
          return !isNaN(dt) && dt.toISOString().slice(0,10) === todayISO;
        }
        if (d?.seconds) return new Date(d.seconds * 1000).toISOString().slice(0,10) === todayISO;
        try { return new Date(d).toISOString().slice(0,10) === todayISO; } catch { return false; }
      };

      const todayRows = allRows.filter(r => isTodayRow(r["Date"]));
      const todayPnL = todayRows.reduce((sum, r) => sum + (Number(r["Actual Reward"]) || 0), 0);

      const dailyGoal = Number(settings["Daily Goal"] || 0);
      const dailyLossLimit = Number(settings["Daily Loss Limit"] || 0);
      const positivePnL = Math.max(0, todayPnL);
      const lossAbs = Math.max(0, -todayPnL);

      const goalPct = dailyGoal > 0 ? Math.min(100, (positivePnL / dailyGoal) * 100) : 0;
      const lossPct = dailyLossLimit > 0 ? Math.min(100, (lossAbs / dailyLossLimit) * 100) : 0;

      setDailyGoalProg({ done: positivePnL, target: dailyGoal, pct: goalPct });
      setDailyLossProg({ lost: lossAbs, limit: dailyLossLimit, pct: lossPct });
    } catch (err) {
      console.error("Failed to refresh daily progress bars:", err);
      setDailyGoalProg({ done: 0, target: Number(settings["Daily Goal"] || 0), pct: 0 });
      setDailyLossProg({ lost: 0, limit: Number(settings["Daily Loss Limit"] || 0), pct: 0 });
    }
  })();
}, [userId, accountId]);

// Entry is required if either exit is set to 'Price' and has a value
  const entryRequired = (settings["Stop Loss Mode"] === "Price" && Number(settings["Stop Loss"]) > 0) || 
                        (settings["Take Profit Mode"] === "Price" && Number(settings["Take Profit"]) > 0);
  
  const showEntryWarning = entryRequired && !settings["Entry Price"];

  // 1. Define the criteria for when the app is allowed to calculate
  const canCalculate = useMemo(() => {
    if (!settings["Product"]) return false;

    // Check if there is a valid Risk value
    const riskVal = settings["Risk Mode"] === "$" 
      ? Number(settings["Risk Amount"]) 
      : Number(settings["Risk Percentage"]);
    if (!riskVal || riskVal <= 0) return false;

    // Stop Loss must be entered and greater than 0
    if (!Number(settings["Stop Loss"]) || Number(settings["Stop Loss"]) <= 0) return false;

    // 4. Target (Take Profit) must be present and > 0
      const takeProfit = Number(settings["Take Profit"]);
      if (isNaN(takeProfit) || takeProfit <= 0) return false;

    // If "by Price" is selected, the Entry Price becomes required
    if (entryRequired && !settings["Entry Price"]) return false;

    return true;
  }, [settings, entryRequired]);

  // 2. The Auto-Calculate Trigger (with a 400ms delay to wait for you to stop typing)
  useEffect(() => {
    if (canCalculate) {
      const delayDebounceFn = setTimeout(() => {
        handleCalculate(false); // Runs silently in the background
      }, 400); 
      
      return () => clearTimeout(delayDebounceFn);
    } else {
      // Clear the summary if the required fields are deleted
      setCalculatedSummary(null); 
    }
  }, [
    canCalculate, 
    settings["Product"], 
    settings["Risk Mode"], 
    settings["Risk Amount"], 
    settings["Risk Percentage"], 
    settings["Stop Loss"], 
    settings["Take Profit"], 
    settings["Entry Price"], 
    settings["Running Balance"],
    settings["Account Goal"],
    settings["Maximum Loss Limit"],
    settings["Stop Loss Mode"],
    settings["Take Profit Mode"]
  ]);


return (
  <div className="space-y-8 max-w-5xl mx-auto py-10">
<div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-100 dark:border-gray-700 p-8 mb-4 animate-fadeIn">
  <div className="flex flex-col md:flex-row justify-between md:items-center mb-6 gap-4">
    {/* Wrap the Icon and Title in their own flex container */}
    <div className="flex items-center gap-2">
      <IconGoal />
      <h2 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100">Trade Setup (Funded)</h2>
    </div>

    {/* NEW STRATEGY CHECKLIST BUTTON */}
    <button 
      onClick={() => setShowStrategyModal(true)}
      className="ml-auto md:ml-0 flex items-center gap-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/50 font-bold px-4 py-2 rounded-lg transition"
    >
      <FaClipboardList /> Strategy Checklist
    </button>
  </div>

       {/* --- INFO BANNER --- */}
        <div className="mb-6 text-sm text-gray-600 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 p-3 rounded-lg flex flex-wrap items-center justify-between gap-2">
          <span>Calculations are based on your <strong>effective balance</strong> (your maximum loss amount), not the full account size.</span>
          <button
            onClick={() => setShowLearnMorePopup(true)}
            className="font-bold text-blue-600 hover:text-blue-700 dark:text-blue-400 hover:underline whitespace-nowrap"
          >
            Learn More
          </button>
        </div>

{/* --- COLLAPSIBLE BALANCES --- */}
      <details className="group bg-gray-50 dark:bg-gray-800/30 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <summary className="font-bold text-gray-700 dark:text-gray-300 cursor-pointer list-none flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span>Account Cash Status</span>
            <span className="text-sm font-normal text-gray-500">
              (Balance: <span className="font-bold text-green-600 dark:text-green-400">{isPrivacyMode ? "******" : formatCurrency(settings["Running Balance"] || 0)}</span> | Effective: <span className="font-bold text-blue-600 dark:text-blue-400">{isPrivacyMode ? "******" : formatCurrency(displayPropRunningBalance)}</span>)
            </span>
          </div>
          <span className="text-blue-500 text-sm group-open:hidden">+ Edit Balances</span>
          <span className="text-blue-500 text-sm hidden group-open:block">- Hide</span>
        </summary>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Initial Balance</label>
              <input 
                type="text" 
                readOnly 
                value={formatCurrency(settings["Initial Balance"] ?? 0)} 
                className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-200 dark:bg-gray-800 bg-gray-50 dark:text-gray-200 cursor-not-allowed" 
              />
            </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Account Goal</label>
            <input type="number" value={settings["Account Goal"] || ""} onChange={e => handleSettingChange("Account Goal", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Running Balance</label>
            <input type="number" value={settings["Running Balance"] ?? ""} onChange={e => handleSettingChange("Running Balance", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" />
          </div>

          {/* Effective Balance (Prop Running Balance) */}
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Effective Balance</label>
            <input 
              type="text"
              readOnly
              value={formatCurrency(settings["Prop Running Balance"] ?? 0)}
              className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-200 dark:bg-gray-800 bg-gray-50 dark:text-green-400 font-bold cursor-not-allowed" 
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Max Loss Limit ($)</label>
            <input type="number" value={settings["Maximum Loss Limit"] || ""} onChange={e => handleSettingChange("Maximum Loss Limit", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-red-400" />
          </div>
        </div>
      </details>

      <div className="space-y-6">
        {/* --- STEP 1: TRADE SETUP --- */}
        <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
          <h3 className="text-sm font-bold text-blue-600 dark:text-blue-400 mb-4 uppercase tracking-wide">1. Trade Setup</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Product<span className="text-red-500 text-lg">*</span></label>
              <select value={settings["Product"] || ""} onChange={e => handleSettingChange("Product", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 bg-gray-50 focus:ring-2 focus:ring-blue-400 transition">
                <option value="">Select Product</option>
                {favoriteFuturesOptions.length > 0 && (
                  <optgroup label="♥ Favorites">{favoriteFuturesOptions.map(([v, label]) => <option key={v} value={v}>{label}</option>)}</optgroup>
                )}
                <optgroup label="All Futures">{nonFavoriteOptions.map(([v, label]) => <option key={v} value={v}>{label}</option>)}</optgroup>
              </select>
            </div>
           <div>
             <label className={`block font-bold mb-1 transition-colors ${showEntryWarning ? "text-red-600 dark:text-red-400" : "text-gray-800 dark:text-gray-200"}`}>
               Entry Price {entryRequired ? <span className="text-red-500">* (Required)</span> : "(Optional)"}
             </label>
             <input 
               type="number" 
               placeholder="e.g. 4500.25" 
               value={settings["Entry Price"] || ""} 
               onChange={e => handleSettingChange("Entry Price", e.target.value)} 
               className={`w-full px-4 py-2 rounded-lg border dark:bg-gray-700 bg-gray-100 transition-all outline-none ${
                 showEntryWarning 
                   ? "border-red-500 ring-2 ring-red-500/20 focus:ring-red-400 shadow-[0_0_10px_rgba(239,68,68,0.1)]" 
                   : "dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"
               }`} 
             />
             {showEntryWarning && (
               <p className="text-[10px] font-bold text-red-500 mt-1 uppercase flex items-center gap-1 animate-pulse">
                 <FaExclamationTriangle size={10} /> Entry Price required for Price-based exits
               </p>
             )}
           </div>
          </div>
        </div>

        {/* --- STEP 2: EXITS --- */}
        <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
          <h3 className="text-sm font-bold text-purple-600 dark:text-purple-400 mb-4 uppercase tracking-wide">2. Planned Exits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Stop Loss Group */}
            <div className="bg-red-50/50 dark:bg-red-900/10 p-3 rounded-lg border border-red-100 dark:border-red-900/30">
              <div className="flex justify-between items-center mb-2">
                <label className="font-bold text-gray-800 dark:text-gray-200">Stop Loss<span className="text-red-500 text-lg">*</span></label>
                <select 
                  value={settings["Stop Loss Mode"] || "Ticks"} 
                  onChange={e => handleSettingChange("Stop Loss Mode", e.target.value)}
                  className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
                >
                  <option value="Ticks">by Ticks</option>
                  <option value="Price">by Price</option>
                </select>
              </div>
              <input 
                type="number" 
                placeholder={settings["Stop Loss Mode"] === "Price" ? "Exit Price" : "Ticks"} 
                value={settings["Stop Loss"] || ""} 
                onChange={e => handleSettingChange("Stop Loss", e.target.value)} 
                className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-red-400 dark:bg-gray-700 dark:text-white" 
              />
              
              {/* DYNAMIC HELPER LABEL (Stop Loss) */}
              {settings["Entry Price"] > 0 && settings["Stop Loss"] > 0 && (
                <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
                  {settings["Stop Loss Mode"] === "Price" 
                    ? `Est. Ticks: ${Math.round(Math.abs(settings["Entry Price"] - settings["Stop Loss"]) / (getTickSizeForProduct(settings["Product"]) || 0.25))}`
                    : `Est. Stop Price: ${formatCurrency(Number(settings["Entry Price"]) - (Number(settings["Stop Loss"]) * (getTickSizeForProduct(settings["Product"]) || 0.25)))}`}
                </p>
              )}
            </div>

            {/* Target Group */}
            <div className="bg-green-50/50 dark:bg-green-900/10 p-3 rounded-lg border border-green-100 dark:border-green-900/30">
              <div className="flex justify-between items-center mb-2">
                <label className="font-bold text-gray-800 dark:text-gray-200">Target</label>
                <select 
                  value={settings["Take Profit Mode"] || "Ticks"} 
                  onChange={e => handleSettingChange("Take Profit Mode", e.target.value)}
                  className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
                >
                  <option value="Ticks">by Ticks</option>
                  <option value="Price">by Price</option>
                </select>
              </div>
              <input 
                type="number" 
                placeholder={settings["Take Profit Mode"] === "Price" ? "Exit Price" : "Ticks"} 
                value={settings["Take Profit"] || ""} 
                onChange={e => handleSettingChange("Take Profit", e.target.value)} 
                className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-green-400 dark:bg-gray-700 dark:text-white" 
              />

              {/* DYNAMIC HELPER LABEL (Target) */}
              {settings["Entry Price"] > 0 && settings["Take Profit"] > 0 && (
                <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
                  {settings["Take Profit Mode"] === "Price" 
                    ? `Est. Ticks: ${Math.round(Math.abs(settings["Take Profit"] - settings["Entry Price"]) / (getTickSizeForProduct(settings["Product"]) || 0.25))}`
                    : `Est. Target Price: ${formatCurrency(Number(settings["Entry Price"]) + (Number(settings["Take Profit"]) * (getTickSizeForProduct(settings["Product"]) || 0.25)))}`}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* --- STEP 3: RISK --- */}
        <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
          <h3 className="text-sm font-bold text-orange-600 dark:text-orange-400 mb-4 uppercase tracking-wide">3. Risk Parameters</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="font-bold text-gray-800 dark:text-gray-200">Risk Budget</label>
                <select value={settings["Risk Mode"] || "%"} onChange={e => handleSettingChange("Risk Mode", e.target.value)} className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1">
                  <option value="%">% of Balance</option>
                  <option value="$">$ Amount</option>
                </select>
              </div>
              {String(settings["Risk Mode"] || "%") === "%" ? (
                <input type="text" inputMode="decimal" value={settings["Risk Percentage"] ?? ""} onChange={e => handleSettingChange("Risk Percentage", e.target.value)} onBlur={e => handleSettingChange("Risk Percentage", commitNumberOrEmpty(e.target.value))} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-orange-400 dark:bg-gray-700 dark:text-white" placeholder="Risk %" />
              ) : (
                <input type="text" inputMode="numeric" value={settings["Risk Amount"] ?? ""} onChange={e => handleSettingChange("Risk Amount", e.target.value)} onBlur={e => handleSettingChange("Risk Amount", commitNumberOrEmpty(e.target.value))} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-orange-400 dark:bg-gray-700 dark:text-white" placeholder="Risk $" />
              )}
            </div>
          </div>
        </div>
 


  {/* Row 4 — Trades to Goal + buttons */}
 <div className="col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4 items-end mt-6 md:mt-6">
    <div>
      <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200"># Trades to Goal</label>
      <input
        type="number"
        value={calculatedSummary && winProjection ? winProjection.tradeCount : ""}
        readOnly
        disabled
        className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-500 dark:text-white"
      />
    </div>
    <div className="flex items-end">
      <button
        type="button"
        className={`w-full h-[42px] bg-blue-600 dark:bg-blue-700 text-white rounded-xl px-3 py-2 font-semibold hover:bg-blue-700 hover:dark:bg-blue-800 transition ${
          !calculatedSummary ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
        }`}
        onClick={() => setProjectionOpen(true)}
        disabled={!calculatedSummary}
      >
        Win Projections
      </button>
    </div>
    <div className="flex items-end">
      <button
        type="button"
        className={`w-full h-[42px] bg-red-600 dark:bg-red-700 text-white rounded-xl px-3 py-2 font-semibold hover:bg-red-700 hover:dark:bg-red-800 transition ${
          !calculatedSummary ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
        }`}
        onClick={() => setLossProjectionOpen(true)}
        disabled={!calculatedSummary}
      >
        Loss Projections
      </button>
    </div>
  </div>


  {/* Divider */}
  <div className="col-span-3 border-t border-gray-200 dark:border-gray-700 my-2" />

  {/* Daily Goal, Daily Loss, and Account Progress (all in one grid) */}
  <div className="col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
    {/* Daily Goal (amounts) */}
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="font-bold text-gray-800 dark:text-gray-100">Daily Goal</span>
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          {formatCurrency(dailyGoalProg.done)} / {formatCurrency(dailyGoalProg.target)}
        </span>
      </div>
      <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
        <div
          className="h-3 bg-green-500 transition-all duration-500"
          style={{ width: `${dailyGoalProg.pct}%` }}
        />
      </div>
    </div>

    {/* Daily Loss Limit (amounts) */}
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="font-bold text-gray-800 dark:text-gray-100">Daily Loss Limit</span>
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          {formatCurrency(dailyLossProg.lost)} / {formatCurrency(dailyLossProg.limit)}
        </span>
      </div>
      <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
        <div
          className="h-3 bg-red-500 transition-all duration-500"
          style={{ width: `${dailyLossProg.pct}%` }}
        />
      </div>
    </div>

    {/* Account Goal Progress (FULL width == both columns combined) */}
    <div className="col-span-1 md:col-span-2">
      <div className="flex items-center justify-between mb-1">
        <span className="font-bold text-gray-800 dark:text-gray-100">Goal Progress</span>
        <span className="text-sm font-bold text-gray-700 dark:text-gray-200">
          {Math.round(progress)}%
        </span>
      </div>
      <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
        <div
          className="h-3 bg-green-500 transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  </div>


  {/* Divider */}
  <div className="col-span-3 border-t border-gray-200 dark:border-gray-700 my-4" />
</div>




      {/* Summary Section */}
      <div className="max-w-lg mx-auto border rounded-xl bg-gray-50 dark:bg-gray-800 p-4 mt-6">
        <h3 className="text-lg font-bold mb-2 flex items-center dark:text-white"><IconGoal />Summary</h3>
        {calculatedSummary ? (
          <>
            <SummaryRow label="Price Per Tick" value={calculatedSummary.pricePerTick ? formatCurrency(calculatedSummary.pricePerTick) : ""} />
            <SummaryRow label="Reward to Risk Ratio" value={calculatedSummary.riskRewardRatio} />
              {String(settings["Risk Mode"] || "%") === "$" ? (
                <>
                  <SummaryRow
                    label="Risk Amount (Fixed $)"
                    value={calculatedSummary.percentOfBalance ? formatCurrency(calculatedSummary.percentOfBalance) : ""}
                  />
                  <SummaryRow
                    label="Risk % of Balance"
                    value={`${(calculatedSummary.riskPctFromBase ?? 0).toFixed(2)}%`}
                  />
                </>
              ) : (
                <>
                  <SummaryRow
                    label="Risk % of Balance"
                    value={`${Number(settings["Risk Percentage"] || 0).toFixed(2)}%`}
                  />
                  <SummaryRow
                    label="Risk Budget ($)"
                    value={calculatedSummary.percentOfBalance ? formatCurrency(calculatedSummary.percentOfBalance) : ""}
                  />
                </>
              )}
            <SummaryRow 
              label={
                <span className="inline-flex items-center gap-1">
                  Stop Loss
                  <Tooltip variant="neutral" backdrop content="Planned exit price and distance for a loss.">
                    <span className="cursor-help text-[10px] text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
                  </Tooltip>
                </span>
              }
              value={calculatedSummary.stopLoss 
                ? `${Math.round((calculatedSummary.stopLoss * (getTickSizeForProduct(settings["Product"]) || 0.25)) * 100) / 100} pts / ${calculatedSummary.stopLoss} ticks` 
                : ""} 
            />
            <SummaryRow 
              label={
                <span className="inline-flex items-center gap-1">
                  Take Profit
                  <Tooltip variant="neutral" backdrop content="Planned exit price and distance for a profit.">
                    <span className="cursor-help text-[10px] text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
                  </Tooltip>
                </span>
              }
              value={calculatedSummary.takeProfit 
                ? `${Math.round((calculatedSummary.takeProfit * (getTickSizeForProduct(settings["Product"]) || 0.25)) * 100) / 100} pts / ${calculatedSummary.takeProfit} ticks` 
                : ""} 
            />

            

            <SummaryRow
              label={
                <span className="inline-flex items-center gap-2">
                  Total Risk
                  {calculatedSummary && (
                    <Tooltip
                      variant={
                        // Neutral panel when suggested = 0; otherwise blue on pass, red on fail
                        Number(calculatedSummary.contracts || 0) === 0
                          ? "neutral"
                          : (Number(calculatedSummary.totalRisk || 0) <= Number(calculatedSummary.percentOfBalance || 0))
                              ? "blue"
                              : "red"
                      }
                      backdrop
                      content={(() => {
                        const pricePerTick = Number(calculatedSummary.pricePerTick || 0);
                        const stopLoss = Number(calculatedSummary.stopLoss || 0);
                        const suggested = Number(calculatedSummary.contracts || 0);
                        const minQty = Math.max(1, Number(calculatedSummary.minContracts || 1));
                        const usedContracts = suggested > 0 ? suggested : minQty;

                        const riskUsed = pricePerTick * stopLoss * usedContracts;
                        const percentOfBalance = Number(calculatedSummary.percentOfBalance || 0);
                        const approxPct = Number(calculatedSummary.riskPctFromBase ?? 0).toFixed(2);

                        const isZero = suggested === 0;

                        return (
                          // Make the CONTENT red when suggested = 0 (panel stays neutral)
                          <div className={`space-y-2 ${isZero ? "text-red-600 dark:text-red-400" : ""}`}>
                            <div
                              className={
                                "text-sm uppercase tracking-wide font-bold opacity-100 " +
                                (isZero ? "text-red-700 dark:text-red-500" : "text-blue-700 dark:text-blue-400")
                              }
                            >
                              How we calculate total risk
                            </div>

                            {/* Plain-English formula */}
                            <div className="font-sans text-[16px]">
                              Price Per Tick × Stop Loss × Suggested Contracts
                            </div>

                            {/* Formula with actual values USED for Total Risk */}
                            <div className="font-sans text-[16px]">
                              {formatCurrency(pricePerTick)} × {stopLoss} ticks × {usedContracts} contract{usedContracts === 1 ? "" : "s"}
                              {isZero && <span className="opacity-80"> (min)</span>}{" "}
                              = <strong>{formatCurrency(riskUsed)}</strong>
                            </div>

                            {/* If suggested is 0, explain the fallback */}
                            {isZero && (
                              <p className="text-[13px]">
                                Suggested contracts is <span className="font-semibold">0</span>, so we use your minimum:{" "}
                                <span className="font-semibold">{minQty}</span>.
                              </p>
                            )}

                            {/* Percent of balance to compare against */}
                            <div className="font-sans text-[16px]">
                               Percent of balance (≈ {approxPct}%): <strong>{formatCurrency(percentOfBalance)}</strong>
                            </div>

                            {/* Verdict: keep explicit colors for clarity */}
                            <div
                              className={`mt-1 text-[16px] font-semibold ${
                                riskUsed <= percentOfBalance ? "text-green-300" : "text-red-400 dark:text-red-400"
                              }`}
                            >
                              {riskUsed <= percentOfBalance ? "✓" : "✕"} Total risk{" "}
                              {riskUsed <= percentOfBalance ? "is" : "is not"} ≤ percent of balance
                            </div>

                            {/* Hide this advice when suggested = 0 */}
                            {suggested > 0 && (
                              <div className="font-sans text-[14px] mb-6">
                                If you want to trade with more contracts you can increase the risk percentage to an amount you are comfortable with.
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    >
                      {/* Single trigger element (child of Tooltip). No second Tooltip needed. */}
                      <span
                        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-[12px] font-bold"
                        aria-label="Show how total risk is calculated"
                      >
                        ?
                      </span>
                    </Tooltip>
                  )}
                </span>
              }
              value={calculatedSummary.totalRisk ? formatCurrency(calculatedSummary.totalRisk) : ""}
              red={calculatedSummary.contracts === 0}
            />
            <SummaryRow label="Target Reward" value={calculatedSummary.targetReward ? formatCurrency(calculatedSummary.targetReward) : ""} />
            <SummaryRow
              label={
                <span className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-300">
                  {`Max Stop Loss Size for ${calculatedSummary.minContracts} contract(s)`}

                  <Tooltip
                    variant="blue"
                    backdrop
                    content={(() => {
                      const minQty = Math.max(1, Number(calculatedSummary.minContracts || 1));
                      const ppt = Number(calculatedSummary.pricePerTick || 0);           // $ per tick
                      const pob = Number(calculatedSummary.percentOfBalance || 0);       // $ risk budget
                      const maxTicksForMin = (ppt > 0 && minQty > 0)
                        ? Math.floor(pob / (ppt * minQty))
                        : 0;

                      return (
                        <div className="space-y-2">
                          <div className="text-sm uppercase font-bold opacity-100 text-blue-700 dark:text-blue-400">
                            Max stop loss for minimum contracts
                          </div>

                          <p className="text-[16px] ">
                            Your <strong>minimum contract</strong> setting is used to calculate
                            the largest stop loss (in ticks) you can take while staying within your risk tolerance.
                          </p>

                          {/* Guidance */}
                          <p className="text-[16px] leading-relaxed">
                            If the maximum stop loss is a <strong>very large number</strong>,
                            that can indicate you should increase the <strong>minimum contracts </strong>
                            in your settings.
                          </p>
                        </div>
                      );
                    })()}
                  >
                    {/* Small “?” trigger button; fully accessible */}
                    <span
                      className="inline-flex items-center justify-center w-5 h-5 rounded-full
                                 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200
                                 text-[12px] font-bold cursor-help"
                      aria-label="Explain how max stop loss size for minimum contracts is calculated"
                    >
                      ?
                    </span>
                  </Tooltip>
                </span>
              }
              value={
                Number.isFinite(calculatedSummary.maxStopLossSizeForMin) && calculatedSummary.maxStopLossSizeForMin > 0
                  ? <span className="text-gray-600 dark:text-gray-300 font-semibold">
                      {Math.round((calculatedSummary.maxStopLossSizeForMin * (getTickSizeForProduct(settings["Product"]) || 0.25)) * 100) / 100} pts / {calculatedSummary.maxStopLossSizeForMin} ticks
                    </span>
                  : ""
              }
            />
            <div
              className={
                calculatedSummary.contracts === 0
                  ? "rounded-md px-2 py-1 bg-red-50 dark:bg-red-900/40"
                  : "rounded-md px-2 py-1 bg-blue-300 dark:bg-blue-400/30"
              }
            >
              <SummaryRow
                label={<span className="text-gray-600 dark:text-gray-300">Suggested # of Contracts</span>}
                value={
                  calculatedSummary.contracts === 0
                    ? <span className="text-red-600 font-bold">0</span>
                    : <span className="text-gray-600 dark:text-gray-300 font-semibold">
                        {calculatedSummary.contracts}
                      </span>
                }
                red={calculatedSummary.contracts === 0}
              />
            </div>

          </>

        ) : (
          <div className="text-gray-400 dark:text-gray-600 text-center py-6">Summary will appear after you click Calculate.</div>
        )}
      </div>
      {/* Save Settings */}
      <div className="flex gap-4 justify-end mt-8">
        <button
          type="button"
          ref={calcBtnRef}
          onClick={() => handleCalculate(true)}
          disabled={savingSettings || !settings["Product"]}
          className={`bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-800 dark:to-blue-500 
            text-white font-semibold px-6 py-3 rounded-xl shadow-lg 
            transition 
            ${savingSettings || !settings["Product"]
              ? "opacity-50 cursor-not-allowed"
              : "hover:from-blue-700 hover:to-blue-500 dark:hover:from-blue-900 dark:hover:to-blue-700"
            }`}
        >
          {savingSettings ? "Calculating..." : "Calculate"}
        </button>


      </div>
    </div>



{/* Journal Entry - Logical Grouping Layout (Prop Futures) */}
    {!settings["Calculator Only"] && (
<div key={formResetKey} className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-100 dark:border-gray-700 p-8 animate-fadeIn">
  
  {/* Header */}
  <div className="flex items-center mb-6 border-b border-gray-100 dark:border-gray-700 pb-4">
    <IconJournal />
    <div>
      <h3 className="text-xl font-bold text-yellow-700 dark:text-yellow-400">Journal Entry</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
        Log your trade details below. <span className="text-red-500">* denotes required</span>
      </p>
    </div>
  </div>

  <div className="space-y-8"> 

    {/* SECTION 1: SETUP & CONTEXT */}
    <div>
      <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-blue-500 pl-3">
        1. Setup & Context
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Date */}
        <div>
          <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Date<span className="text-red-500 text-lg">*</span></label>
          <input 
            type="date" 
            value={journal["Date"] || getTodayLocalISO()} 
            onChange={e => handleJournalChange("Date", e.target.value)} 
            className={`w-full h-[42px] px-4 py-2 rounded-lg border bg-gray-50 dark:bg-gray-700 dark:text-white transition focus:ring-2 focus:ring-blue-400 ${
              missingFields.includes("Date") ? "border-red-500" : "border-gray-200 dark:border-gray-700"
            }`}
          />
        </div>
        
        {/* Session */}
        {!settings["Hide Session"] && (
        <div>
          <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Session</label>
          <select 
            id="session"
            value={journal["Session"]} 
            onChange={e => handleJournalChange("Session", e.target.value)} 
            className="w-full h-[42px] px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
          >
            <option value="">Select</option>
            {SESSION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
        )}

        {/* Timeframe */}
        <div>
           <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Timeframe</label>
           <input 
             id="timeframe"
             type="text" 
             list="timeframe-options" 
             value={journal["Timeframe"]} 
             onChange={e => handleJournalChange("Timeframe", e.target.value)} 
             placeholder="e.g. 5m"
             className="w-full h-[42px] px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition" 
           />
           <datalist id="timeframe-options">
              {TIMEFRAME_OPTIONS.map(tf => <option key={tf} value={tf} />)}
           </datalist>
        </div>

        {/* Position */}
        <div>
           <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Position</label>
           <select 
             id="position"
             value={journal["Position"]} 
             onChange={e => handleJournalChange("Position", e.target.value)} 
             className="w-full h-[42px] px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
           >
            <option value="">Select</option>
            {POSITION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
      </div>
    </div>

    {/* SECTION 2: EXECUTION */}
    <div>
      <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-purple-500 pl-3">
        2. Execution
      </h4>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Entry Group */}
        <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
          <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Entry Details</label>
          <div className="space-y-4">
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Time</label>
               <div className="relative flex items-center">
                  <input 
                    id="entryTime" 
                    type="time" 
                    ref={entryTimeRef} 
                    value={to24HourTime(journal["Entry Time"])} 
                    onChange={e => {
                      const rawTime24 = e.target.value;
                      setJournal(prev => {
                        const nextState = { ...prev, "Entry Time": toAmPm(rawTime24) };
                        
                        // Auto-populate Session only if it is currently empty
                        if (!prev.Session || prev.Session === "") {
                          const calculatedSession = getSessionFromTime(rawTime24);
                          if (calculatedSession) {
                            nextState.Session = calculatedSession;
                          }
                        }
                        
                        return nextState;
                      });
                    }} 
                    className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" 
                  />
                  <FaRegClock
                    className="absolute right-3 text-lg text-gray-400 cursor-pointer hover:text-blue-500"
                    onClick={() => entryTimeRef.current?.showPicker && entryTimeRef.current.showPicker()}
                  />
               </div>
             </div>

             {!settings["Hide Entry Price"] && (
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Price</label>
               <input
                  id="entryPrice"
                  type="number"
                  inputMode="decimal"
                  value={journal["Entry Price"] || ""}
                  onChange={(e) => setJournal(prev => ({ ...prev, "Entry Price": e.target.value }))}
                  className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition"
                  placeholder="e.g. 4523.25"
                />
             </div>
             )}

             {!settings["Hide Entry Strategy"] && (
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Strategy</label>
               <input
                  id="entryStrategy"
                  type="text"
                  value={journal["Entry Strategy"] ?? ""}
                  onChange={(e) => setJournal((j) => ({ ...j, ["Entry Strategy"]: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition"
                  list="entry-strategy-options"
                />
                <datalist id="entry-strategy-options">
                  {entryStrategies.map(strategy => <option key={strategy} value={strategy} />)}
                </datalist>
             </div>
             )}
          </div>
        </div>

        {/* Exit Group */}
        <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
          <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Exit Details</label>
          <div className="space-y-4">
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Time</label>
               <div className="relative flex items-center">
                  <input
                    id="exitTime"
                    type="time"
                    ref={exitTimeRef}
                    className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition"
                    value={to24HourTime(journal["Exit Time"])}
                    onChange={(e) => {
                      const formatted = toAmPm(e.target.value);
                      setJournal((prev) => ({ ...prev, ["Exit Time"]: formatted }));
                    }}
                  />
                  <FaRegClock
                    className="absolute right-3 text-lg text-gray-400 cursor-pointer hover:text-blue-500"
                    onClick={() => exitTimeRef.current?.showPicker && exitTimeRef.current.showPicker()}
                  />
               </div>
             </div>

             {!settings["Hide Exit Price"] && (
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Price</label>
               <input
                  id="exitPrice"
                  type="number"
                  inputMode="decimal"
                  value={journal["Exit Price"] || ""}
                  onChange={(e) => setJournal(prev => ({ ...prev, "Exit Price": e.target.value }))}
                  className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition"
                  placeholder="e.g. 4527.50"
                />
             </div>
            )}

            {!settings["Hide Exit Strategy"] && (
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Strategy</label>
               <input
                  id="exitStrategy"
                  type="text"
                  value={journal["Exit Strategy"] ?? ""}
                  onChange={(e) => setJournal((j) => ({ ...j, ["Exit Strategy"]: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition"
                  list="exit-strategy-options"
                />
                <datalist id="exit-strategy-options">
                  {exitStrategies.map(strategy => <option key={strategy} value={strategy} />)}
                </datalist>
             </div>
             )}
          </div>
        </div>


            {/* Calculated Stats Group */}
        <div className="flex flex-col justify-center bg-blue-50 dark:bg-blue-900/10 p-4 rounded-xl border border-blue-100 dark:border-blue-800">
          <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Quantity DETAILS</label>
          <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold mb-1 text-gray-800 dark:text-gray-200">
                  # of Contracts<span className="text-red-500 text-lg">*</span>
                </label>
                <input
                  id="contracts"
                  type="number"
                  min={0}
                  max={settings["Maximum Contracts"] || 100}
                  value={
                    journal["Size"] !== undefined && journal["Size"] !== ""
                      ? journal["Size"]
                      : calculatedSummary?.contracts ?? ""
                  }
                  onChange={e => {
                    handleJournalChange("Size", e.target.value);
                    setUserEditingSize(true);
                  }}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    missingFields.includes("- # of Contracts") 
                        ? "border-red-500" 
                        : "border-gray-200 dark:border-gray-600"
                  } focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono`}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1 text-gray-800 dark:text-gray-200">
                  Ticks
                </label>
                <input
                  id="ticks"
                  type="number"
                  value={journal["Ticks"] || ""}
                  onChange={e => handleJournalChange("Ticks", e.target.value)}
                  className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono"
                  placeholder="Qty"
                />
              </div>

              {!settings["Hide Potential"] && (
              <div>
                              <label className="block text-sm font-bold mb-1 text-gray-800 dark:text-gray-200 flex items-center gap-2"> 
                                Potential Ticks
                                <Tooltip 
                                  variant="neutral" 
                                  content={<div className="text-sm">This field captures the potential ticks that could have been captured with the trade.</div>} 
                                > {/* [!code ++] */}
                                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-[12px] font-bold cursor-help"> 
                                    ? 
                                  </span> 
                                </Tooltip> 
                              </label>
                              <input
                                id="potential"
                                type="number"
                                value={journal["Potential"] || ""}
                                onChange={e => handleJournalChange("Potential", e.target.value)}
                                className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono"
                                placeholder="Qty"
                              />
                            </div>
                            )}
                        </div>
                      </div>
                  </div>
              </div>

    {/* SECTION 3: OUTCOME */}
    <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-700/50 p-6 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm">
      <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-300 font-bold mb-4 border-l-4 border-green-500 pl-3">
        3. Outcome
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 items-start">
        
        {/* Win/Loss */}
        <div>
          <label className="block font-bold mb-2 text-gray-800 dark:text-gray-200">Result<span className="text-red-500 text-lg">*</span></label>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => { handleJournalChange("Win", true); handleJournalChange("Loss", false); }}
              className={`flex-1 py-3 rounded-xl font-bold transition shadow-sm border ${
                journal["Win"] 
                  ? "bg-green-500 text-white border-green-600 ring-2 ring-green-300 dark:ring-green-800" 
                  : "bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-gray-600"
              }`}
            >
              WON
            </button>
            <button
              type="button"
              onClick={() => { handleJournalChange("Win", false); handleJournalChange("Loss", true); }}
              className={`flex-1 py-3 rounded-xl font-bold transition shadow-sm border ${
                journal["Loss"] 
                  ? "bg-red-500 text-white border-red-600 ring-2 ring-red-300 dark:ring-red-900" 
                  : "bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-gray-600"
              }`}
            >
              LOSS
            </button>
          </div>
        </div>

        {/* New Balance */}
        <div>
           <label className="block font-bold mb-2 text-gray-800 dark:text-gray-200">New Account Balance<span className="text-red-500 text-lg">*</span></label>
           <div className="relative">
             <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
             <input 
               id="actual-balance-input"
               type="text" 
               inputMode="decimal" 
               ref={actualBalRef}
               value={journal["Actual Balance"] ?? ""} 
               onChange={e => setJournal(j => ({ ...j, ["Actual Balance"]: e.target.value }))} 
               onBlur={(e) => {
                  const n = parseCurrencyToNumber(e.target.value);
                  if (Number.isFinite(n)) {
                    setJournal(j => ({ ...j, ["Actual Balance"]: n }));
                  }
               }}
               className={`w-full pl-8 px-4 py-3 rounded-xl border focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white bg-white transition font-bold text-lg shadow-inner ${
                 missingFields.includes("- Actual Balance") ? "border-red-500" : "border-gray-300 dark:border-gray-500"
               }`} 
               placeholder="0.00"
             />
           </div>
        </div>
        {/* NEW: Target Reached Checkbox */}
      <div className="mt-6 flex items-center bg-green-50 dark:bg-green-900/10 p-3 rounded-xl border border-green-100 dark:border-green-800">
         <input 
             type="checkbox" 
             id="targetReachedFut" 
             checked={!!journal["Target Reached"]} 
             onChange={e => handleJournalChange("Target Reached", e.target.checked)} 
             className="mr-3 w-5 h-5 text-green-600 rounded border-gray-300 focus:ring-green-500 cursor-pointer" 
         />
         <label htmlFor="targetReachedFut" className="text-sm font-bold text-green-700 dark:text-green-400 cursor-pointer">
             Target Reached?
         </label>
      </div>
      </div>
    </div>

    {/* SECTION 4: PSYCHOLOGY & REVIEW */}
    <div>
      <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-yellow-500 pl-3">
        4. Psychology & Review
      </h4>
      
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
        {/* Rules */}
        <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Followed Rules?<span className="text-red-500 text-lg">*</span></label>
            <select
              id="rules"
              value={journal["Did I follow my Rules?"] || ""}
              onChange={e => handleJournalChange("Did I follow my Rules?", e.target.value)}
              className={`w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition ${
                missingFields.includes("- Did I follow my Rules?") ? "border-red-500" : "border-gray-200 dark:border-gray-700"
              }`}
            >
              <option value="">Select</option>
              {RULES_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
        </div>
        {/* Mood */}
        {!settings["Hide Mood"] && (
        <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Mood (1-5)</label>
            <select
              id="mood"
              value={journal["Mood_Mindset"] ?? ""}
              onChange={(e) => setJournal((j) => ({ ...j, Mood_Mindset: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
            >
              <option value="">Select (1=Calm, 5=Stress)</option>
              {MOOD_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
        </div>
        )}

        {/* Grade */}
        {!settings["Hide Grade"] && (
        <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Grade</label>
            <select
              id="grade"
              value={journal["Grade"] ?? ""}
              onChange={(e) => setJournal((j) => ({ ...j, Grade: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
            >
              <option value="">Select</option>
              {GRADE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
        </div>
        )}
      </div>

      {/* Inputs */}
      <div className="space-y-6">
         <div>
           <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Link / Screenshot</label>
           <input
              id="link"
              type="text"
              value={journal["Links_Screenshots"] ?? ""}
              onChange={(e) => setJournal((j) => ({ ...j, ["Links_Screenshots"]: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
              placeholder="Paste link here"
            />
         </div>
         
         <div>
           <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Notes</label>
           <textarea
              id="notes"
              value={journal["Notes"] ?? ""}
              onChange={(e) => setJournal((j) => ({ ...j, Notes: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition resize-y"
              rows={3}
              placeholder="Anything to note about this trade?"
            />
         </div>

         <div>
           <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">
             Emotional Notes
           </label>
           <textarea 
             rows={3} 
             value={journal["Emotional Notes"] || ""} 
             onChange={e => handleJournalChange("Emotional Notes", e.target.value)} 
             placeholder="How were you feeling during this execution? Anxious? Confident? Rushed?"
             className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition resize-y" 
           />
         </div>

         {/* FLAG FOR REVIEW CHECKBOX */}
         <div className="mt-2">
           <label className="inline-flex items-center text-red-600 dark:text-red-400 font-bold cursor-pointer select-none">
             <input 
               type="checkbox" 
               checked={!!journal["Needs Review"]} 
               onChange={e => handleJournalChange("Needs Review", e.target.checked)} 
               className="mr-2 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500" 
             />
             Flag for Review
           </label>
         </div>

         {!settings["Hide Lesson"] && (
         <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-xl border border-blue-100 dark:border-blue-800/50">
            <label className="block font-bold mb-2 text-blue-800 dark:text-blue-300">
              Lesson (Optional)
            </label>
            <textarea
              id="lesson"
              value={journal["Lesson"] ?? ""}
              onChange={(e) => setJournal((j) => ({ ...j, Lesson: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-blue-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 dark:text-white bg-white transition resize-y"
              rows={2}
              placeholder="What did you learn from this trade?"
            />
            <div className="mt-3">
              <label className="inline-flex items-center text-blue-800 dark:text-blue-300 font-semibold cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!journal.LogLesson}
                  onChange={(e) => setJournal((j) => ({ ...j, LogLesson: e.target.checked }))}
                  className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                Log this as a Lesson
              </label>
            </div>
         </div>
         )}
      </div>
    </div>
  
  </div>

  {/* Save Button */}
  <div className="flex justify-end mt-10 pt-6 border-t border-gray-100 dark:border-gray-700">
     <button 
       className="w-full sm:w-auto min-w-[200px] bg-gradient-to-r from-orange-500 to-orange-400 dark:from-orange-600 dark:to-orange-500 text-white font-bold px-8 py-3 rounded-xl shadow-lg hover:from-orange-600 hover:to-orange-500 transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed" 
       onClick={handleSaveTrade} 
       disabled={savingTrade}
     >
        {savingTrade ? "Saving..." : "Save Trade"}
     </button>
  </div>

</div>
)} {/* <--- ADD THIS CLOSING BRACE/PARENTHESIS HERE */}


    {/* --- WIN PROJECTION MODAL --- */}
{projectionOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center animate-fadeIn">
    <div className="bg-gradient-to-br from-blue-50/90 via-white/90 to-blue-100/80 dark:from-[#1e293b] dark:via-gray-800 dark:to-[#1e293b]
      max-w-4xl w-full rounded-2xl shadow-2xl border-4 border-blue-300 dark:border-blue-900 p-8 relative overflow-auto max-h-[85vh]">
      <button
        className="absolute top-2 right-2 bg-gray-200 dark:bg-gray-900 text-gray-600 dark:text-gray-200 rounded-full w-8 h-8 flex items-center justify-center hover:bg-blue-100 hover:dark:bg-blue-800"
        onClick={() => setProjectionOpen(false)}
        aria-label="Close"
      >×</button>
        
        <h3 className="text-2xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center gap-2">
          <svg width="28" height="28" fill="none" viewBox="0 0 24 24" className="mr-2">
            <circle cx="12" cy="12" r="10" stroke="#2563eb" strokeWidth="2" />
            <path d="M8 12l2 2l4-4" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Win Projection Table
        </h3>

        {/* --- RESTORED EXPLANATORY TEXT --- */}
        <p className="mb-6 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
          Projections show how many trades (using at least your minimum lot size setting) it would take to reach your goal. Any trades with the red background indicate when you are over your risk tolerance. The totals are estimates and don't take into account broker fees and commissions.
          <br /><br />
          If you prefer to always trade the same number of contracts, you can set the minimum and maximum number of contracts to the same number in the settings.
        </p>

        {/* 👇 ADD THIS TOGGLE BLOCK 👇 */}
                    <div className="mb-4 flex items-center justify-end">
                      <label className="flex items-center cursor-pointer text-sm font-bold text-gray-700 dark:text-gray-300 select-none">
                        <span className="mr-3">Group by Trading Day</span>
                        <div className="relative flex items-center">
                          <input 
                            type="checkbox" 
                            className="sr-only" 
                            checked={showDaySeparators} 
                            onChange={() => setShowDaySeparators(!showDaySeparators)} 
                          />
                          <div className={`w-10 h-5 rounded-full shadow-inner transition-colors duration-200 ease-in-out ${showDaySeparators ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-700'}`}></div>
                          <div className={`absolute left-0 top-0 w-5 h-5 bg-white rounded-full shadow border border-gray-200 transform transition-transform duration-200 ease-in-out ${showDaySeparators ? 'translate-x-5' : 'translate-x-0'}`}></div>
                        </div>
                      </label>
                    </div>
                    {/* 👆 END TOGGLE BLOCK 👆 */}
        
        {/* --- DYNAMIC TIME ESTIMATION BLOCK --- */}
        {(() => {
              const accountGoal = Number(settings["Account Goal"] || 0);
              const running = Number(settings["Running Balance"] || 0);
              const dailyGoal = Number(settings["Daily Goal"] || 0);
              const trades      = winProjection?.trades || [];
              const tradeCount = winProjection?.tradeCount || 0;

              // 1. Check if account is blown (Prop Firm Max Loss or $0)
              const init = Number(settings["Initial Balance"] || 0);
              const maxLoss = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
              const maxLossAbs = Math.abs(maxLoss);
              const ruinThreshold = maxLossAbs > 0 ? init - maxLossAbs : 0;

              if (running <= ruinThreshold) {
                return (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 text-sm font-bold text-red-800 dark:text-red-200">
                    No trades can be taken. Your account is blown. Please consider switching to a demo account and refine your strategy before purchasing another challenge or resetting your account.
                  </div>
                );
              }

              if (accountGoal <= 0) return null;
              if (running >= accountGoal) {
                return (
                  <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 text-sm font-bold text-green-800 dark:text-green-200">
                    You’re already at or above your account goal of {formatCurrency(accountGoal)}.
                  </div>
                );
              }

          // 2. Calculate "Trading Days" using the Hybrid Logic
          //    - If Trade Profit < Daily Goal: Accumulate trades until Daily Goal is met (counts as 1 day).
          //    - If Trade Profit >= Daily Goal: Count 1 Trade as 1 Day.
          let estimatedDays = 0;
          let currentDayAccumulator = 0;

          if (dailyGoal > 0) {
            for (const t of trades) {
              const profit = t.tradeProfit || 0;
              
              if (profit >= dailyGoal) {
                // One trade makes the day
                estimatedDays++;
                currentDayAccumulator = 0; 
              } else {
                // Accumulate small trades to make a day
                currentDayAccumulator += profit;
                if (currentDayAccumulator >= dailyGoal) {
                  estimatedDays++;
                  currentDayAccumulator = 0; // Day complete
                }
              }
            }
            // Count any partial progress at the end as a final day
            if (currentDayAccumulator > 0) estimatedDays++;
          } else {
            // Fallback if no Daily Goal set: 1 trade = 1 day (Conservative)
            estimatedDays = tradeCount;
          }

          return (
            <div className="mb-6 p-5 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/50">
              <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                <FaRegClock className="text-blue-500" />
                Estimated Time to Goal
              </h4>
              
              <div className="flex flex-col sm:flex-row gap-6 items-start">
                <div className="flex-1">
                   <div className="text-3xl font-extrabold text-blue-700 dark:text-blue-300">
                    {tradeCount} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">winning trades in ≈ </span> {estimatedDays} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">trading days</span> 
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                    To reach <strong>{formatCurrency(accountGoal)}</strong> requires approximately <strong>{tradeCount}</strong> winning trades in about {estimatedDays} if you reached your trading daily goal.
                  </p>
                </div>

                <div className="flex-1 text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm">
                  <strong className="block text-gray-800 dark:text-gray-200 mb-1">How is this calculated?</strong>
                  <ul className="list-disc pl-4 space-y-1">
                    <li>
                      While trade profit &lt; <strong>{formatCurrency(dailyGoal)}</strong>, we assume you trade until you hit your daily goal.
                    </li>
                    <li>
                      Once trade profit &ge; <strong>{formatCurrency(dailyGoal)}</strong>, we assume 1 trade per day.
                    </li>
                     <li>
                      As your balance compounds, your position sizes and profits will <strong>increase</strong>. Make sure to update your <strong>Daily Goal</strong> in the settings to reflect your growing account size!
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          );
        })()}
        {/* --- END DYNAMIC BLOCK --- */}

        {winProjection?.tradeCount === 0 && (
              <div className={`text-center py-4 font-semibold ${
                (() => {
                  const r = Number(settings["Running Balance"] || 0);
                  const i = Number(settings["Initial Balance"] || 0);
                  const m = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
                  const mAbs = Math.abs(m);
                  const thresh = mAbs > 0 ? i - mAbs : 0;
                  return r <= thresh;
                })() 
                  ? "text-red-600 dark:text-red-400" 
                  : "text-green-600 dark:text-green-300"
              }`}>
                {(() => {
                  const r = Number(settings["Running Balance"] || 0);
                  const i = Number(settings["Initial Balance"] || 0);
                  const m = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
                  const mAbs = Math.abs(m);
                  const thresh = mAbs > 0 ? i - mAbs : 0;
                if (r <= thresh) {
                    return "";
                  }
                  return "No trades can be taken with the current settings. If you have reached your goal you can adjust it and continue trading.";
                })()}
              </div>
            )}


      {winProjection?.tradeCount > 0 && (() => {
              // Only allow the table to render if the account is NOT blown
            const r = Number(settings["Running Balance"] || 0);
            const i = Number(settings["Initial Balance"] || 0);
            const m = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
            const mAbs = Math.abs(m);
            const thresh = mAbs > 0 ? i - mAbs : 0;
            return r > thresh; 
            })() && (
      <div className="overflow-auto max-h-[50vh]">
        <table className="min-w-full border-separate border-spacing-0 rounded-xl shadow bg-white/95 dark:bg-gray-900/90 border-2 border-blue-200 dark:border-blue-800">
          <thead>
            <tr className="bg-blue-100 dark:bg-blue-900/80">
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Trade #</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Balance</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Max Drawdown</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold"># Contracts</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Total Risk</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Total Profit</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">End Balance</th>
            </tr>
          </thead>
          <tbody>
                    {(() => {
                      // 1. ROBUST START: Grab the actual balance to start compounding
                      let runningBal = Number(settings["Running Balance"] || settings["Beginning Balance"] || 0);
                      
                      const rowsToShow = isTrialing 
                                      ? winProjection.trades.slice(0, 10) 
                                      : winProjection.trades;

                      let currentDay = 1;
                      let dayAccumulator = 0;
                      const dailyGoalAmt = Number(settings["Daily Goal"] || 0);

                      const tradesWithDays = rowsToShow.map((row) => {
                        const assignedDay = currentDay;
                        
                        // 2. BUG FIX: Force profit to be positive (Fixes Short trades missing Math.abs)
                        const safeProfit = Math.abs(row.tradeProfit ?? row.targetReward ?? row.profit ?? 0);
                        
                        if (dailyGoalAmt > 0) {
                          dayAccumulator += safeProfit;
                          if (dayAccumulator >= dailyGoalAmt) {
                            currentDay++;
                            dayAccumulator = 0;
                          }
                        }
                        return { ...row, day: assignedDay, safeProfit };
                      });

                      return tradesWithDays.map((row, i) => {
                        const isNewDay = showDaySeparators && i > 0 && row.day !== tradesWithDays[i-1].day;

                        // 3. DYNAMIC COMPOUNDING: Force the balance to strictly go UP
                        const before = runningBal;
                        const after = before + row.safeProfit;
                        runningBal = after; // Carry forward to the next row

                        // Robust contract sizing fallback
                        const contracts = row.contracts ?? row.size ?? row.lots ?? 0;

                        return (
                          <React.Fragment key={i}>
                            {isNewDay && (
                              <tr className="bg-blue-100/50 dark:bg-blue-900/30">
                                <td colSpan="10" className="px-3 py-2 text-center text-xs font-bold text-blue-800 dark:text-blue-300 uppercase tracking-widest border-y border-blue-200 dark:border-blue-800">
                                  — Day {row.day} —
                                </td>
                              </tr>
                            )}
                            <tr className="hover:bg-blue-50 dark:hover:bg-blue-900/30 bg-white dark:bg-gray-900 transition border-b border-gray-100 dark:border-gray-800">
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{row.trade}</td>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(before)}</td>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(row.riskDollar)}</td>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                                {assetClass === "Forex" ? Number(contracts).toFixed(2) : contracts}
                              </td>
                              {(assetClass === "Options") && (
                                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100 italic">
                                      {formatCurrency(row.cost)}
                                  </td>
                              )}
                              <td className={`px-3 py-2 ${contracts === 0 ? "text-red-600 font-bold" : "text-gray-900 dark:text-gray-100"}`}>
                                {formatCurrency(row.totalRisk)}
                              </td>
                              <td className="px-3 py-2 text-green-600 dark:text-green-400 font-bold">{formatCurrency(row.safeProfit)}</td>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(after)}</td>
                            </tr>
                          </React.Fragment>
                        );
                      });
                    })()}
                  </tbody>
        </table>

      {/* --- NEW: Trial Indicator --- */}
              {isTrialing && winProjection.trades.length > 5 && (
                <div className="p-4 bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700 text-center">
                  <p className="text-lg font-bold text-gray-600 dark:text-red-500 flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                    Full projection table available after Free Trial
                  </p>
                </div>
              )}
            </div>
            )}
    </div>
  </div>
)}


{lossProjectionOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center animate-fadeIn">
    <div className="bg-gradient-to-br from-red-50/90 via-white/90 to-red-100/80 dark:from-[#2d1616] dark:via-gray-800 dark:to-[#2d1616]
      max-w-5xl w-full rounded-2xl shadow-2xl border-4 border-red-300 dark:border-red-900 p-8 relative overflow-auto max-h-[85vh]">
      <button
        className="absolute top-2 right-2 bg-gray-200 dark:bg-gray-900 text-gray-600 dark:text-gray-200 rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-100 hover:dark:bg-red-800"
        onClick={() => setLossProjectionOpen(false)}
        aria-label="Close"
      >×</button>

      <h3 className="text-2xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center gap-2">
        <svg width="28" height="28" fill="none" viewBox="0 0 24 24" className="mr-2">
          <circle cx="12" cy="12" r="10" stroke="#dc2626" strokeWidth="2" />
          <path d="M12 8v4l3 3" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Loss Projection Table
      </h3>

      <p className="mb-6 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
        Projections show how many consecutive full-loss trades (using at least your minimum contract setting) it would take to blow your account.
      </p>

      {/* 👇 ADD THIS TOGGLE BLOCK 👇 */}
            <div className="mb-4 flex items-center justify-end">
              <label className="flex items-center cursor-pointer text-sm font-bold text-gray-700 dark:text-gray-300 select-none">
                <span className="mr-3">Group by Trading Day</span>
                <div className="relative flex items-center">
                  <input 
                    type="checkbox" 
                    className="sr-only" 
                    checked={showDaySeparators} 
                    onChange={() => setShowDaySeparators(!showDaySeparators)} 
                  />
                  <div className={`w-10 h-5 rounded-full shadow-inner transition-colors duration-200 ease-in-out ${showDaySeparators ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-700'}`}></div>
                  <div className={`absolute left-0 top-0 w-5 h-5 bg-white rounded-full shadow border border-gray-200 transform transition-transform duration-200 ease-in-out ${showDaySeparators ? 'translate-x-5' : 'translate-x-0'}`}></div>
                </div>
              </label>
            </div>
            {/* 👆 END TOGGLE BLOCK 👆 */}

      {/* --- DYNAMIC SUMMARY BLOCK --- */}
      {(() => {
        const dailyLoss = Number(settings["Daily Loss Limit"] || 0);
        const init      = Number(settings["Initial Balance"] || 0);
        const maxLoss   = Math.abs(Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0));
        const running   = Number(settings["Running Balance"] || 0);
        const tradeCount = lossProjection?.tradeCount || 0;

        if (!(dailyLoss > 0)) return null;

        let daysToRuin = 0;
        let ruinThreshold = 0;

        if (maxLoss > 0) {
          ruinThreshold = init - maxLoss; // e.g. 0 - 2000 = -2000
          const distance = Math.max(0, running - ruinThreshold);
          daysToRuin = Math.ceil(distance / dailyLoss);
        } else {
          daysToRuin = Math.ceil(running / dailyLoss);
        }

          return (
            <div className="mb-6 p-5 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800/50">
              <h4 className="font-bold text-red-900 dark:text-red-100 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
                </svg>
                Risk to Ruin Analysis
              </h4>
              
              <div className="flex flex-col sm:flex-row gap-6 items-start">
                <div className="flex-1">
                  <div className="text-3xl font-extrabold text-red-700 dark:text-red-300">
                    {tradeCount} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">consecutive losses</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                    To reach your failure threshold of <strong>{formatCurrency(ruinThreshold)}</strong> based on your current risk settings.
                  </p>
                </div>

                <div className="flex-1 bg-white dark:bg-gray-800 p-3 rounded-lg border border-red-100 dark:border-red-900 shadow-sm">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-bold tracking-wider mb-1">Time to Ruin (Daily Limit)</div>
                  <div className="text-xl font-bold text-gray-800 dark:text-gray-100">
                    {daysToRuin} <span className="text-sm font-medium text-gray-500">trading days</span>
                  </div>
                  <div className="text-xs text-red-600 dark:text-red-400 font-medium mt-1">
                    If you hit your Daily Loss Limit ({formatCurrency(dailyLoss)}) every single day.
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      <div className="overflow-auto max-h-[50vh] rounded-xl border-2 border-red-200 dark:border-red-800 shadow">
        <table className="min-w-full border-separate border-spacing-0 bg-white dark:bg-gray-900">
          <thead className="bg-red-100 dark:bg-red-900/80 sticky top-0 z-10">
            <tr>
              <th className="px-3 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold text-xs uppercase tracking-wider">Trade #</th>
              <th className="px-3 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold text-xs uppercase tracking-wider">Running Bal Before</th>
              <th className="px-3 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold text-xs uppercase tracking-wider">Effective Bal Before</th>
              <th className="px-3 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold text-xs uppercase tracking-wider"># Contracts</th>
              <th className="px-3 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold text-xs uppercase tracking-wider">Loss Amount</th>
              <th className="px-3 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold text-xs uppercase tracking-wider">Running Bal After</th>
              <th className="px-3 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold text-xs uppercase tracking-wider">Effective Bal After</th>
            </tr>
          </thead>
          <tbody>
            {(lossProjection?.trades?.length ?? 0) === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 italic">
                  No trades can be taken with the current settings.
                </td>
              </tr>
            ) : (
              (() => {
                const rowsToShow = isTrialing ? lossProjection.trades.slice(0, 10) : lossProjection.trades;
                
                let currentLossDay = 1;
                let lossAccumulator = 0;
                const dailyLossAmt = Number(settings["Daily Loss Limit"] || 0);

                const tradesWithDaysLoss = rowsToShow.map(row => {
                  const assignedDay = currentLossDay;
                  if (dailyLossAmt > 0) {
                    lossAccumulator += (row.loss || 0);
                    if (lossAccumulator >= dailyLossAmt) {
                      currentLossDay++;
                      lossAccumulator = 0;
                    }
                  }
                  return { ...row, day: assignedDay };
                });

                return tradesWithDaysLoss.map((row, i) => {
                  const isNewDay = showDaySeparators && i > 0 && row.day !== tradesWithDaysLoss[i-1].day;

                  return (
                    <React.Fragment key={i}>
                      {isNewDay && (
                        <tr className="bg-red-100/50 dark:bg-red-900/30">
                          <td colSpan="10" className="px-3 py-2 text-center text-xs font-bold text-red-800 dark:text-red-300 uppercase tracking-widest border-y border-red-200 dark:border-red-800">
                            — Day {row.day} —
                          </td>
                        </tr>
                      )}
                      <tr className="hover:bg-red-50 dark:hover:bg-red-900/40 transition group">
                        <td className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 text-gray-900 dark:text-gray-100">{row.trade}</td>
                        <td className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 text-gray-900 dark:text-gray-100">
                          {formatCurrency(row.balanceBefore, settings["Account Currency"])}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 text-blue-700 dark:text-blue-300">
                          {formatCurrency(row.effectiveBefore, settings["Account Currency"])}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 text-gray-900 dark:text-gray-100">
                          {row.contracts}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 text-red-600 dark:text-red-400 font-bold group-hover:text-red-700">
                          {formatCurrency(row.loss, settings["Account Currency"])}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 text-gray-900 dark:text-gray-100">
                          {formatCurrency(row.balanceAfter, settings["Account Currency"])}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 text-blue-700 dark:text-blue-300">
                          {formatCurrency(row.effectiveAfter, settings["Account Currency"])}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                });
              })()
            )}
          </tbody>
        </table>
       {/* --- NEW: Trial Indicator --- */}
        {isTrialing && (lossProjection?.trades?.length ?? 0) > 5 && (
          <div className="p-4 bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700 text-center">
            <p className="text-lg font-bold text-gray-600 dark:text-red-500 flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
              Full projection table available after Free Trial
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
)}





{/* --- ADD THIS ENTIRE POPUP MODAL --- */}
{showLearnMorePopup && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={() => setShowLearnMorePopup(false)}
        aria-label="Close"
      >
        ×
      </button>
      <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M13 16h-1v-4h-1m1-4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"></path>
        </svg>
        Understanding Funded Prop Calculations
      </h2>
      <div className="space-y-4 text-gray-700 dark:text-gray-300">
        <p>
          After you pass your prop firm evaluation you will received a prop funded acccount. Your starting balance will be <strong>$0</strong> and most prop firms allow you to lose a certain amount in the negative before the account is blown. This amount known as the <span className="font-bold text-blue-400">Effective Balance</span> and it will be used for the calculations.
        </p>
        <p className="font-semibold text-gray-800 dark:text-gray-100 bg-gray-100 dark:bg-gray-800 p-2 rounded">
          Effective Balance = Maximum Loss Amount
        </p>
        <p>
          For example, on an account with a <strong>$0</strong> starting balance and a <strong>$2,000</strong> maximum loss, your effective balance for risk calculations is <strong>$2,000 </strong> and the account is blown when the balance falls below <span className="font-bold text-red-500">-$2,000</span>.
        </p>
        <p>
          This tool uses your effective balance to suggest contract sizes, helping you stay within the firm's drawdown rules and not overleverage and blow your account too quickly.
        </p>
      </div>
      <button
        onClick={() => setShowLearnMorePopup(false)}
        className="mt-6 bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition w-full"
      >
        OK
      </button>
    </div>
  </div>
)}


    {/* --- POPUPS AND WARNINGS --- */}

{showZeroContractsPopup && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={() => setShowZeroContractsPopup(false)}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning!
      </h2>

      <p className="mb-3 text-gray-800 dark:text-gray-100">
        Based on the symbol you selected, your account balance, risk tolerance, and stop loss size, the suggested number of contracts for this trade is <span className="font-bold text-red-500">0</span>.
      </p>

      <p className="mb-3 text-gray-800 dark:text-gray-100">
        You are currently risking <span className="font-bold text-red-500">{lastActualRiskPercent}%</span> of your balance, which is <span className="font-bold text-red-500">{formatCurrency(lastTotalRisk)}</span>.
      </p>

      {/* ▼▼ NEW: tip that uses the user's *minimum* contracts instead of hard-coding 1 ▼▼ */}
      {(() => {
        // Use the min contracts from the calculation, falling back to settings
        const minQty = Number(calculatedSummary?.minContracts || settings["Minimum Contracts"] || 1);

        // maxStopLossSize is for 1 contract; divide by minQty to get ticks allowed for minQty contracts
        const maxSLForMin = (Number(calculatedSummary?.maxStopLossSize) > 0 && minQty > 0)
          ? Math.floor(Number(calculatedSummary.maxStopLossSize) / minQty)
          : 0;

        if (maxSLForMin <= 0) return null; // don’t show if not meaningful

        return (
          <p className="mb-6 text-gray-800 dark:text-gray-100">
            Tip: To trade <span className="font-semibold">{minQty}</span> contract{minQty > 1 ? "s" : ""} within your risk, your stop loss must be ≤{" "}
            <span className="font-bold text-yellow-600">{maxSLForMin}</span> ticks.
          </p>
        );
      })()}
      {/* ▲▲ NEW block ends ▲▲ */}

      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Consider choosing a different product or waiting for a trade setup that requires a smaller stop loss.
      </p>

      <button
        onClick={() => setShowZeroContractsPopup(false)}
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}





{currentPopup?.id === 'dailyGoalPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn">
    {/* Confetti for celebration! */}
    <Confetti width={width} height={height} numberOfPieces={500} recycle={false} />
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>

      <h2 className="text-2xl font-bold mb-4 text-green-700 dark:text-green-500 flex items-center">
        <svg className="w-7 h-7 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Congratulations!
      </h2>

      <div className="mb-4 text-lg text-gray-800 dark:text-gray-100 font-semibold text-center">
        You reached your{" "}
        <span className="text-green-600 dark:text-green-300 font-bold">Daily Goal</span>!
        <br />
        {currentPopup.data?.dailyGoal !== undefined && (
          <span className="block mt-2 text-base text-green-700 dark:text-green-500 font-bold">
            Goal:{" "}
            <span className="text-green-700 dark:text-green-500">
              {Number(currentPopup.data.dailyGoal).toLocaleString(undefined, {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
              })}
            </span>
          </span>
        )}

        <span className="block mt-2 font-bold text-lg text-gray-700 dark:text-gray-200">
          Take a moment to celebrate your discipline and progress.<br /><br />
          Size down if you continue to trade, you don't want to risk giving your earnings back to the market.<br /><br />
          {/* Dynamic day-based message (local time). Pass a TZ if you prefer: getDailyGoalMessage('America/New_York') */}
          {getDailyGoalMessage()}
        </span>
      </div>

      <button
        onClick={closeCurrentPopup}
        className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}



{currentPopup?.id === 'overtradingPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div
      className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-labelledby="overtrading-title"
      aria-describedby="overtrading-desc"
    >
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >
        ×
      </button>

      <h2
        id="overtrading-title"
        className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center"
      >
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning: Overtrading!
      </h2>

      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-yellow-700 dark:text-yellow-300">You have logged more trades than your daily plan allows.</span>
      </p>

      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold">Your maximum trades per day is: </span>
        <span className="font-bold text-yellow-700 dark:text-yellow-300">
          {currentPopup.data.maxTradesPerDay}
        </span>
      </p>

      <p id="overtrading-desc" className="mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Overtrading can lead to impulsive decisions and unnecessary risk by draining your mental capital.
      </p>

      <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Review your trades, lock your account, and come back tomorrow recharged for a strong start!
      </p>

      <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Below is a really good video from Psychology of Profits regarding protecting your mental capital.
      </p>

      {/* Actions */}
      <div className="mt-6 flex flex-wrap gap-3 justify-end">
        <a
          href="https://www.youtube.com/watch?v=6vZG4epFEOM"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-yellow-500 text-yellow-700 dark:text-yellow-300 font-bold px-4 py-2 rounded hover:bg-yellow-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition"
        >
          {/* Play icon */}
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6 4l10 6-10 6V4z" />
          </svg>
          Watch: Mental Capital
          <span className="sr-only">(opens in a new tab)</span>
        </a>

        <button
          onClick={closeCurrentPopup}
          className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
        >
          Close
        </button>
      </div>
    </div>
  </div>

)}


{currentPopup?.id === "maxLossLimitBlown" && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>

      <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-400 flex items-center">
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Account Blown
      </h2>

      <p className="font-semibold mb-3 text-gray-800 dark:text-gray-100">
        You’ve reached the maximum loss limit for this account.
      </p>

      <p className="mb-3 text-gray-800 dark:text-red-400">
        <span className="font-semibold">Start:</span>{" "}
        <span className="font-bold">
          {formatCurrency(currentPopup?.data?.initial, settings["Account Currency"] || "USD")}
        </span>{" "}
        <br/>  <span className="font-semibold">Max Loss:</span>{" "}
        <span className="font-bold">
          {formatCurrency(currentPopup?.data?.limit, settings["Account Currency"] || "USD")}
        </span>{" "}
        <br/> <span className="font-semibold">Current:</span>{" "}
        <span className="font-bold">
          {formatCurrency(currentPopup?.data?.rb, settings["Account Currency"] || "USD")}
        </span>
      </p>

      <p className="font-semibold mb-6 text-gray-700 dark:text-gray-300">
        Per the Challenge rules, once you hit the Maximum Loss Limit the challenge is failed.
      </p>
      <p className="font-semibold mt-6 mb-6 text-gray-700 dark:text-gray-300">
        Consider switching to a practice account until you are winning consistently before
        resetting or starting another challenge. 
      </p>


      {/* Add this just above the close button area in maxLossLimitBlown */}
      <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-100">
        Trading is 80% Psychology.  Review mindset and risk-management refreshers, the Psychology of Profit has some really good videos.
      </p>

      {/* Actions */}
      <div className="mt-6 flex flex-wrap gap-3 justify-end">
        <a
          href="https://www.youtube.com/@Psychology-Of-Profit" // <- replace with the exact channel if different
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-red-600 text-red-700 dark:text-red-300 font-bold px-4 py-2 rounded hover:bg-red-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600 transition"
        >
          {/* Play icon */}
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6 4l10 6-10 6V4z" />
          </svg>
          Visit the Psychology of Profit Youtube Channel
          <span className="sr-only">(opens in a new tab)</span>
        </a>

       <p className="font-semibold mt-6 mb-6 text-red-700 dark:text-red-300">
        Don't give up!  You can do this!
      </p>

             </div>
      <button
        onClick={closeCurrentPopup}
        className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'tooManyLossesPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-500 flex items-center">
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Daily Lost Trades Limit Reached!
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-red-700 dark:text-red-300">
          You've hit your max daily loss count.
        </span>
      </p>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold">Your maximum losses per day is: </span>
        <span className="font-bold text-red-700 dark:text-red-300">
          {currentPopup.data.maxLossesPerDay}
        </span>
      </p>
      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Consider stopping for the day to protect your capital. <br/> <br/>Review your trades and reset for tomorrow.
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'lossLimitWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Daily Loss Limit Exceeded!
      </h2>
      <p className="mb-3 text-lg text-gray-800 dark:text-gray-100">
        Your total loss for today is <span className="font-bold text-red-600">{formatCurrency(currentPopup.data.lossForDay)}</span>,
        which exceeds your daily loss limit of <span className="font-bold text-red-600">{formatCurrency(currentPopup.data.lossLimitAmount)}</span>.
      </p>
      <p className="mb-6 text-gray-700 dark:text-gray-400">
        Please consider stopping trading for the day to protect your account.
      </p>
      <p className="mb-6 font-semibold text-red-700 dark:text-red-400">
        You are worthy of success!  Don't give up!
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === "win10NoRulesPopup" && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning - Big Wins Without Following Rules
      </h2>
      <div className="font-bold mb-4 text-gray-800 dark:text-gray-100 whitespace-pre-line">
        {currentPopup.data?.message
          ?.split('\n')
          .map((line, i) =>
            line.trim() === "Discipline > excitement!" ? (
              <span key={i} className="font-bold text-blue-700 dark:text-blue-500">
                Discipline &gt; excitement!
                <br />
              </span>
            ) : (
              <React.Fragment key={i}>
                {renderWithTiltTooltip(line)}
                <br />
              </React.Fragment>
            )
          )
        }
      </div>
      <button
        onClick={closeCurrentPopup}
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}



{currentPopup?.id === "win10WithRulesPopup" && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-green-700 dark:text-green-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Congratulations – Following Your Rules!
      </h2>
      <div className="mb-4 text-gray-800 dark:text-gray-100 whitespace-pre-line">
        {(currentPopup.data?.message || "Amazing job! You achieved 10 wins in a row while sticking to your rules. This is the true mark of a disciplined trader.").split('\n').map((line, i) =>
          <React.Fragment key={i}>
            {line}
            <br />
          </React.Fragment>
        )}
      </div>
      <button
        onClick={closeCurrentPopup}
        className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}



{currentPopup?.id === 'moodVolatilityPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 20v-6m0 0V4m0 10c-2 0-4-1-4-4s2-4 4-4 4 1 4 4-2 4-4 4z" strokeWidth="2"/>
        </svg>
        Mood Volatility Warning
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-blue-700 dark:text-blue-300">
          Your recent mood is elevated.
        </span>
      </p>
      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Trading in a state of high emotions is not ideal. A calm, clear mind is your greatest asset.
      </p>
      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Journal how you're feeling and why before your next trade.
      </p>
       <p className="font-bold mb-6 text-gray-700 dark:text-gray-300">
        We recommend you pause until your focus is back on your process. If you consistently feel high emotions with every trade, you may be trading a symbol or timeframe that doesn't fit your style.
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}

{currentPopup?.id === 'twoLossAfterGoalWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning – Losses After Goal
      </h2>
      <div className="font-bold mb-4 text-gray-800 dark:text-gray-100 whitespace-pre-line text-left overflow-visible">
          {(currentPopup.data?.message || "You had 2 losses after reaching your daily goal.")
            .split('\n')
            .map((line, i) => (
              <React.Fragment key={i}>
                {renderWithTiltTooltip(line)}
                <br />
              </React.Fragment>
          ))}
      </div>
      <button
        onClick={closeCurrentPopup}
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        OK
      </button>
    </div>
  </div>
)}




{currentPopup?.id === 'overRiskWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning!
      </h2>
      <div className="text-gray-800 dark:text-gray-100">
              <p className="mb-3">You lost more than your planned risk amount.</p>
      
              <p className="mb-1">
                Risk amount:
                {" "}
                <span className="font-extrabold text-yellow-500">
                  {formatCurrency(currentPopup.data?.riskAmount ?? currentPopup.data?.planned)}
                </span>
              </p>
      
              <p className="mb-4">
                Amount lost:
                {" "}
                <span className="font-extrabold text-yellow-500">
                  {formatCurrency(currentPopup.data?.amountLost ?? currentPopup.data?.actualLoss)}
                </span>
              </p>
      
              <p className="mb-3 text-gray-700 dark:text-gray-300">
                Make sure that you are always using the proper stop loss and not moving it.
                If the trade is going too far against your analysis, you can always get out and
                get back in when it is the right time.
              </p>
      
              <p className="font-extrabold text-yellow-600 dark:text-yellow-400">
                Keep your losers small and your winners big and never marry a trade idea!
              </p>
            </div>
      <button
        onClick={closeCurrentPopup}
        className="mt-6 bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}

{currentPopup?.id === 'drawdownWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-purple-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-purple-700 dark:text-purple-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 20v-6m0 0V4m0 10c-2 0-4-1-4-4s2-4 4-4 4 1 4 4-2 4-4 4z" strokeWidth="2"/>
        </svg>
        Drawdown Warning
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-purple-700 dark:text-purple-300">
          Your account has experienced a drawdown of more than <span className="underline">{currentPopup.data.drawdownLimitPct}%</span> from its peak.
        </span>
      </p>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold">Drawdown amount: </span>
        <span className="font-bold text-purple-700 dark:text-purple-300">
          ${currentPopup.data.drawdownAmount?.toLocaleString(undefined, {maximumFractionDigits:2})}
        </span>
        <span className="ml-2 text-sm text-purple-500 dark:text-purple-300">
          ({currentPopup.data.drawdownPct?.toFixed(2)}%)
        </span>
      </p>
      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Consider pausing to review your trades and protect your capital before proceeding.
      </p>
      <p className="font-bold mt-6 mb-6 text-gray-700 dark:text-gray-300">
        No one wins 100% of the time and there is nothing wrong with switching to demo until you are winning consistently and growing your account.
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-purple-500 hover:bg-purple-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'consecutiveWinsPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M8 12l2 2l4-4" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Hot Streak!
      </h2>
      <p className="mb-3 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-blue-700 dark:text-blue-300">You've won 5 trades in a row!</span>
      </p>
      <p className="text-gray-700 dark:text-gray-300">
        Stay disciplined—don&apos;t increase your risk or change your plan just because you&apos;re on a hot streak.
      </p>
      <p className="mt-4 mb-6 text-gray-700 dark:text-blue-300">
        Stick to your process and keep your edge!
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}



{currentPopup?.id === 'accountGoalReached' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    {/* Confetti for celebration! */}
    <Confetti width={width} height={height} numberOfPieces={500} recycle={false} />
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-green-700 dark:text-green-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M5 13l4 4L19 7" strokeWidth="2" />
        </svg>
        Account Goal Achieved!
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-green-700 dark:text-green-300">
          Congratulations! You've reached your account goal and passed your challenge!
        </span>
      </p>
      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Your balance is  <span className="font-bold">{formatCurrency(settings["Running Balance"])}</span><br />
        Your Goal was <span className="font-bold">{formatCurrency(settings["Account Goal"])}</span>
      </p>
          <p className="mb-4 text-gray-800 dark:text-gray-100">
              <span className="font-bold text-gray-700 dark:text-gray-300">You've made it to the next phase—don't change what's working. Stick to the disciplined plan that got you here.  </span>
            </p>

      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-green-700 dark:text-green-300">
          Your hard work has paid off!
        </span>
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}



{currentPopup?.id === 'lessonsLearnedStreakWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M12 6v6l4 2" strokeWidth="2" />
        </svg>
        Lessons Learned Streak!
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-blue-700 dark:text-blue-300">Great job extracting value from your trades!</span>
      </p>
      <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">Consistently recording your lessons learned will accelerate your growth. <br/><br/> Review them every day during your pretrade routine until they become seared in your brain!</p>
      <button
        onClick={closeCurrentPopup}
        className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'riskOfRuinWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
        </svg>
        Risk of Ruin Warning
      </h2>

       <div className="text-gray-800 dark:text-gray-100">
         {/* Prefer explicit popup data mode or presence of propInitial */}
         {currentPopup?.data?.mode === "propThreshold" || typeof currentPopup?.data?.propInitial === "number" ? (
           <>
             <p className="mb-3">
               Your <span className="font-bold">Account Running Balance</span> is{" "}
               <span className="font-extrabold text-red-600">
                 {formatCurrency(currentPopup?.data?.runningBalance ?? settings["Running Balance"])}
               </span>.
             </p>
      <p className="mb-3">
        You have about{" "}
        <span className="font-bold">
          {
            (Number(currentPopup?.data?.propInitial) > 0)
              ? Math.round(
                  (Number(currentPopup?.data?.propRunning || 0) /
                   Number(currentPopup?.data?.propInitial)) * 100
                )
              : 0
          }% of the balance remaining
        </span>{" "}
        (<span className="font-extrabold text-red-600">
          {formatCurrency(
            Number(
              currentPopup?.data?.propRunning ??
              (Number(settings["Running Balance"] || 0) +
               Math.abs(Number(settings["Maximum Loss Limit"] || 0)))
            )
          )}
        </span>) before you blow your account.
      </p>
             {Number.isFinite(currentPopup?.data?.tradeCount) || Number.isFinite(lossProjection?.tradeCount) ? (
               <p className="mb-4 text-gray-700 dark:text-gray-400">
                 At your current risk per trade, you have about{" "}
                 <span className="text-red-600 font-bold underline">
                   {currentPopup?.data?.tradeCount ?? lossProjection?.tradeCount}
                 </span>{" "}
                 consecutive full‑risk losses before you hit the failure threshold
                 {Number.isFinite(currentPopup?.data?.blownAt) ? (
                   <> (≈ {formatCurrency(currentPopup.data.blownAt)}).</>
                 ) : "."}
               </p>
             ) : null}
             <p className="mb-4 text-gray-700 dark:text-gray-400">
               You are now at high risk of blowing the evaluation. Reduce risk per trade,
               focus only on A+ setups.  Switch to demo while you fine tune your strategy before continuing.
             </p>
             <p className="font-semibold text-gray-700 dark:text-gray-300">
               You are worthy of success—protect your capital.
             </p>
           </>
         ) : Number.isFinite(currentPopup?.data?.tradeCount) ? (
           /* Loss‑projection style when queued with tradeCount only */
           <>
             <p className="mb-4">
               <span className="font-bold text-red-700 dark:text-red-300">
                 Based on your current risk, your account could be wiped out in just{" "}
                 <span className="underline">{currentPopup.data.tradeCount}</span>{" "}
                 consecutive losses.
               </span>
             </p>
             <p className="mb-2 text-gray-700 dark:text-gray-400">
               Consider lowering your risk per trade to protect your capital.
             </p>
           </>
         ) : Number.isFinite(lossProjection?.tradeCount) ? (
           /* Fallback to current state */
           <>
             <p className="mb-4">
               <span className="font-bold text-red-700 dark:text-red-300">
                 Based on your current risk, your account could be wiped out in just{" "}
                 <span className="underline">{lossProjection.tradeCount}</span>{" "}
                 consecutive losses.
               </span>
             </p>
             <p className="mb-2 text-gray-700 dark:text-gray-400">
               Consider lowering your risk per trade to protect your capital.
             </p>
           </>
         ) : (
           /* Generic fallback */
           <p className="mb-2 text-gray-700 dark:text-gray-400">
             Your current risk profile indicates a high probability of ruin. Please reduce
             your position size and reassess your plan.
           </p>
         )}
       </div>

      <button
        onClick={closeCurrentPopup}
        className="mt-6 bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}



{currentPopup?.id === 'tradeSaved' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-20 animate-fadeIn">
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center border-2 border-blue-500 animate-fadeIn">
      <span className="flex items-center justify-center bg-blue-100 dark:bg-blue-800 rounded-full mb-3" style={{ width: 60, height: 60 }}>
        <svg className="w-10 h-10 text-blue-600 dark:text-blue-300" fill="none" viewBox="0 0 48 48">
          <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="4" fill="currentColor" opacity="0.13"/>
          <path d="M16 24l6 6 10-14" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        </svg>
      </span>
      <div className="text-2xl font-extrabold text-blue-700 dark:text-blue-300 mb-1">
        Trade Saved!
      </div>
      <div className="text-gray-700 dark:text-gray-200 mb-1 text-center">
        Your journal entry has been saved.
      </div>
      <button
        className="mt-2 px-5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow transition"
        onClick={closeCurrentPopup}
      >
        OK
      </button>
    </div>
  </div>
)}


{/* REQUIRED FIELDS */}
{missingFields.length > 0 && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={() => setMissingFields([])}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Required Fields Missing
      </h2>
      <ul className="mb-4 text-gray-800 dark:text-gray-200">
        {missingFields.map((f) => (
   <li key={f} className="pl-2">{f}</li>
 ))}

      </ul>
      <button
        onClick={() => setMissingFields([])}
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-4 py-2 rounded shadow"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'twoLossWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
      <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning - 2 Consecutive Losses!
      </h2>

      {/* Renders every line from the queued message and preserves new lines */}
      <div className="mb-4 text-gray-800 dark:text-gray-100 text-left">
        {currentPopup.data?.message
          ? currentPopup.data.message.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {highlightHundredPercent(line)}
                <br />
              </React.Fragment>
            ))
          : (
            <>
              You've lost <span className="font-bold text-red-500">2 trades in a row</span>.<br />
              Consider taking a break and reviewing your trades and strategy before continuing.<br />
              You don't want the risk of going on tilt.
            </>
          )
        }
      </div>

      <button onClick={closeCurrentPopup} className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition">
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'threeLossWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div
      className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-labelledby="three-loss-title"
      aria-describedby="three-loss-desc"
    >
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >
        ×
      </button>

      <h2
        id="three-loss-title"
        className="text-xl font-bold mb-4 text-red-700 dark:text-red-500 flex items-center"
      >
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Danger!
      </h2>

      {(() => {
        const fallbackMsg =
          "You've lost 3 trades in a row.\n\nTake a break! \n\nTake some time to evaluate what is really going on. Make sure you are fresh and have a clear mind before taking another trade.\n\nThe Key is to avoid going on tilt and revenge trading.\n\n Trading is 80% Trading Psychology. This may be a good time to watch some videos.  The video below from Psychology of Profits is a good one to start with.   You are worthy of success and you got this!!";

        const text = (currentPopup?.data?.message ?? fallbackMsg);

        return (
          <div id="three-loss-desc" className="mb-4 font-semibold text-gray-800 dark:text-gray-100 text-left">
            {text.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {formatThreeLossMessage(line)}
                <br />
              </React.Fragment>
            ))}

            <div className="mt-4 text-blue-400 font-bold">{"You got this!!"}</div>
          </div>
        );
      })()}

      <div className="mt-6 flex flex-wrap gap-3 justify-end">
        {currentPopup?.data?.videoLink && (
          <a
            href={currentPopup.data.videoLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-red-500 text-red-700 dark:text-red-300 font-bold px-4 py-2 rounded hover:bg-red-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-offset-gray-900 transition"
          >
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M6 4l10 6-10 6V4z" />
            </svg>
            Watch: Dealing with Trading Losses
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        )}

        <button
          onClick={closeCurrentPopup}
          className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

{/* Add this near your other popups */}
<FirstTradeAnalysisPopup 
  currentPopup={currentPopup} 
  onClose={closeCurrentPopup} 
/>

{showChecklist && (
  <PreTradeChecklistModal
    onClose={handleChecklistConfirm}
    onConfirm={handleChecklistConfirm}
  />
)}
{/* --- ADD THIS FLOATING ACTION BUTTON AND CALCULATOR --- */}
    <button
      onClick={() => setShowCalculator(true)}
      title="Open Calculator"
      className="fixed bottom-12 right-6 z-40 bg-gray-800 dark:bg-gray-700 text-white rounded-full p-4 shadow-lg hover:bg-gray-900 dark:hover:bg-gray-600 transition-transform transform hover:scale-110"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
      </svg>
    </button>

{/* Strategy Checklist Floating Panel */}
      {showStrategyModal && (
        <div className="fixed top-24 right-8 z-40 animate-fadeIn pointer-events-auto w-96 max-w-[calc(100vw-2rem)] shadow-2xl">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-6 border-2 border-blue-500 relative max-h-[80vh] flex flex-col">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={() => setShowStrategyModal(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center gap-2 shrink-0">
              <FaClipboardList className="text-blue-500" />
              Strategy Checklist
            </h2>
            
            <div className="overflow-y-auto mb-4 pr-2 flex-1">
              {userPrefs?.tradingRules ? (
                <div className="space-y-3">
                  {userPrefs.tradingRules.split('\n').filter(rule => rule.trim() !== '').map((rule, index) => (
                    <label key={index} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition">
                      <input 
                        type="checkbox" 
                        className="mt-1 h-5 w-5 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700" 
                      />
                      <span className="text-gray-800 dark:text-gray-200 font-medium leading-relaxed text-sm">
                        {rule}
                      </span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  <p className="mb-2">No Trading Rules defined yet.</p>
                  <p className="text-sm">Head over to the Lessons page to define your core strategy.</p>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-gray-800 shrink-0">
              <button
                onClick={() => setShowStrategyModal(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-2 rounded-xl shadow transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    
    {showCalculator && <SimpleCalculator onClose={() => setShowCalculator(false)} />}
    {/* --- END OF NEW CODE --- */}
  </div>
);
}